import React, { useState, useMemo, useEffect, useCallback } from "react";
import AI_PERSONALITIES from "./personalities";
import useAgentData from "./useAgentData";
// ExecutiveDashboard was removed — duplicated Opportunities + intelligence widgets in 2,910 lines no one used.
import AgentOutbox from "./AgentOutbox";
import AdminPanel from "./AdminPanel";
import ProfileEditor from "./ProfileEditor";
import Login from "./Login";
import Onboarding from "./Onboarding";
import { Lockup } from "./Logo";
import { computeScoringV2, POSTURE_STYLE } from "./scoringV2";
import BdTeamPanel from "./BdTeamPanel";

// Error boundary so a single broken sub-component (e.g. an opportunity detail
// section choking on unexpected data shape) doesn't unmount the entire React
// tree and leave the user with a white screen. Shows the error inline with a
// reset button instead. We wrap the opportunity detail panel specifically
// because that's where most of the prior crashes have happened — a thrown
// error in any of ~10 detail sub-sections used to take the whole app down.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', this.props.label || 'render error', error, info);
    this.setState({ info });
  }
  reset = () => this.setState({ error: null, info: null });
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ padding: 24, color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 12, lineHeight: 1.6, background: 'var(--bg-1)', border: '1px solid var(--red)', borderRadius: 6, margin: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--red)', marginBottom: 8 }}>
          ⚠ Something broke while rendering {this.props.label || 'this view'}
        </div>
        <div style={{ marginBottom: 8, color: 'var(--text-1)' }}>{String(this.state.error?.message || this.state.error)}</div>
        {this.state.info?.componentStack && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer', color: 'var(--text-2)' }}>Component stack (paste this to debug)</summary>
            <pre style={{ marginTop: 8, padding: 8, background: 'var(--bg-2)', borderRadius: 4, fontSize: 10, whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto' }}>{this.state.info.componentStack}</pre>
          </details>
        )}
        <div style={{ marginTop: 12 }}>
          <button onClick={this.reset} style={{ fontSize: 11, padding: '4px 10px', background: 'var(--bg-3)', color: 'var(--text-0)', border: '1px solid var(--border)', borderRadius: 3, cursor: 'pointer', fontFamily: 'var(--font-mono)' }}>Try again</button>
          <button onClick={() => { if (this.props.onClose) this.props.onClose(); this.reset(); }} style={{ fontSize: 11, padding: '4px 10px', background: 'var(--bg-3)', color: 'var(--text-0)', border: '1px solid var(--border)', borderRadius: 3, cursor: 'pointer', fontFamily: 'var(--font-mono)', marginLeft: 6 }}>Close panel</button>
        </div>
      </div>
    );
  }
}

const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "";

// SAM.gov date-only values are calendar dates, not UTC timestamps. Parsing
// YYYY-MM-DD as UTC displays the previous day for users west of UTC.
function parseCalendarDate(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
  return new Date(value);
}

function calendarDaysUntil(value, now = new Date()) {
  const target = parseCalendarDate(value);
  if (isNaN(target)) return null;
  const targetDay = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const currentDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((targetDay - currentDay) / 86400000);
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=IBM+Plex+Mono:wght@300;400;500&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    /* Sovereign Enterprise design system — light mode primary, navy
       sidebar accent, copper for pursue/urgent. Legacy --bg-* / --text-*
       names are kept so existing selectors keep working; values now map
       to the new palette. */

    /* Surfaces (was dark navy → now warm off-white layers) */
    --bg-0: #fcf9f8;            /* base canvas */
    --bg-1: #ffffff;             /* topbar / elevated */
    --bg-2: #f5f1ef;             /* card surface */
    --bg-3: #eee8e3;             /* hover / subtle */
    --bg-4: #e5e0dc;             /* pressed / strong */

    /* Sidebar / inverse surfaces (the only intentionally dark area) */
    --sidebar-bg: #1d2b3e;
    --sidebar-fg: #e6e8ec;
    --sidebar-fg-muted: #94a3b8;

    /* Text (was light → now dark) */
    --text-0: #1c1b1b;
    --text-1: #5b5751;
    --text-2: #8b857d;

    /* Brand: primary is navy chart blue (was bright blue),
       accent gold becomes tactical copper. */
    --accent: #1d2b3e;
    --accent-dim: rgba(29, 43, 62, 0.08);
    --accent-glow: rgba(29, 43, 62, 0.18);
    --gold: #b8763d;
    --gold-dim: rgba(184, 118, 61, 0.12);

    /* Status (desaturated for the professional tone) */
    --red: #9a3934;
    --red-dim: rgba(154, 57, 52, 0.10);
    --amber: #b8763d;
    --amber-dim: rgba(184, 118, 61, 0.12);
    --blue: #3a5a7e;
    --blue-dim: rgba(58, 90, 126, 0.10);
    --purple: #6b6093;
    --purple-dim: rgba(107, 96, 147, 0.10);

    --border: #e5e0dc;
    --radius: 6px;
    --font-display: 'Archivo Narrow', sans-serif;
    --font-body: 'Geist', 'Inter', -apple-system, sans-serif;
    --font-mono: 'Geist Mono', 'IBM Plex Mono', monospace;
  }

  body {
    background: var(--bg-0);
    color: var(--text-0);
    font-family: var(--font-body);
    font-size: 13px;
    line-height: 1.5;
    min-height: 100vh;
    overflow-x: hidden;
  }

  /* ── LAYOUT ── */
  .app-shell {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
  }

  /* ── TOP BAR ── */
  .topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 28px;
    background: var(--bg-1);
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
  }
  .topbar-left {
    display: flex;
    align-items: center;
    gap: 20px;
  }
  /* .logo div is now just a flex slot — the React <Lockup> component renders
     its own IconMark SVG + Wordmark with the design-system typography, so no
     font / color / letter-spacing rules are needed here. */
  .logo {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
  .topbar-right {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  /* ── SHARED BUTTON BASE (all topbar buttons use this height/rhythm) ── */
  .btn-run, .btn-secondary, .btn-primary-alt, .btn-toggle-activity {
    height: 32px;
    box-sizing: border-box;
  }
  .btn-run {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: var(--gold);
    color: #ffffff;
    border: none;
    border-radius: var(--radius);
    padding: 0 16px;
    font-family: var(--font-body);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: background-color 0.15s;
    box-shadow: 0 1px 2px rgba(20, 25, 35, 0.06);
  }
  .btn-run:hover { background: #a4682f; }
  .btn-run:hover { opacity: 0.85; }
  .btn-run .dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #ffffff;
  }
  .status-indicator {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 11px;
    color: var(--text-2);
    font-family: var(--font-mono);
  }
  .status-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: var(--text-2);
  }
  .status-dot.active { background: var(--accent); box-shadow: 0 0 6px var(--accent-glow); }

  /* ── MAIN NAV ── */
  .main-nav {
    display: flex;
    gap: 4px;
    align-items: center;
  }
  .topbar-divider {
    width: 1px;
    height: 20px;
    background: var(--border);
    margin: 0 4px;
  }

  /* ── MAIN CONTENT ── */
  .main-content {
    display: flex;
    flex: 1;
    overflow: hidden;
  }

  /* ── CENTER PANEL ── */
  .center-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* ── STATS BAR ── */
  .stats-bar {
    display: flex;
    gap: 12px;
    padding: 16px 28px;
    flex-shrink: 0;
  }
  .stat-card {
    flex: 1;
    background: var(--bg-1);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 14px 18px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .stat-label {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--text-2);
    font-family: var(--font-mono);
  }
  .stat-value {
    font-family: var(--font-display);
    font-size: 28px;
    letter-spacing: 1px;
    color: var(--text-0);
  }
  .stat-value.green { color: var(--accent); }
  .stat-value.amber { color: var(--amber); }
  .stat-value.red { color: var(--red); }
  .stat-card.clickable { cursor: pointer; transition: border-color 0.2s, background 0.2s; }
  .stat-card.clickable:hover { border-color: var(--text-2); background: var(--bg-2); }
  .stat-card.active { border-color: var(--accent); background: var(--accent-dim); }

  /* ── BUTTON SYSTEM ── */
  .btn-secondary {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 0 16px; background: var(--bg-3); border: 1px solid var(--border);
    border-radius: var(--radius); color: var(--text-0); cursor: pointer;
    font-family: var(--font-body); font-size: 12px; font-weight: 600;
    transition: border-color 0.2s, background 0.2s;
  }
  .btn-secondary:hover { border-color: var(--text-2); background: var(--bg-2); }
  .btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-primary-alt {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 0 16px; background: var(--accent); border: none;
    border-radius: var(--radius); color: var(--bg-0); cursor: pointer;
    font-family: var(--font-body); font-size: 12px; font-weight: 600;
    transition: opacity 0.2s;
  }
  .btn-primary-alt:hover { opacity: 0.85; }
  .btn-outbox { position: relative; }
  .btn-outbox.has-pending { background: var(--gold); border-color: var(--gold); color: var(--bg-0); }
  .btn-badge {
    position: absolute; top: -6px; right: -6px;
    background-color: var(--red); color: #fff; border-radius: 50%;
    width: 20px; height: 20px; display: flex; align-items: center;
    justify-content: center; font-size: 10px; font-weight: 700;
  }
  .btn-toggle-activity {
    background: none; border: 1px solid var(--border); border-radius: var(--radius);
    color: var(--text-2); cursor: pointer; padding: 6px 10px; font-size: 12px;
    font-family: var(--font-mono); font-weight: 600;
    transition: color 0.2s, border-color 0.2s;
  }
  .btn-toggle-activity:hover { color: var(--accent); border-color: var(--accent); }
  .btn-toggle-activity.open { color: var(--accent); border-color: var(--accent); }
  .next-run-label { font-size: 10px; color: var(--text-2); font-family: var(--font-mono); margin-left: 8px; white-space: nowrap; }

  /* ── PAGE NAV BUTTONS ── */
  .btn-nav {
    height: 32px;
    display: inline-flex; align-items: center; gap: 6px;
    padding: 0 14px; background: transparent; border: 1px solid var(--border);
    border-radius: var(--radius); color: var(--text-2); cursor: pointer;
    font-family: var(--font-body); font-size: 12px; font-weight: 600;
    transition: color 0.2s, border-color 0.2s, background 0.2s;
    box-sizing: border-box;
  }
  .btn-nav:hover { color: var(--text-1); border-color: var(--text-2); }
  .btn-nav.active { color: var(--text-0); border-color: var(--accent); background: var(--accent-dim); }

  /* ── TOOLBAR ── */
  .toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 28px 12px;
    flex-shrink: 0;
    flex-wrap: wrap;
  }
  .filters {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
  }
  .filter-btn {
    padding: 5px 12px;
    border-radius: 20px;
    border: 1px solid var(--border);
    background: transparent;
    color: var(--text-1);
    font-family: var(--font-body);
    font-size: 11px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .filter-btn:hover { border-color: var(--text-2); color: var(--text-0); }
  .filter-btn.active { background: var(--accent-dim); border-color: var(--accent); color: var(--accent); }
  .toolbar-divider { width: 1px; height: 20px; background: var(--border); flex-shrink: 0; }
  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
  }
  .search-input {
    flex: 1;
    min-width: 120px;
    max-width: 200px;
    height: 28px;
    background: var(--bg-2);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    color: var(--text-0);
    font-family: var(--font-body);
    font-size: 11px;
    padding: 0 10px;
    outline: none;
    transition: border-color 0.2s;
  }
  .search-input::placeholder { color: var(--text-2); }
  .search-input:focus { border-color: var(--text-2); }
  .filter-select {
    height: 28px;
    background: var(--bg-2);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    color: var(--text-1);
    font-family: var(--font-mono);
    font-size: 10px;
    padding: 0 8px;
    cursor: pointer;
    outline: none;
    transition: border-color 0.2s;
  }
  .filter-select:focus { border-color: var(--text-2); }
  .score-slider-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    color: var(--text-2);
    font-family: var(--font-mono);
    flex-shrink: 0;
  }
  .score-slider-wrap input[type=range] {
    width: 80px;
    accent-color: var(--accent);
    cursor: pointer;
  }
  .score-slider-val {
    min-width: 24px;
    color: var(--accent);
    font-weight: 600;
    font-size: 11px;
  }
  .toolbar-count {
    font-size: 10px;
    color: var(--text-2);
    font-family: var(--font-mono);
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* ── TABLE ── */
  .table-wrap {
    flex: 1;
    overflow-y: auto;
    padding: 0 28px 28px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
  }
  thead th {
    text-align: left;
    padding: 9px 12px;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--text-2);
    font-family: var(--font-mono);
    border-bottom: 1px solid var(--border);
    position: sticky;
    top: 0;
    background: var(--bg-0);
    z-index: 2;
    white-space: nowrap;
  }
  thead th.sortable { cursor: pointer; user-select: none; }
  thead th.sortable:hover { color: var(--text-1); }
  thead th .sort-arrow { opacity: 0.4; margin-left: 4px; }
  thead th.sorted .sort-arrow { opacity: 1; color: var(--accent); }

  tbody tr {
    border-bottom: 1px solid var(--border);
    cursor: pointer;
    transition: background 0.15s;
  }
  tbody tr:hover { background: var(--bg-1); }
  tbody tr.selected { background: var(--bg-2); }

  /* ── ROW ACTIONS ── */
  .row-actions { display: inline-flex; align-items: center; gap: 4px; margin-left: 8px; opacity: 0; transition: opacity 0.15s; }
  tbody tr:hover .row-actions { opacity: 1; }
  .row-action-btn {
    background: none; border: 1px solid var(--border); border-radius: 3px;
    color: var(--text-2); cursor: pointer; padding: 2px 6px; font-size: 10px;
    font-family: var(--font-mono); transition: all 0.15s; text-decoration: none;
  }
  .row-action-btn:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-dim); }

  td {
    padding: 12px 14px;
    color: var(--text-1);
    font-size: 12px;
    vertical-align: middle;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  td.name-col { color: var(--text-0); font-weight: 500; white-space: normal; max-width: 340px; }

  /* ── SCORE BADGE ── */
  .score-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-mono);
    font-size: 13px;
    font-weight: 500;
  }
  .score-ring {
    width: 32px; height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
  }
  .score-ring.high { background: var(--accent-dim); color: var(--accent); border: 1px solid var(--accent); }
  .score-ring.medium { background: var(--amber-dim); color: var(--amber); border: 1px solid var(--amber); }
  .score-ring.low { background: var(--red-dim); color: var(--red); border: 1px solid var(--red); }

  /* ── TIER / REC PILLS ── */
  .pill {
    display: inline-block;
    padding: 3px 9px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.5px;
    font-family: var(--font-mono);
    opacity: 0.85;
  }
  .pill.high { background: var(--accent-dim); color: var(--accent); }
  .pill.medium { background: var(--amber-dim); color: var(--amber); }
  .pill.low { background: var(--red-dim); color: var(--red); }
  .pill.pursue { background: var(--accent-dim); color: var(--accent); }
  .pill.monitor { background: var(--amber-dim); color: var(--amber); }
  .pill.skip { background: var(--red-dim); color: var(--red); }

  /* ── BADGES (Source / Freshness) ── */
  .badge {
    display: inline-block; padding: 2px 6px; border-radius: 4px;
    font-size: 10px; font-weight: 700; color: #fff; white-space: nowrap;
    font-family: var(--font-mono);
  }
  .badge.sam { background-color: #2563eb; }
  .badge.hatch { background-color: #e97a1f; }
  .badge.govcon { background-color: #8b5cf6; }
  .badge.execgov { background-color: #0891b2; }
  .badge.defone { background-color: #dc2626; }
  .badge.fedscoop { background-color: #059669; }
  .badge.fresh-new { background-color: #2ecc71; }
  .badge.fresh-week { background-color: #f39c12; }
  .badge.fresh-old { background-color: #e67e22; }
  .badge.fresh-stale { background-color: #95a5a6; }

  /* ── DETAIL PANEL ── */
  .detail-panel {
    width: 380px;
    background: var(--bg-1);
    border-left: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex-shrink: 0;
    transition: width 0.3s ease, opacity 0.3s;
  }
  .detail-panel.hidden { width: 0; opacity: 0; overflow: hidden; }
  .detail-header {
    padding: 20px 22px 16px;
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
  }
  .detail-close {
    background: none;
    border: none;
    color: var(--text-2);
    font-size: 18px;
    cursor: pointer;
    float: right;
    line-height: 1;
    padding: 2px;
  }
  .detail-close:hover { color: var(--text-0); }
  .detail-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-0);
    margin-top: 8px;
    line-height: 1.4;
    padding-right: 20px;
  }
  .detail-meta {
    margin-top: 10px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
  .detail-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px 22px;
  }
  .detail-section {
    margin-bottom: 20px;
  }
  .detail-section-label {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: var(--text-2);
    font-family: var(--font-mono);
    margin-bottom: 8px;
  }
  .detail-section p {
    font-size: 12px;
    color: var(--text-1);
    line-height: 1.6;
  }
  .detail-list {
    list-style: none;
  }
  .detail-list li {
    font-size: 12px;
    color: var(--text-1);
    padding: 5px 0;
    border-bottom: 1px solid var(--border);
    display: flex;
    gap: 8px;
    align-items: flex-start;
  }
  .detail-list li:last-child { border-bottom: none; }
  .detail-list li::before {
    content: '—';
    color: var(--text-2);
    flex-shrink: 0;
    font-size: 10px;
  }
  .detail-list.risks li::before { content: '!'; color: var(--red); font-weight: 600; }
  .detail-list.fit li::before { content: '✓'; color: var(--accent); font-weight: 600; }

  /* ── DETAIL PANEL BRIEFING ── */
  .detail-section.highlighted {
    background: linear-gradient(135deg, var(--accent-dim), transparent);
    border-left: 3px solid var(--accent);
    padding: 12px 14px; border-radius: 0 var(--radius) var(--radius) 0;
    margin-left: -22px; margin-right: -22px; padding-left: 22px; padding-right: 22px;
  }
  .detail-section-divider { height: 1px; background: var(--border); margin: 12px 0; }
  .detail-link-btn {
    display: inline-block; padding: 6px 14px; font-size: 12px; font-weight: 600;
    color: #fff; background-color: var(--accent); border-radius: var(--radius);
    text-decoration: none; transition: opacity 0.2s;
  }
  .detail-link-btn:hover { opacity: 0.85; }

  /* Agent Plan Panel */
  .agent-plan-panel {
    background: linear-gradient(135deg, var(--accent-dim), transparent);
    border: 2px solid var(--accent);
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
  }
  .agent-plan-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }
  .agent-plan-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--accent);
    font-family: var(--font-display);
  }
  .agent-plan-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--text-1);
    cursor: pointer;
  }
  .agent-plan-toggle input[type="checkbox"] {
    cursor: pointer;
  }
  .agent-plan-section {
    margin-top: 12px;
  }
  .agent-plan-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-0);
    margin-bottom: 6px;
  }
  .agent-plan-rationale {
    list-style: none;
    padding: 0;
    margin: 8px 0;
  }
  .agent-plan-rationale li {
    font-size: 12px;
    color: var(--text-1);
    padding: 4px 0 4px 16px;
    position: relative;
  }
  .agent-plan-rationale li::before {
    content: '→';
    position: absolute;
    left: 0;
    color: var(--accent);
    font-weight: 600;
  }
  .agent-plan-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .agent-action-card {
    background: var(--bg-3);
    border-left: 3px solid var(--accent);
    border-radius: 4px;
    padding: 10px 12px;
    transition: all 0.2s;
  }
  .agent-action-card:hover {
    background: var(--bg-2);
  }
  .agent-action-card.priority-high {
    border-left-color: var(--red);
  }
  .agent-action-card.priority-medium {
    border-left-color: var(--yellow);
  }
  .agent-action-card.priority-low {
    border-left-color: var(--text-2);
  }
  .agent-action-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
  }
  .agent-action-type {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-0);
    text-transform: uppercase;
    font-family: var(--font-mono);
  }
  .agent-action-priority {
    font-size: 9px;
    padding: 2px 6px;
    border-radius: 3px;
    background: var(--bg-1);
    color: var(--text-2);
    text-transform: uppercase;
    font-weight: 600;
  }
  .agent-action-card.priority-high .agent-action-priority {
    background: rgba(255, 90, 90, 0.15);
    color: var(--red);
  }
  .agent-action-card.priority-medium .agent-action-priority {
    background: rgba(255, 200, 50, 0.15);
    color: var(--yellow);
  }
  .agent-action-target {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-0);
    margin-bottom: 2px;
  }
  .agent-action-purpose {
    font-size: 11px;
    color: var(--text-1);
    margin-bottom: 4px;
  }
  .agent-action-timing {
    font-size: 10px;
    color: var(--text-2);
    font-family: var(--font-mono);
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
    border-bottom: 1px solid var(--border);
  }
  .detail-row:last-child { border-bottom: none; }
  .detail-row-label { color: var(--text-2); font-size: 11px; }
  .detail-row-value { color: var(--text-0); font-size: 11px; font-weight: 500; text-align: right; max-width: 55%; word-break: break-word; }

  /* ── ACTIVITY PANEL ── */
  .activity-panel.collapsed { width: 0; overflow: hidden; border-left: none; }
  .activity-panel {
    width: 320px;
    background: var(--bg-1);
    border-left: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex-shrink: 0;
  }
  .activity-header {
    padding: 16px 18px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
  }
  .activity-header h3 {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--text-2);
    font-family: var(--font-mono);
  }
  .activity-live {
    font-size: 9px;
    color: var(--accent);
    font-family: var(--font-mono);
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .activity-live .dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: var(--accent);
    animation: pulse 2s infinite;
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
  .activity-feed {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;
  }
  .activity-entry {
    padding: 8px 18px;
    border-bottom: 1px solid var(--border);
    transition: background 0.15s;
  }
  .activity-entry:hover { background: var(--bg-2); }
  .activity-entry:last-child { border-bottom: none; }
  .activity-entry-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 3px;
  }
  .activity-phase {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    font-family: var(--font-mono);
    padding: 2px 6px;
    border-radius: 2px;
  }
  .activity-phase.crawler { background: var(--blue-dim); color: var(--blue); }
  .activity-phase.filter { background: var(--amber-dim); color: var(--amber); }
  .activity-phase.downloader { background: rgba(160,100,255,0.12); color: #a064ff; }
  .activity-phase.scorer { background: var(--accent-dim); color: var(--accent); }
  .activity-phase.orchestrator { background: var(--bg-3); color: var(--text-1); }
  .activity-phase.intel { background: var(--blue-dim); color: var(--blue); }
  .activity-phase.system { background: var(--bg-3); color: var(--text-2); }
  .activity-time {
    font-size: 10px;
    color: var(--text-2);
    font-family: var(--font-mono);
    margin-left: auto;
  }
  .activity-msg {
    font-size: 11px;
    color: var(--text-1);
    line-height: 1.5;
    padding-left: 0;
  }

  /* ── EMPTY / LOADING STATES ── */
  .empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--text-2);
    gap: 12px;
    padding: 60px 28px;
  }
  .empty-state .icon { font-size: 36px; opacity: 0.3; }
  .empty-state p { font-size: 13px; text-align: center; max-width: 320px; line-height: 1.6; }
  .empty-state .sub { font-size: 11px; font-family: var(--font-mono); color: var(--text-2); }

  /* ── SCROLLBAR ── */
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--bg-4); border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: var(--text-2); }

  /* ── DATE ── */
  .date-col { font-family: var(--font-mono); font-size: 13px; font-weight: 500; line-height: 1.25; }
  .date-past { color: var(--text-2) !important; text-decoration: line-through; }
  .date-critical { color: var(--red) !important; font-weight: 700; background: var(--red-dim); padding: 2px 6px; border-radius: 4px; display: inline-block; }
  .date-urgent { color: #b86a2f !important; font-weight: 700; background: rgba(184,118,61,0.12); padding: 2px 6px; border-radius: 4px; display: inline-block; }
  .date-soon { color: var(--amber) !important; font-weight: 600; }
  .date-missing { font-family: var(--font-mono); font-size: 11px; color: var(--text-2); background: var(--bg-3); padding: 2px 6px; border-radius: 4px; display: inline-block; font-style: italic; }

  /* ── PRINT STYLES FOR PDF EXPORT ── */
  @media print {
    @page {
      size: letter;
      margin: 0.5in;
    }

    body {
      background: white !important;
      color: black !important;
    }

    /* Hide non-essential UI elements */
    .topbar,
    .main-nav,
    .btn-run,
    .exportButton,
    button {
      display: none !important;
    }

    /* Optimize for print */
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    /* Main container adjustments */
    .app-shell,
    [data-print-container] {
      background: white !important;
      overflow: visible !important;
      max-height: none !important;
    }

    /* Make content flow naturally */
    .contentGrid {
      display: block !important;
    }

    .leftColumn,
    .rightColumn {
      width: 100% !important;
      page-break-inside: avoid;
    }

    /* Opportunity cards */
    .oppCard {
      background: white !important;
      border: 1px solid #ccc !important;
      page-break-inside: avoid;
      margin-bottom: 16px;
    }

    /* Headers */
    h1, h2 {
      color: black !important;
      page-break-after: avoid;
    }

    /* Stat cards */
    .statCard {
      background: #f5f5f5 !important;
      border: 1px solid #ddd !important;
      page-break-inside: avoid;
    }

    /* Links should show URL */
    a[href]:after {
      content: " (" attr(href) ")";
      font-size: 9px;
      color: #666;
    }

    /* Hide empty sections */
    .emptyState {
      display: none !important;
    }
  }

  /* ─── MOBILE RESPONSIVE ──────────────────────────────────────────────────── */

  @media (max-width: 1024px) {
    .topbar {
      padding: 10px 16px;
    }
    .main-nav {
      gap: 2px;
    }
    .main-nav .btn-nav {
      padding: 6px 10px;
      font-size: 10px;
    }
    .table-wrap {
      padding: 0 12px 12px;
    }
    .detail-panel {
      width: 340px;
    }
  }

  @media (max-width: 768px) {
    .topbar {
      padding: 8px 12px;
      flex-wrap: wrap;
      gap: 8px;
    }
    .topbar-left {
      width: 100%;
      justify-content: space-between;
    }
    .main-nav {
      width: 100%;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      gap: 2px;
      padding-bottom: 4px;
    }
    .main-nav .btn-nav {
      padding: 6px 8px;
      font-size: 10px;
      white-space: nowrap;
      flex-shrink: 0;
    }
    .topbar-right {
      width: 100%;
      justify-content: flex-end;
      gap: 6px;
    }
    .toolbar {
      padding: 8px 12px;
      gap: 6px;
    }
    .toolbar-right {
      flex-wrap: wrap;
      gap: 6px;
    }
    .stats-bar {
      padding: 8px 12px;
      gap: 8px;
      flex-wrap: wrap;
    }
    .stat-card {
      min-width: 80px;
      padding: 8px 10px;
    }
    .stat-card .stat-value {
      font-size: 20px;
    }
    .stat-card .stat-label {
      font-size: 8px;
    }
    .table-wrap {
      padding: 0 8px 8px;
    }
    .opp-table th,
    .opp-table td {
      padding: 8px 6px;
      font-size: 11px;
    }
    /* Hide less-critical columns on tablet */
    .opp-table th:nth-child(n+6),
    .opp-table td:nth-child(n+6) {
      display: none;
    }
    .detail-panel {
      width: 100vw;
      right: 0;
      border-left: none;
    }
    .detail-panel .detail-header {
      padding: 12px;
    }
    .detail-panel .detail-body {
      padding: 12px;
    }
    .activity-panel {
      display: none;
    }
  }

  @media (max-width: 480px) {
    .topbar {
      padding: 6px 8px;
    }
    .main-nav .btn-nav {
      padding: 5px 6px;
      font-size: 9px;
    }
    .toolbar {
      padding: 6px 8px;
    }
    .stats-bar {
      padding: 6px 8px;
      gap: 6px;
    }
    .stat-card {
      min-width: 60px;
      padding: 6px 8px;
    }
    .stat-card .stat-value {
      font-size: 16px;
    }
    .opp-table th,
    .opp-table td {
      padding: 6px 4px;
      font-size: 10px;
    }
    /* Hide even more columns on phone */
    .opp-table th:nth-child(n+4),
    .opp-table td:nth-child(n+4) {
      display: none;
    }
    .name-col {
      max-width: 200px !important;
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// HELPER COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function ScoreCell({ value }) {
  if (value === "Pending" || value === undefined || value === null) {
    return <span style={{ color: "var(--text-2)", fontFamily: "var(--font-mono)", fontSize: 11 }}>pending...</span>;
  }
  const num = Number(value);
  const tier = num >= 70 ? "high" : num >= 40 ? "medium" : "low";
  return (
    <div className="score-badge">
      <div className={`score-ring ${tier}`}>{num}</div>
      <span style={{ fontSize: 12, color: "var(--text-1)" }}>/100</span>
    </div>
  );
}

function PillCell({ value, type }) {
  if (!value || value === "—") return <span style={{ color: "var(--text-2)" }}>—</span>;
  const cls = value.toLowerCase();
  return <span className={`pill ${cls}`}>{value}</span>;
}

// ── CAPTURE RISK CALCULATOR ──
// Factors: deadline proximity, competitor count, missing data fields, AI score
function getCaptureRisk(opp) {
  let riskScore = 0; // 0-100, higher = riskier

  // 1. Deadline proximity (0-30 points)
  if (opp.responseDeadline) {
    const daysLeft = calendarDaysUntil(opp.responseDeadline);
    if (daysLeft < 0) riskScore += 30;       // past due
    else if (daysLeft <= 7) riskScore += 25;  // urgent
    else if (daysLeft <= 14) riskScore += 15; // soon
    else if (daysLeft <= 30) riskScore += 5;  // comfortable
  } else {
    riskScore += 20; // unknown deadline is risky
  }

  // 2. Competitor count (0-25 points)
  let compCount = 0;
  if (opp.competitiveIntel) {
    const intel = typeof opp.competitiveIntel === 'string'
      ? (() => { try { return JSON.parse(opp.competitiveIntel); } catch { return null; } })()
      : opp.competitiveIntel;
    compCount = intel?.competitors?.length || 0;
  }
  if (compCount >= 8) riskScore += 25;
  else if (compCount >= 5) riskScore += 18;
  else if (compCount >= 3) riskScore += 10;
  else if (compCount >= 1) riskScore += 5;

  // 3. Extraction quality (0-25 points) — uses extraction score for holistic data quality
  const eqScore = getExtractionQuality(opp).score;
  if (eqScore < 25) riskScore += 25;
  else if (eqScore < 50) riskScore += 18;
  else if (eqScore < 75) riskScore += 10;
  else if (eqScore < 88) riskScore += 3;

  // 4. AI score weakness (0-20 points)
  if (opp.score == null) riskScore += 15;
  else if (opp.score < 40) riskScore += 20;
  else if (opp.score < 60) riskScore += 12;
  else if (opp.score < 75) riskScore += 5;

  // Classify
  if (riskScore >= 50) return { level: 'High', color: 'var(--red)', bg: 'var(--red-dim)', score: riskScore };
  if (riskScore >= 25) return { level: 'Medium', color: 'var(--amber)', bg: 'var(--amber-dim)', score: riskScore };
  return { level: 'Low', color: 'var(--accent)', bg: 'var(--accent-dim)', score: riskScore };
}

function RiskBadge({ opp, compact }) {
  const risk = getCaptureRisk(opp);
  const style = compact
    ? { display: 'inline-block', padding: '1px 6px', fontSize: 10, fontWeight: 700, borderRadius: 4, background: risk.bg, color: risk.color, fontFamily: 'var(--font-mono)', letterSpacing: '0.3px' }
    : { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', fontSize: 11, fontWeight: 700, borderRadius: 6, background: risk.bg, color: risk.color, fontFamily: 'var(--font-mono)' };
  return <span style={style}>{compact ? risk.level[0] : risk.level} Risk</span>;
}

// ── EXTRACTION QUALITY (computed client-side from available fields) ──
function getExtractionQuality(opp) {
  const gaps = [];
  if (!opp.responseDeadline) gaps.push('Response Date');
  if (!opp.agency || opp.agency === 'Unknown') gaps.push('Agency');
  if (!opp.estimatedValue || opp.estimatedValue === 'Unknown') gaps.push('Estimated Value');
  if (!opp.naics) gaps.push('NAICS');
  if (!opp.setAside || opp.setAside === 'None') gaps.push('Set-Aside');
  if (!opp.placeOfPerformance) gaps.push('Place of Performance');
  if (!Array.isArray(opp.attachments) || opp.attachments.length === 0) gaps.push('Attachments');
  if (!opp.description || opp.description.length < 50) gaps.push('Description');
  const score = Math.round(((8 - gaps.length) / 8) * 100);
  const level = score >= 75 ? 'High' : score >= 50 ? 'Medium' : 'Low';
  const color = score >= 75 ? 'var(--accent)' : score >= 50 ? 'var(--amber)' : 'var(--red)';
  const bg = score >= 75 ? 'var(--accent-dim)' : score >= 50 ? 'var(--amber-dim)' : 'var(--red-dim)';
  return { score, level, color, bg, gaps };
}

function PartnerIntelSection({ row }) {
  // Parse competitive intel — may be string (JSON) or object
  let intel = null;
  if (row.competitiveIntel) {
    if (typeof row.competitiveIntel === 'string') {
      try { intel = JSON.parse(row.competitiveIntel); } catch { intel = null; }
    } else {
      intel = row.competitiveIntel;
    }
  }

  const competitors = intel?.competitors || [];
  const hasData = competitors.length > 0;

  // Generate partner signal text
  const signalText = (() => {
    if (!hasData) return null;
    const topNames = competitors.slice(0, 3).map(c => c.name).join(', ');
    const naicsNote = row.naics ? ` in NAICS ${row.naics}` : '';
    let text = `Companies frequently winning contracts${naicsNote} include ${topNames}.`;
    // If top winner has high value, suggest subcontracting
    const topValue = competitors[0]?.totalValue || 0;
    if (topValue > 10000000 && competitors.length >= 3) {
      text += ' Consider positioning as a subcontractor supporting these primes for this requirement.';
    }
    return text;
  })();

  const formatValue = (v) => {
    if (!v || v === 0) return '—';
    if (v >= 1000000000) return `$${(v / 1000000000).toFixed(1)}B`;
    if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
    return `$${v.toLocaleString()}`;
  };

  const piStyles = {
    section: { marginTop: 0 },
    label: { fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10, fontFamily: 'var(--font-mono)' },
    signal: {
      padding: '10px 14px', background: 'rgba(76,159,255,0.08)', borderLeft: '3px solid var(--blue)',
      borderRadius: 4, fontSize: 12, lineHeight: 1.6, color: 'var(--text-1)', marginBottom: 12
    },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 },
    card: {
      background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
      padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 4
    },
    cardName: { fontSize: 12, fontWeight: 600, color: 'var(--text-0)', lineHeight: 1.3 },
    cardMeta: { display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' },
    cardValue: { color: 'var(--accent)', fontWeight: 500 },
    empty: { fontSize: 12, color: 'var(--text-2)', fontStyle: 'italic', padding: '8px 0' },
    recentWin: { fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 2, lineHeight: 1.4 }
  };

  return (
    <div className="detail-section" style={piStyles.section}>
      <div className="detail-section-label">Partner & Competitor Intelligence</div>

      {signalText && <div style={piStyles.signal}>{signalText}</div>}

      {hasData ? (
        <div style={piStyles.grid}>
          {competitors.slice(0, 8).map((c, i) => (
            <div key={i} style={piStyles.card}>
              <div style={piStyles.cardName}>{c.name}</div>
              <div style={piStyles.cardMeta}>
                <span><span style={piStyles.cardValue}>{c.contracts}</span> awards</span>
                <span><span style={piStyles.cardValue}>{formatValue(c.totalValue)}</span> total</span>
              </div>
              {c.recentWins?.[0] && (
                <div style={piStyles.recentWin}>
                  Latest: {c.recentWins[0].description ? (c.recentWins[0].description.length > 60 ? c.recentWins[0].description.substring(0, 60) + '…' : c.recentWins[0].description) : 'Contract award'}
                  {c.recentWins[0].amount ? ` — ${formatValue(c.recentWins[0].amount)}` : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={piStyles.empty}>
          {row.recommendation === 'PURSUE'
            ? 'Competitor research not yet available. Run the pipeline to generate USAspending analysis.'
            : 'Competitor research is available for pursue-recommended opportunities.'}
        </div>
      )}

      {/* Teaming Suggestions from Spend Intel */}
      {intel?.teamingSuggestions?.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Teaming Targets</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {intel.teamingSuggestions.map((t, i) => (
              <div key={i} style={{ background: 'rgba(76,159,255,0.10)', border: '1px solid rgba(76,159,255,0.2)', borderRadius: 4, padding: '5px 10px', fontSize: 11 }}>
                <span style={{ fontWeight: 600, color: 'var(--text-0)' }}>{t.name}</span>
                <span style={{ color: 'var(--text-2)', marginLeft: 6, fontFamily: 'var(--font-mono)', fontSize: 10 }}>{t.rationale}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agency Spend Context */}
      {intel?.agencySpendContext && (
        <div style={{ marginTop: 8, fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
          Agency context: {intel.agencySpendContext.totalAwards} awards totaling {formatValue(intel.agencySpendContext.totalSpend)} in similar scope (2yr)
        </div>
      )}

      {intel?.researchedAt && (
        <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 4 }}>
          Researched {new Date(intel.researchedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} via USAspending.gov
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCORE BREAKDOWN — Capture Probability V2
// Renders the 5-component weighted score with win posture and confidence.
// ─────────────────────────────────────────────────────────────────────────────
function ScoreBreakdown({ opp }) {
  const v2 = computeScoringV2(opp);
  if (!v2) return null;

  const ps = POSTURE_STYLE[v2.winPosture] || POSTURE_STYLE.MONITOR;
  const compOrder = [
    v2.components.capabilityFit,
    v2.components.marketAttractiveness,
    v2.components.competitivePosture,
    v2.components.executionFeasibility,
    v2.components.strategicValue,
  ];

  return (
    <div className="detail-section">
      <div className="detail-section-label">Capture Score Breakdown</div>
      <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--text-0)', letterSpacing: 1 }}>{v2.overallScore}</span>
          <span style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginRight: 4 }}>/100</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700, color: ps.color, background: ps.bg, border: `1px solid ${ps.border}`, borderRadius: 3, padding: '3px 8px', letterSpacing: 0.5 }}>{v2.winPosture}</span>
          <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: v2.confidence.color, fontWeight: 700 }}>{v2.confidence.label} CONF</span>
        </div>

        {/* Component bars */}
        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {compOrder.map(c => {
            const barColor = c.score >= 68 ? 'var(--accent)' : c.score >= 45 ? 'var(--amber)' : 'var(--red)';
            return (
              <div key={c.label}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{c.label}</span>
                  <span style={{ fontSize: 11, color: barColor, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{c.score}</span>
                </div>
                <div style={{ height: 4, background: 'var(--bg-3)', borderRadius: 2, overflow: 'hidden', marginBottom: 3 }}>
                  <div style={{ height: '100%', width: `${c.score}%`, background: barColor, borderRadius: 2 }} />
                </div>
                {c.signals[0] && (
                  <div style={{ fontSize: 10, color: 'var(--text-2)', lineHeight: 1.4 }}>{c.signals[0]}</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Top signals */}
        {v2.topReasons.length > 0 && (
          <div style={{ padding: '8px 14px 12px', borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Top Signals</div>
            {v2.topReasons.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 7, marginBottom: i < v2.topReasons.length - 1 ? 5 : 0 }}>
                <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0, marginTop: 5 }} />
                <span style={{ fontSize: 11, color: 'var(--text-1)', lineHeight: 1.4 }}>{r}</span>
              </div>
            ))}
          </div>
        )}
        {/* Score delta note — shown after re-score if score changed materially */}
        {opp.scoreDeltaNote && (
          <div style={{ padding: '6px 14px 10px', borderTop: '1px solid var(--border)' }}>
            <span style={{ fontSize: 10, color: 'var(--amber)', fontFamily: 'var(--font-mono)' }}>↻ {opp.scoreDeltaNote}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// IMPLICIT FEEDBACK — Derives a signal score [−50, 100] from BD actions
// Used by CalibrationView to surface model/user disagreements
// ─────────────────────────────────────────────────────────────────────────────
function computeImplicitFeedback(opp) {
  let signal = 0;
  const reasons = [];
  const status = (opp.bdStatus || 'new').toLowerCase();
  if (['pursuing', 'submitted'].includes(status)) { signal += 40; reasons.push('actively pursuing'); }
  else if (status === 'partnering')                { signal += 35; reasons.push('partnering'); }
  else if (status === 'won')                       { signal += 50; reasons.push('won'); }
  else if (['lost', 'archived'].includes(status))  { signal -= 30; reasons.push('passed/archived'); }

  if (opp.priority === 'high')   { signal += 25; reasons.push('high priority'); }
  else if (opp.priority === 'medium') { signal += 10; }

  const openTasks = (opp.captureTasks || []).filter(t => !t.done && !t.completed).length;
  if      (openTasks >= 3) { signal += 15; reasons.push(`${openTasks} capture tasks`); }
  else if (openTasks >  0) { signal +=  8; }

  if ((opp.notes || '').length > 50) { signal += 8; reasons.push('team notes on file'); }

  return { signal: Math.max(-50, Math.min(100, signal)), reasons };
}

function CaptureStrategy({ row }) {
  // Only show for scored opportunities
  if (!row.score && row.score !== 0) return null;

  const v2 = computeScoringV2(row);
  const winPosture = v2?.winPosture;
  const winPs = winPosture ? (POSTURE_STYLE[winPosture] || POSTURE_STYLE.MONITOR) : null;

  // Parse competitor data
  let competitors = [];
  if (row.competitiveIntel) {
    const intel = typeof row.competitiveIntel === 'string'
      ? (() => { try { return JSON.parse(row.competitiveIntel); } catch { return null; } })()
      : row.competitiveIntel;
    competitors = intel?.competitors || [];
  }

  // ── Pursue Recommendation ──
  const pursueText = row.recommendationReason || row.summary || (
    row.recommendation === 'PURSUE'
      ? 'This opportunity has been flagged for pursuit based on AI scoring criteria.'
      : row.recommendation === 'MONITOR'
        ? 'This opportunity is worth monitoring but does not yet meet the threshold for active pursuit.'
        : 'This opportunity does not strongly align with current pursuit criteria.'
  );

  // ── Suggested Position — v2 posture takes precedence over heuristics ──
  const suggestPosition = (() => {
    if (winPosture === 'PRIME') return { label: 'Prime Contractor', reason: v2?.components?.competitivePosture?.signals?.[0] || 'Scoring model recommends prime positioning based on capability fit and competitive field.' };
    if (winPosture === 'PARTNER') return { label: 'Subcontractor / Teaming Partner', reason: v2?.components?.competitivePosture?.signals?.[0] || 'Teaming is the strongest path given the competitive landscape.' };
    if (winPosture === 'PASS') return { label: 'No Bid', reason: 'Opportunity does not meet pursuit criteria.' };

    // Fallback: legacy heuristics
    const hasSetAside = row.setAside && row.setAside !== 'None' && row.setAside !== 'Total Small Business';
    const isSmallBiz = row.setAside && (row.setAside.includes('Small') || row.setAside.includes('8(a)') || row.setAside.includes('SDVOSB') || row.setAside.includes('HUBZone') || row.setAside.includes('WOSB'));
    const largePrimes = competitors.filter(c => c.totalValue > 50000000).length;
    if (isSmallBiz) return { label: 'Prime Contractor', reason: `Set-aside (${row.setAside}) favors direct prime positioning.` };
    if (hasSetAside) return { label: 'Prime Contractor', reason: `Set-aside designation (${row.setAside}) supports a direct bid.` };
    if (largePrimes >= 3) return { label: 'Subcontractor / Teaming Partner', reason: `${largePrimes} large primes dominate this space — teaming is recommended.` };
    if (competitors.length > 0 && competitors[0]?.totalValue > 100000000) return { label: 'Subcontractor or Technology Partner', reason: 'Incumbent landscape favors a teaming arrangement.' };
    return { label: 'Prime Contractor', reason: 'No dominant incumbents identified. Direct pursuit is viable.' };
  })();

  // ── Why This Fits ──
  const fitPoints = row.fitReasons && row.fitReasons.length > 0
    ? row.fitReasons
    : null;

  // ── Risks ──
  const risks = [];
  if (row.risks && row.risks.length > 0) {
    row.risks.forEach(r => risks.push(r));
  }
  // Add deadline risk
  if (row.responseDeadline) {
    const days = calendarDaysUntil(row.responseDeadline);
    if (days >= 0 && days <= 7) risks.unshift(`Response deadline in ${days} day${days !== 1 ? 's' : ''} — immediate action required.`);
    else if (days >= 0 && days <= 14) risks.unshift(`Response deadline in ${days} days — limited time for capture planning.`);
  } else if (row.recommendation === 'PURSUE') {
    risks.push('Response deadline not extracted — verify timeline before committing resources.');
  }
  // Add competition risk
  if (competitors.length >= 5) risks.push(`${competitors.length} competitors identified in this space — expect heavy competition.`);
  // Add missing data
  if (!row.naics) risks.push('NAICS code not captured — confirm industry classification.');
  if (!row.estimatedValue) risks.push('Contract value not disclosed — estimate required for bid/no-bid decision.');

  // ── Next Actions ──
  const actions = [];
  const hasAttachments = Array.isArray(row.attachments) && row.attachments.length > 0;
  if (hasAttachments && row.attachmentStatus !== 'complete') {
    actions.push('Download and review solicitation attachments');
  } else if (hasAttachments) {
    actions.push('Review solicitation documents and requirements');
  } else {
    actions.push('Check SAM.gov for solicitation documents');
  }
  if (competitors.length > 0) {
    actions.push(`Evaluate teaming opportunities with top contractors (${competitors.slice(0, 2).map(c => c.name).join(', ')})`);
  } else {
    actions.push('Identify potential teaming partners');
  }
  if (row.responseDeadline) {
    const days = calendarDaysUntil(row.responseDeadline);
    if (days >= 0 && days <= 14) actions.push('Begin proposal drafting immediately');
    else actions.push('Schedule capture planning review');
  } else {
    actions.push('Confirm response deadline and set internal milestones');
  }
  actions.push('Contact agency program office for additional context');

  // ── Likely Primes ──
  const topPrimes = competitors.slice(0, 5);

  const formatValue = (v) => {
    if (!v || v === 0) return '—';
    if (v >= 1000000000) return `$${(v / 1000000000).toFixed(1)}B`;
    if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
    return `$${v.toLocaleString()}`;
  };

  const cs = {
    card: {
      background: 'var(--bg-1)', border: '1px solid var(--accent-dim)',
      borderRadius: 'var(--radius)', padding: '0', overflow: 'hidden'
    },
    header: {
      padding: '12px 18px', borderBottom: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
    },
    headerTitle: {
      fontSize: 13, fontWeight: 700, color: 'var(--text-0)',
      fontFamily: 'var(--font-body)', letterSpacing: '0.2px'
    },
    recBadge: {
      fontSize: 10, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase',
      padding: '3px 10px', borderRadius: 3, fontFamily: 'var(--font-mono)'
    },
    body: { padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 16 },
    field: { display: 'flex', flexDirection: 'column', gap: 4 },
    fieldLabel: {
      fontSize: 10, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase',
      letterSpacing: '0.8px', fontFamily: 'var(--font-mono)'
    },
    fieldValue: { fontSize: 12, lineHeight: 1.6, color: 'var(--text-1)' },
    positionPill: {
      display: 'inline-block', padding: '4px 10px', fontSize: 11, fontWeight: 600,
      background: 'var(--accent-dim)', color: 'var(--accent)', borderRadius: 4,
      fontFamily: 'var(--font-mono)', marginBottom: 4
    },
    list: { margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3 },
    listItem: { fontSize: 12, lineHeight: 1.5, color: 'var(--text-1)' },
    primeRow: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 12
    },
    primeName: { color: 'var(--text-0)', fontWeight: 500 },
    primeStat: { color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 11 },
    actionItem: {
      display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, lineHeight: 1.5, color: 'var(--text-1)'
    },
    actionDot: {
      width: 5, height: 5, borderRadius: '50%', background: 'var(--accent)',
      marginTop: 7, flexShrink: 0
    }
  };

  const recColor = row.recommendation === 'PURSUE'
    ? { bg: 'var(--accent-dim)', color: 'var(--accent)' }
    : row.recommendation === 'MONITOR'
      ? { bg: 'rgba(229,160,0,0.12)', color: 'var(--amber)' }
      : { bg: 'rgba(255,76,76,0.12)', color: 'var(--red)' };

  return (
    <div className="detail-section" style={{ marginTop: 0 }}>
      <div style={cs.card}>
        <div style={cs.header}>
          <span style={cs.headerTitle}>AI Capture Strategy</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {winPosture && winPs && (
              <span style={{ ...cs.recBadge, backgroundColor: winPs.bg, color: winPs.color, border: `1px solid ${winPs.border}` }}>
                {winPosture}
              </span>
            )}
            <span style={{ ...cs.recBadge, backgroundColor: recColor.bg, color: recColor.color }}>
              {row.recommendation || 'UNSCORED'}
            </span>
          </div>
        </div>
        <div style={cs.body}>

          {/* Pursue Recommendation */}
          <div style={cs.field}>
            <div style={cs.fieldLabel}>Pursue Recommendation</div>
            <div style={cs.fieldValue}>{pursueText}</div>
          </div>

          {/* Suggested Position */}
          <div style={cs.field}>
            <div style={cs.fieldLabel}>Suggested Position</div>
            <div>
              <span style={cs.positionPill}>{suggestPosition.label}</span>
              <div style={{ ...cs.fieldValue, marginTop: 4 }}>{suggestPosition.reason}</div>
            </div>
          </div>

          {/* Likely Prime Contractors */}
          {topPrimes.length > 0 && (
            <div style={cs.field}>
              <div style={cs.fieldLabel}>Likely Prime Contractors</div>
              <div>
                {topPrimes.map((c, i) => (
                  <div key={i} style={{ ...cs.primeRow, ...(i === topPrimes.length - 1 ? { borderBottom: 'none' } : {}) }}>
                    <span style={cs.primeName}>{c.name}</span>
                    <span style={cs.primeStat}>{c.contracts} awards · {formatValue(c.totalValue)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Why This Fits */}
          {fitPoints && (
            <div style={cs.field}>
              <div style={cs.fieldLabel}>Why This Opportunity Fits</div>
              <ul style={cs.list}>
                {fitPoints.map((r, i) => <li key={i} style={cs.listItem}>{r}</li>)}
              </ul>
            </div>
          )}

          {/* Risks */}
          {risks.length > 0 && (
            <div style={cs.field}>
              <div style={cs.fieldLabel}>Risks & Considerations</div>
              <ul style={cs.list}>
                {risks.map((r, i) => <li key={i} style={cs.listItem}>{r}</li>)}
              </ul>
            </div>
          )}

          {/* Next Actions */}
          <div style={cs.field}>
            <div style={cs.fieldLabel}>Next Suggested Actions</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {actions.map((a, i) => (
                <div key={i} style={cs.actionItem}>
                  <div style={cs.actionDot} />
                  <span>{a}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RECOMMENDED PARTNERS
// Derives teaming recommendations from competitor intel, agency/NAICS overlap,
// market research, and opportunity characteristics.
// ─────────────────────────────────────────────────────────────────────────────

function RecommendedPartners({ row }) {
  // Only show for scored opportunities
  if (!row.score && row.score !== 0) return null;

  // Parse competitive intel
  let competitors = [];
  if (row.competitiveIntel) {
    const intel = typeof row.competitiveIntel === 'string'
      ? (() => { try { return JSON.parse(row.competitiveIntel); } catch { return null; } })()
      : row.competitiveIntel;
    competitors = intel?.competitors || [];
  }

  if (competitors.length === 0) {
    return (
      <div className="detail-section" style={{ marginTop: 0 }}>
        <div style={rp.card}>
          <div style={rp.header}>
            <span style={rp.headerTitle}>Recommended Partners</span>
          </div>
          <div style={rp.body}>
            <div style={rp.empty}>
              {row.recommendation === 'PURSUE'
                ? 'Partner recommendations will be available after the next pipeline run generates competitive intelligence.'
                : 'Partner analysis is generated for pursue-recommended opportunities.'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatVal = (v) => {
    if (!v || v === 0) return '—';
    if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
    if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
    return `$${v.toLocaleString()}`;
  };

  // Derive partner recommendations from competitors
  const isSmallBiz = row.setAside && (row.setAside.includes('Small') || row.setAside.includes('8(a)') || row.setAside.includes('SDVOSB') || row.setAside.includes('HUBZone') || row.setAside.includes('WOSB'));
  const oppText = (`${row.title || ''} ${row.description || ''}`).toLowerCase();

  // Mission area detection for rationale
  const missionAreas = [];
  const missionDefs = [
    ['cyber', 'Cybersecurity'], ['zero trust', 'Zero Trust'], ['cloud', 'Cloud Services'],
    ['devsecops', 'DevSecOps'], ['artificial intelligence', 'AI/ML'], ['machine learning', 'AI/ML'],
    ['command and control', 'C2/C4ISR'], ['c4isr', 'C2/C4ISR'], ['jadc2', 'JADC2'],
    ['software development', 'Software Development'], ['training', 'Training & Simulation'],
    ['data engineer', 'Data Engineering'], ['data analytics', 'Data Analytics'],
    ['electronic warfare', 'Electronic Warfare'],
  ];
  missionDefs.forEach(([term, label]) => {
    if (oppText.includes(term) && !missionAreas.includes(label)) missionAreas.push(label);
  });

  const partners = competitors.slice(0, 5).map(c => {
    const rationale = [];
    const overlaps = [];
    let role = 'Prime Partner';

    // Agency overlap
    if (row.agency) {
      const agencyMatch = c.recentWins?.some(w =>
        (w.description || '').toLowerCase().includes(row.agency.toLowerCase().split(' ')[0])
      );
      if (agencyMatch || c.contracts >= 3) {
        overlaps.push(`${row.agency} (${c.contracts} awards)`);
        rationale.push(`Established presence at ${row.agency} with ${c.contracts} prior awards totaling ${formatVal(c.totalValue)}.`);
      } else {
        rationale.push(`${c.contracts} awards totaling ${formatVal(c.totalValue)} in related contract space.`);
      }
    } else {
      rationale.push(`${c.contracts} awards totaling ${formatVal(c.totalValue)} in this contract space.`);
    }

    // NAICS overlap
    if (row.naics) {
      overlaps.push(`NAICS ${row.naics}`);
    }

    // Mission area overlap
    if (missionAreas.length > 0) {
      overlaps.push(missionAreas.slice(0, 2).join(', '));
    }

    // Role determination
    if (c.totalValue > 100000000) {
      role = 'Prime Partner';
      if (!isSmallBiz) rationale.push('Large contract portfolio suggests prime contractor positioning — consider subcontracting under this partner.');
    } else if (c.totalValue > 10000000) {
      role = 'Prime Partner';
    } else if (c.contracts >= 3) {
      role = 'Subcontract Partner';
      rationale.push('Strong award history but moderate contract size — good candidate for a peer teaming arrangement.');
    } else {
      role = 'Technology Partner';
      rationale.push('Focused contract history — potential technology or niche capability partner.');
    }

    // If set-aside, adjust role
    if (isSmallBiz && c.totalValue > 50000000) {
      role = 'Mentor / Prime Partner';
    }

    return {
      name: c.name,
      role,
      rationale: rationale[0],
      overlaps,
      awardHistory: `${c.contracts} awards · ${formatVal(c.totalValue)}`,
      recentWin: c.recentWins?.[0],
    };
  });

  // Generate "Why We Fit Under This Partner" based on opportunity data
  const whyWeFit = (() => {
    const points = [];
    if (row.fitReasons && row.fitReasons.length > 0) {
      points.push(row.fitReasons[0]);
    }
    if (missionAreas.length > 0) {
      points.push(`Core capabilities in ${missionAreas.slice(0, 3).join(', ')} align directly with this requirement.`);
    }
    if (isSmallBiz) {
      points.push(`Small business status qualifies for set-aside positioning under ${row.setAside}.`);
    }
    if (points.length === 0) {
      points.push('Technical capabilities and past performance align with the scope of this opportunity.');
    }
    return points.join(' ');
  })();

  // Generate outreach angle from opportunity characteristics
  const outreachAngle = (() => {
    if (missionAreas.includes('JADC2') || missionAreas.includes('C2/C4ISR')) {
      return 'Lead with rapid prototyping, operational interface design, and mission command integration.';
    }
    if (missionAreas.includes('Cybersecurity') || missionAreas.includes('Zero Trust')) {
      return 'Position as a zero trust implementation and security engineering specialist supporting enterprise modernization.';
    }
    if (missionAreas.includes('AI/ML')) {
      return 'Lead with AI/ML integration, model deployment, and decision support system development.';
    }
    if (missionAreas.includes('Cloud Services') || missionAreas.includes('DevSecOps')) {
      return 'Position as a cloud migration and DevSecOps automation specialist supporting platform modernization.';
    }
    if (missionAreas.includes('Software Development')) {
      return 'Lead with agile development, user-centered design, and full-stack application delivery.';
    }
    if (missionAreas.includes('Training & Simulation')) {
      return 'Position as a training technology and simulation integration specialist.';
    }
    if (missionAreas.includes('Data Engineering') || missionAreas.includes('Data Analytics')) {
      return 'Lead with data pipeline engineering, analytics dashboard development, and business intelligence.';
    }
    if (isSmallBiz) {
      return 'Leverage small business status and technical depth to pursue directly or as a high-value teaming partner.';
    }
    return 'Position as a mission-focused technology partner with rapid delivery capabilities and relevant domain expertise.';
  })();

  return (
    <div className="detail-section" style={{ marginTop: 0 }}>
      <div style={rp.card}>
        <div style={rp.header}>
          <span style={rp.headerTitle}>Recommended Partners</span>
          <span style={rp.headerCount}>{partners.length} identified</span>
        </div>
        <div style={rp.body}>

          {/* Partner Cards */}
          {partners.map((p, i) => (
            <div key={i} style={rp.partnerCard}>
              <div style={rp.partnerTop}>
                <div style={rp.partnerName}>{p.name}</div>
                <span style={rp.rolePill}>{p.role}</span>
              </div>
              <div style={rp.partnerRationale}>{p.rationale}</div>
              <div style={rp.partnerMeta}>
                <span style={rp.metaItem}>{p.awardHistory}</span>
                {p.overlaps.length > 0 && (
                  <span style={rp.metaItem}>{p.overlaps.join(' · ')}</span>
                )}
              </div>
            </div>
          ))}

          {/* Divider */}
          <div style={rp.divider} />

          {/* Why We Fit */}
          <div style={rp.field}>
            <div style={rp.fieldLabel}>Why We Fit Under This Partner</div>
            <div style={rp.fieldValue}>{whyWeFit}</div>
          </div>

          {/* Outreach Angle */}
          <div style={rp.field}>
            <div style={rp.fieldLabel}>Suggested Outreach Angle</div>
            <div style={rp.outreachValue}>{outreachAngle}</div>
          </div>

        </div>
      </div>
    </div>
  );
}

const rp = {
  card: {
    background: 'var(--bg-1)', border: '1px solid rgba(76,159,255,0.2)',
    borderRadius: 'var(--radius)', overflow: 'hidden',
  },
  header: {
    padding: '12px 18px', borderBottom: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: 13, fontWeight: 700, color: 'var(--text-0)',
    fontFamily: 'var(--font-body)', letterSpacing: '0.2px',
  },
  headerCount: {
    fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)',
    fontWeight: 600, letterSpacing: '0.5px',
  },
  body: { padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 },
  partnerCard: {
    background: 'var(--bg-2)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', padding: '12px 14px',
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  partnerTop: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  },
  partnerName: {
    fontSize: 13, fontWeight: 600, color: 'var(--text-0)', lineHeight: 1.3, flex: 1,
  },
  rolePill: {
    display: 'inline-block', padding: '3px 8px', fontSize: 9, fontWeight: 700,
    letterSpacing: '0.5px', textTransform: 'uppercase',
    fontFamily: 'var(--font-mono)', borderRadius: 3, flexShrink: 0,
    background: 'rgba(76,159,255,0.12)', color: '#4c9fff',
  },
  partnerRationale: {
    fontSize: 12, lineHeight: 1.5, color: 'var(--text-1)',
  },
  partnerMeta: {
    display: 'flex', gap: 12, flexWrap: 'wrap',
    fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)',
  },
  metaItem: { whiteSpace: 'nowrap' },
  divider: {
    height: 1, background: 'var(--border)', margin: '6px 0',
  },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  fieldLabel: {
    fontSize: 10, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase',
    letterSpacing: '0.8px', fontFamily: 'var(--font-mono)',
  },
  fieldValue: { fontSize: 12, lineHeight: 1.6, color: 'var(--text-1)' },
  outreachValue: {
    fontSize: 12, lineHeight: 1.6, color: 'var(--accent)', fontWeight: 500,
    fontStyle: 'italic',
  },
  empty: {
    fontSize: 12, color: 'var(--text-2)', fontStyle: 'italic', padding: '8px 0',
  },
};

function extractNamedContactsFromResearch(marketResearch) {
  const contacts = [];
  if (!marketResearch?.intelligence) return contacts;
  const text = marketResearch.intelligence;
  const patterns = [
    /(?:led by|managed by|headed by|overseen by|directed by)\s+([A-Z][a-z]+ [A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/g,
    /(?:program manager|project lead|director|contracting officer)\s*(?:is|:)?\s*([A-Z][a-z]+ [A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/gi,
  ];
  patterns.forEach((pattern) => {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const name = (match[1] || '').trim();
      if (name.length > 4 && name.length < 40 && !contacts.some(c => c.name === name)) {
        contacts.push({
          name,
          source: "Perplexity market research",
          citation: marketResearch.citations?.[0] || null,
        });
      }
    }
  });
  return contacts;
}

function detectMissionAreas(text) {
  const t = (text || "").toLowerCase();
  const missions = [];
  const defs = [
    ['Cyber', ['cyber', 'zero trust', 'siem', 'rmf']],
    ['Cloud', ['cloud', 'kubernetes', 'saas', 'iaas']],
    ['AI/ML', ['artificial intelligence', 'machine learning', 'ai/ml', 'llm']],
    ['C2', ['command and control', 'c2', 'c4isr', 'jadc2']],
    ['DevSecOps', ['devsecops', 'ci/cd', 'containeriz']],
    ['Data', ['data engineering', 'data analytics', 'etl', 'data pipeline']],
    ['Software', ['software development', 'application development', 'agile']],
    ['Training', ['training', 'simulation']],
  ];
  defs.forEach(([label, terms]) => {
    if (terms.some(term => t.includes(term))) missions.push(label);
  });
  return missions;
}

function outreachAngleForMission(missions, mode = "agency") {
  if (missions.includes('C2')) return mode === 'agency'
    ? 'Lead with mission command UI/UX and rapid prototyping support.'
    : 'Position as a subcontractor focused on operator workflows and training integration.';
  if (missions.includes('Cyber')) return 'Emphasize secure-by-design delivery and Zero Trust implementation support.';
  if (missions.includes('AI/ML')) return 'Position around applied AI/ML delivery with mission-ready human-in-the-loop workflows.';
  if (missions.includes('Cloud') || missions.includes('DevSecOps')) return 'Lead with cloud modernization and DevSecOps acceleration for faster fielding.';
  if (missions.includes('Data')) return 'Emphasize data engineering, interoperability, and decision-quality analytics.';
  return 'Emphasize command-and-control modernization and interface design.';
}

function findLikelyCompanyContact(opportunities, companyName) {
  const token = (companyName || "").split(/\s+/)[0]?.toLowerCase();
  if (!token) return null;
  for (const opp of opportunities) {
    const mr = opp.marketResearch;
    if (!mr?.intelligence) continue;
    const text = mr.intelligence;
    const lines = text.split(/[\n.]/).map(l => l.trim()).filter(Boolean);
    for (const line of lines) {
      if (!line.toLowerCase().includes(token)) continue;
      const nameMatch = line.match(/([A-Z][a-z]+ [A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/);
      if (nameMatch) {
        return {
          name: nameMatch[1],
          source: "Perplexity market research",
          citation: mr.citations?.[0] || null,
          note: line.length > 100 ? `${line.slice(0, 100)}...` : line,
        };
      }
    }
  }
  return null;
}

function AgencyContactSignals({ agency }) {
  if (!agency) return null;
  const opps = agency.opportunities || [];
  if (opps.length === 0) return null;

  const officeCounts = {};
  opps.forEach((o) => {
    const key = (o.office || '').trim();
    if (!key) return;
    officeCounts[key] = (officeCounts[key] || 0) + 1;
  });
  const topOffice = Object.entries(officeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || agency.name;

  const dept = opps.find(o => o.department)?.department || agency.name;
  const missionAreas = (agency.topMissionAreas || []).map(m => m.name);
  const setAsideCount = opps.filter(o => (o.setAside || '').toLowerCase().includes('small')).length;

  const namedContacts = [];
  opps.forEach((o) => {
    extractNamedContactsFromResearch(o.marketResearch).forEach((c) => {
      if (!namedContacts.some(existing => existing.name === c.name)) namedContacts.push(c);
    });
  });

  const signals = [
    {
      role: 'Contracting Officer',
      likelyName: namedContacts[0]?.name || null,
      org: topOffice,
      source: namedContacts[0] ? namedContacts[0].source : 'SAM opportunity metadata',
      citation: namedContacts[0]?.citation || null,
      reason: 'Primary authority for solicitation terms, amendment handling, and acquisition communications.',
      outreach: outreachAngleForMission(missionAreas, 'agency'),
    },
    {
      role: 'Program Manager',
      likelyName: namedContacts[1]?.name || null,
      org: dept,
      source: namedContacts[1] ? namedContacts[1].source : 'Agency structure + mission demand',
      citation: namedContacts[1]?.citation || null,
      reason: `Owns mission outcomes and technical priorities${missionAreas.length > 0 ? ` in ${missionAreas.slice(0, 2).join(' and ')}` : ''}.`,
      outreach: outreachAngleForMission(missionAreas, 'agency'),
    },
    {
      role: 'Small Business Office',
      likelyName: null,
      org: `${agency.name} OSDBU / Small Business Office`,
      source: 'Set-aside signals from active opportunities',
      citation: null,
      reason: setAsideCount > 0
        ? `${setAsideCount} opportunities include small-business language, indicating active small-business routing influence.`
        : 'Useful channel for teaming introductions and small-business positioning.',
      outreach: 'Position as a subcontractor focused on operator workflows and training integration.',
    },
    {
      role: 'Technical Point of Contact',
      likelyName: null,
      org: topOffice,
      source: 'Office field + mission area matching',
      citation: null,
      reason: 'Best source for requirements clarity, architecture constraints, and delivery expectations.',
      outreach: outreachAngleForMission(missionAreas, 'agency'),
    },
  ];

  const cs = {
    card: { background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 10 },
    row: { border: '1px solid var(--border)', background: 'var(--bg-1)', borderRadius: 6, padding: '10px 12px', marginBottom: 8 },
    top: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4 },
    role: { fontSize: 12, fontWeight: 700, color: 'var(--text-0)' },
    org: { fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' },
    name: { fontSize: 11, color: 'var(--blue)', fontWeight: 600 },
    reason: { fontSize: 11, color: 'var(--text-1)', lineHeight: 1.5, marginTop: 4 },
    outreach: { fontSize: 11, color: 'var(--accent)', marginTop: 4, fontStyle: 'italic' },
    source: { fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 4 },
  };

  return (
    <div style={cs.card}>
      {signals.map((s, i) => (
        <div key={i} style={cs.row}>
          <div style={cs.top}>
            <div style={cs.role}>{s.role}</div>
            <div style={cs.org}>{s.org}</div>
          </div>
          <div style={cs.name}>{s.likelyName || 'Role-based signal (name not confirmed)'}</div>
          <div style={cs.reason}>{s.reason}</div>
          <div style={cs.outreach}>{s.outreach}</div>
          <div style={cs.source}>
            Source: {s.source}
            {s.citation && (
              <>
                {' '}· <a href={s.citation} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue)' }}>citation</a>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── PARTNER CONTACT SIGNALS ──
// Contact roles and likely points-of-contact for partner engagement
function PartnerContactSignals({ row, agencyContext }) {
  const isAgencyMode = !!agencyContext;
  const opportunities = isAgencyMode ? (agencyContext.opportunities || []) : (row ? [row] : []);
  if (opportunities.length === 0) return null;

  const missionAreas = isAgencyMode
    ? (agencyContext.topMissionAreas || []).map(m => m.name)
    : detectMissionAreas(`${row.title || ''} ${row.description || ''}`);

  const partnerCandidates = isAgencyMode
    ? (agencyContext.topContractors || []).slice(0, 5).map(c => ({ name: c.name, awards: c.awards, totalValue: c.totalValue }))
    : (() => {
      let intel = row?.competitiveIntel;
      if (!intel) return [];
      if (typeof intel === 'string') {
        try { intel = JSON.parse(intel); } catch (err) { return []; }
      }
      return (intel?.competitors || []).slice(0, 5).map(c => ({
        name: c.name,
        awards: c.contracts || c.awardsCount || c.awards_count || 1,
        totalValue: c.totalValue || c.total_value || 0,
      }));
    })();

  if (partnerCandidates.length === 0) return null;

  const signals = partnerCandidates.map((partner) => {
    const likely = findLikelyCompanyContact(opportunities, partner.name);
    const roleSignal = missionAreas.includes('C2') ? 'Director, Mission Systems Capture'
      : missionAreas.includes('Cyber') ? 'Cyber Programs BD Director'
      : missionAreas.includes('AI/ML') ? 'AI/ML Solutions Lead'
      : missionAreas.includes('Cloud') || missionAreas.includes('DevSecOps') ? 'Cloud Programs Capture Manager'
      : 'Federal Growth / Capture Lead';

    return {
      company: partner.name,
      contact: likely?.name || roleSignal,
      contactType: likely?.name ? 'Likely contact name' : 'Role signal',
      reason: `${partner.awards} award${partner.awards !== 1 ? 's' : ''} and ${partner.totalValue ? `$${Math.round(partner.totalValue).toLocaleString()}` : 'material'} contract footprint indicate relevance in this account.`,
      outreach: outreachAngleForMission(missionAreas, 'partner'),
      source: likely ? likely.source : 'USAspending competitive intel',
      citation: likely?.citation || (row?.marketResearch?.citations?.[0] || null),
      note: likely?.note || null,
    };
  });

  const cs = {
    section: { marginTop: 0 },
    card: { background: 'var(--bg-1)', border: '1px solid rgba(76,159,255,0.15)', borderRadius: 'var(--radius)', overflow: 'hidden' },
    header: { padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    headerTitle: { fontSize: 12, fontWeight: 700, color: 'var(--text-0)' },
    headerCount: { fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' },
    body: { padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 8 },
    signalCard: { background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '9px 10px' },
    top: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 3 },
    company: { color: 'var(--text-0)', fontWeight: 700, fontSize: 12 },
    typeBadge: { fontSize: 9, color: 'var(--blue)', fontFamily: 'var(--font-mono)', background: 'rgba(76,159,255,0.1)', borderRadius: 3, padding: '1px 6px' },
    contact: { fontSize: 11, color: 'var(--text-1)' },
    reason: { fontSize: 11, color: 'var(--text-1)', lineHeight: 1.45, marginTop: 4 },
    outreach: { fontSize: 11, color: 'var(--accent)', lineHeight: 1.4, marginTop: 4, fontStyle: 'italic' },
    source: { fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 4 },
  };

  return (
    <div className="detail-section" style={cs.section}>
      <div style={cs.card}>
        <div style={cs.header}>
          <span style={cs.headerTitle}>Partner Contact Signals</span>
          <span style={cs.headerCount}>{signals.length} signals</span>
        </div>
        <div style={cs.body}>
          {signals.map((s, i) => (
            <div key={i} style={cs.signalCard}>
              <div style={cs.top}>
                <span style={cs.company}>{s.company}</span>
                <span style={cs.typeBadge}>{s.contactType}</span>
              </div>
              <div style={cs.contact}>{s.contact}</div>
              <div style={cs.reason}>{s.reason}</div>
              <div style={cs.outreach}>{s.outreach}</div>
              <div style={cs.source}>
                Source: {s.source}
                {s.citation && (
                  <>
                    {' '}· <a href={s.citation} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue)' }}>citation</a>
                  </>
                )}
              </div>
              {s.note && <div style={cs.source}>Note: {s.note}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── CHANGE HISTORY ──
// Shows tracked changes for an opportunity (self-contained fetch)
function ChangeHistory({ noticeId }) {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const BASE = process.env.REACT_APP_BACKEND_URL ?? "";

  useEffect(() => {
    if (!noticeId) return;
    setLoading(true);
    const token = localStorage.getItem('bd_auth_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${BASE}/api/opportunity-changes/${encodeURIComponent(noticeId)}`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setChanges(data); setLoading(false); })
      .catch(() => { setChanges([]); setLoading(false); });
  }, [noticeId, BASE]);

  if (loading) return null;
  if (changes.length === 0) return null;

  const ch = {
    card: { background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' },
    header: { padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    headerTitle: { fontSize: 12, fontWeight: 700, color: 'var(--text-0)' },
    headerCount: { fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' },
    body: { padding: '6px 16px', maxHeight: 220, overflowY: 'auto' },
    row: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 12 },
    dot: { width: 6, height: 6, borderRadius: '50%', background: 'var(--amber)', flexShrink: 0, marginTop: 5 },
    text: { flex: 1, color: 'var(--text-1)', lineHeight: 1.5 },
    old: { textDecoration: 'line-through', color: 'var(--text-2)' },
    new: { color: 'var(--accent)', fontWeight: 500 },
    time: { fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', marginTop: 1 },
  };

  const formatChangeText = (c) => {
    if (c.changeType.includes('attachment')) {
      return c.newValue ? `New attachment: ${c.newValue}` : `Removed: ${c.oldValue}`;
    }
    if (c.changeType.includes('Amendment')) return c.changeType;
    const label = c.changeType.replace(' changed', '');
    if (c.oldValue && c.newValue) {
      return <>{label}: <span style={ch.old}>{c.oldValue}</span> → <span style={ch.new}>{c.newValue}</span></>;
    }
    if (c.newValue) return <>{label} set to <span style={ch.new}>{c.newValue}</span></>;
    return c.changeType;
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
           d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="detail-section" style={{ marginTop: 0 }}>
      <div style={ch.card}>
        <div style={ch.header}>
          <span style={ch.headerTitle}>Change History</span>
          <span style={ch.headerCount}>{changes.length} detected</span>
        </div>
        <div style={ch.body}>
          {changes.map((c, i) => (
            <div key={c.id || i} style={{ ...ch.row, ...(i === changes.length - 1 ? { borderBottom: 'none' } : {}) }}>
              <span style={{ ...ch.dot, background: c.changeType.includes('deadline') || c.changeType.includes('Response') ? 'var(--red)' : c.changeType.includes('attachment') || c.changeType.includes('Amendment') ? 'var(--amber)' : 'var(--blue)' }} />
              <div style={ch.text}>{formatChangeText(c)}</div>
              <span style={ch.time}>{formatTime(c.detectedAt)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── RELATED NOTICES ─────────────────────────────────────────────────────────
// External intel section — pulls Perplexity-gathered intel from defense industry
// publications and public sources for a specific opportunity. On-demand only
// (cost-controlled) with a 7-day server-side cache.
function ExternalIntelSection({ row, authToken, onUpdate }) {
  const BASE = process.env.REACT_APP_BACKEND_URL ?? "";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intel = row.externalIntel;
  const fetchedAt = row.externalIntelFetchedAt;

  const runIntel = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) headers.Authorization = `Bearer ${authToken}`;
      const url = `${BASE}/api/opportunities/${encodeURIComponent(row.noticeId)}/intel${force ? '?force=true' : ''}`;
      const res = await fetch(url, { method: 'POST', headers });
      const data = await res.json();
      if (data.ok && data.intel) {
        onUpdate && onUpdate(data.intel, new Date().toISOString());
      } else if (data.reason) {
        setError(`Could not fetch intel: ${data.reason}`);
      } else if (data.error) {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!intel && !loading && !error) {
    return (
      <div className="detail-section" style={{ marginTop: 14 }}>
        <div className="detail-section-label">External Intel</div>
        <div style={{ fontSize: 12, color: 'var(--text-1)', marginBottom: 8, lineHeight: 1.5 }}>
          See what defense industry publications (Hatch Report, GovCon Wire, Defense One, etc.) and public sources are saying about this opportunity. ~$0.30 per query, cached 7 days.
        </div>
        <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => runIntel(false)}>
          Pull external intel
        </button>
      </div>
    );
  }

  return (
    <div className="detail-section" style={{ marginTop: 14 }}>
      <div className="detail-section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>External Intel</span>
        <button
          className="btn-secondary"
          style={{ fontSize: 11, padding: '4px 10px' }}
          onClick={() => runIntel(true)}
          disabled={loading}
        >
          {loading ? 'Fetching…' : 'Refresh'}
        </button>
      </div>

      {fetchedAt && (
        <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>
          last fetched {new Date(fetchedAt).toLocaleString()}
        </div>
      )}

      {error && (
        <div style={{ fontSize: 12, color: 'var(--red)', background: 'var(--red-dim)', padding: 8, borderRadius: 4, marginBottom: 8 }}>
          {error}
        </div>
      )}

      {intel && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12, lineHeight: 1.55 }}>
          {Array.isArray(intel.sources) && intel.sources.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 6 }}>What sources are saying</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {intel.sources.map((s, i) => (
                  <div key={i} style={{ padding: '8px 10px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 4 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-0)', marginBottom: 2 }}>{s.publication || 'Source'}</div>
                    {s.summary && <div style={{ color: 'var(--text-1)', marginBottom: 4 }}>{s.summary}</div>}
                    {s.dateContext && <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{s.dateContext}</div>}
                    {s.url && <div style={{ marginTop: 4 }}><a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontSize: 11 }}>Read source →</a></div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {intel.incumbent && (intel.incumbent.name || intel.incumbent.confidence) && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 4 }}>Likely incumbent</div>
              <div style={{ color: 'var(--text-0)' }}>
                <strong>{intel.incumbent.name || 'Unknown'}</strong>
                {intel.incumbent.confidence && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>({intel.incumbent.confidence} confidence)</span>}
              </div>
              {intel.incumbent.evidence && <div style={{ color: 'var(--text-1)', marginTop: 3 }}>{intel.incumbent.evidence}</div>}
            </div>
          )}

          {intel.competitiveLandscape && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 4 }}>Competitive landscape</div>
              <div style={{ color: 'var(--text-0)' }}>{intel.competitiveLandscape}</div>
            </div>
          )}

          {intel.recentAgencyNews && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 4 }}>Recent agency news</div>
              <div style={{ color: 'var(--text-0)' }}>{intel.recentAgencyNews}</div>
            </div>
          )}

          {Array.isArray(intel.relatedPrograms) && intel.relatedPrograms.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 4 }}>Related programs</div>
              <div style={{ color: 'var(--text-0)' }}>{intel.relatedPrograms.join(' · ')}</div>
            </div>
          )}

          {intel.gapsToInvestigate && (
            <div style={{ padding: 10, background: 'var(--gold-dim)', borderLeft: '3px solid var(--gold)', borderRadius: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 4 }}>Gaps to investigate before bidding</div>
              <div style={{ color: 'var(--text-0)' }}>{intel.gapsToInvestigate}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RelatedNotices({ noticeId, onSelectNotice }) {
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const BASE = process.env.REACT_APP_BACKEND_URL ?? "";

  useEffect(() => {
    if (!noticeId) return;
    setLoading(true);
    const token = localStorage.getItem('bd_auth_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${BASE}/api/related-notices/${encodeURIComponent(noticeId)}`, { headers })
      .then(r => r.ok ? r.json() : { related: [] })
      .then(data => { setRelated(data.related || []); setLoading(false); })
      .catch(() => { setRelated([]); setLoading(false); });
  }, [noticeId, BASE]);

  if (loading || related.length === 0) return null;

  const RELATION_LABELS = {
    lifecycle_progression: { label: 'Lifecycle', color: 'var(--blue)' },
    amendment: { label: 'Amendment', color: 'var(--amber)' },
    same_effort: { label: 'Same Effort', color: 'var(--accent)' },
    related_requirement: { label: 'Related', color: 'var(--text-2)' },
  };

  const rn = {
    card: { background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' },
    header: { padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    headerTitle: { fontSize: 12, fontWeight: 700, color: 'var(--text-0)' },
    headerCount: { fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' },
    body: { padding: '6px 0', maxHeight: 280, overflowY: 'auto' },
    row: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 12, transition: 'background 0.15s' },
    title: { flex: 1, color: 'var(--text-1)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    badge: (color) => ({ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '2px 6px', borderRadius: 3, border: `1px solid ${color}`, color, flexShrink: 0 }),
    meta: { fontSize: 10, color: 'var(--text-2)', flexShrink: 0, textAlign: 'right', minWidth: 60 },
    confidence: { fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' },
    reason: { fontSize: 10, color: 'var(--text-2)', padding: '0 16px 6px 42px', lineHeight: 1.4 },
  };

  return (
    <div className="detail-section" style={{ marginTop: 0 }}>
      <div style={rn.card}>
        <div style={rn.header}>
          <span style={rn.headerTitle}>Related Notices</span>
          <span style={rn.headerCount}>{related.length} linked</span>
        </div>
        <div style={rn.body}>
          {related.slice(0, 10).map((r, i) => {
            const rel = RELATION_LABELS[r.relationType] || { label: r.relationType, color: 'var(--text-2)' };
            return (
              <div key={r.noticeId || i}>
                <div
                  style={{ ...rn.row, ...(i === Math.min(related.length, 10) - 1 ? { borderBottom: 'none' } : {}) }}
                  onClick={() => onSelectNotice && onSelectNotice(r.noticeId)}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={rn.badge(rel.color)}>{rel.label}</span>
                  <span style={rn.title}>{r.title || r.noticeId}</span>
                  {r.score != null && <span style={rn.meta}>{r.score}pts</span>}
                  <span style={rn.confidence}>{Math.round(r.confidence * 100)}%</span>
                </div>
                {r.reason && <div style={rn.reason}>{r.reason}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── GRAPH SIGNALS COMPONENT ─────────────────────────────────────────────────
function GraphSignals({ entityType, entityId, title }) {
  const [signals, setSignals] = useState(null);
  const [loading, setLoading] = useState(true);
  const BASE = process.env.REACT_APP_BACKEND_URL ?? "";

  useEffect(() => {
    if (!entityType || !entityId) return;
    setLoading(true);
    const token = localStorage.getItem('bd_auth_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${BASE}/api/graph/${entityType}/${encodeURIComponent(entityId)}`, { headers })
      .then(r => r.ok ? r.json() : { signals: {} })
      .then(data => { setSignals(data.signals || {}); setLoading(false); })
      .catch(() => { setSignals({}); setLoading(false); });
  }, [entityType, entityId, BASE]);

  if (loading || !signals || Object.keys(signals).length === 0) return null;

  const TYPE_LABELS = {
    agency: 'Agencies', contractor: 'Contractors', mission_area: 'Mission Areas',
    program: 'Programs', opportunity: 'Opportunities',
  };
  const gs = {
    card: { background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginTop: 12 },
    header: { padding: '10px 16px', borderBottom: '1px solid var(--border)' },
    headerTitle: { fontSize: 12, fontWeight: 700, color: 'var(--text-0)' },
    section: { padding: '8px 16px', borderBottom: '1px solid var(--border)' },
    sectionTitle: { fontSize: 10, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 },
    chip: { display: 'inline-block', fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'var(--bg-2)', color: 'var(--text-1)', marginRight: 6, marginBottom: 4, border: '1px solid var(--border)' },
    strength: { fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginLeft: 4 },
  };

  // Filter out opportunity type (too noisy) and the same entity type
  const displayTypes = Object.keys(signals).filter(t => t !== entityType && t !== 'opportunity');

  return (
    <div style={gs.card}>
      <div style={gs.header}>
        <span style={gs.headerTitle}>{title || 'Connected Entities'}</span>
      </div>
      {displayTypes.map(type => {
        const items = signals[type] || [];
        const top = items.slice(0, 8);
        if (top.length === 0) return null;
        return (
          <div key={type} style={gs.section}>
            <div style={gs.sectionTitle}>{TYPE_LABELS[type] || type} ({items.length})</div>
            <div>
              {top.map((item, i) => (
                <span key={item.id || i} style={gs.chip}>
                  {item.id}
                  {item.strength < 1 && <span style={gs.strength}>{Math.round(item.strength * 100)}%</span>}
                </span>
              ))}
              {items.length > 8 && <span style={{ fontSize: 10, color: 'var(--text-2)' }}>+{items.length - 8} more</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── ECOSYSTEM SUMMARY ───────────────────────────────────────────────────────
// Compact graph-derived intelligence panel for Opportunity, Program, Account
function EcosystemSummary({ opp, program, account }) {
  // Build signals from whatever entity is provided
  const items = [];
  const gs = opp?.graphSignals || {};

  if (opp) {
    if (gs.relatedNoticeCount > 0) items.push({ icon: '↔', label: `${gs.relatedNoticeCount} related notice${gs.relatedNoticeCount > 1 ? 's' : ''}`, detail: gs.relatedConfidence >= 0.7 ? 'high confidence' : gs.relatedConfidence >= 0.4 ? 'moderate confidence' : '' });
    if (gs.lifecycleThreadCount > 0) items.push({ icon: '⟳', label: `${gs.lifecycleThreadCount} lifecycle thread${gs.lifecycleThreadCount > 1 ? 's' : ''}`, detail: 'prior/follow-on notices linked' });
    if (gs.linkedProgramCount > 0) items.push({ icon: '◈', label: `Part of ${(gs.linkedProgramNames || []).join(', ') || 'program cluster'}`, detail: `strength ${Math.round(gs.programStrength * 100)}%` });
    if (gs.connectedContractorCount > 0) items.push({ icon: '◆', label: `${gs.connectedContractorCount} contractor${gs.connectedContractorCount > 1 ? 's' : ''} in competitive field`, detail: '' });
    if (gs.contractorEcosystemSize > 0) items.push({ icon: '⊞', label: `${gs.contractorEcosystemSize} contractors active at agency`, detail: gs.contractorEcosystemSize >= 8 ? 'crowded ecosystem' : gs.contractorEcosystemSize <= 2 ? 'narrow field' : '' });
    if (gs.agencyMissionDensity >= 3) items.push({ icon: '▣', label: `${(gs.linkedMissionAreas || []).join(', ')}`, detail: `density ${gs.agencyMissionDensity.toFixed(1)}` });
    if (gs.amendmentCount > 0) items.push({ icon: '✎', label: `${gs.amendmentCount} amendment${gs.amendmentCount > 1 ? 's' : ''} tracked`, detail: '' });
  }
  if (program) {
    const agencyCount = (program.agenciesInvolved || []).length;
    const contractorCount = (program.detectedContractors || []).length;
    const missionCount = (program.missionAreas || []).length;
    if (agencyCount > 0) items.push({ icon: '◈', label: `${agencyCount} linked agenc${agencyCount > 1 ? 'ies' : 'y'}`, detail: (program.agenciesInvolved || []).slice(0, 2).join(', ') });
    if (contractorCount > 0) items.push({ icon: '◆', label: `${contractorCount} contractor${contractorCount > 1 ? 's' : ''} detected`, detail: (program.detectedContractors || []).slice(0, 2).map(c => c.name).join(', ') });
    if (missionCount > 0) items.push({ icon: '▣', label: `${missionCount} mission area${missionCount > 1 ? 's' : ''}`, detail: (program.missionAreas || []).slice(0, 3).map(m => m.name || m).join(', ') });
    if (program.opportunityCount > 0) items.push({ icon: '↔', label: `${program.opportunityCount} linked solicitation${program.opportunityCount > 1 ? 's' : ''}`, detail: '' });
  }
  if (account) {
    const progCount = (account.programs || []).length;
    const contractorCount = (account.topContractors || []).length;
    if (progCount > 0) items.push({ icon: '◈', label: `${progCount} program${progCount > 1 ? 's' : ''}`, detail: (account.programs || []).slice(0, 2).map(p => p.programName || p.name || p).join(', ') });
    if (contractorCount > 0) items.push({ icon: '◆', label: `${contractorCount} known contractor${contractorCount > 1 ? 's' : ''}`, detail: (account.topContractors || []).slice(0, 2).map(c => c.name).join(', ') });
    if (account.opportunityCount > 0) items.push({ icon: '↔', label: `${account.opportunityCount} active solicitation${account.opportunityCount > 1 ? 's' : ''}`, detail: '' });
  }

  if (items.length === 0) return null;

  const es = {
    wrap: { background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginTop: 8 },
    header: { padding: '8px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 },
    title: { fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.5px' },
    body: { padding: '6px 14px' },
    row: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12 },
    icon: { width: 16, textAlign: 'center', fontSize: 11, color: 'var(--accent)', flexShrink: 0 },
    label: { color: 'var(--text-0)', fontWeight: 500 },
    detail: { color: 'var(--text-2)', fontSize: 10, marginLeft: 'auto', fontFamily: 'var(--font-mono)' },
  };

  return (
    <div style={es.wrap}>
      <div style={es.header}>
        <span style={es.title}>Ecosystem</span>
      </div>
      <div style={es.body}>
        {items.slice(0, 6).map((item, i) => (
          <div key={i} style={es.row}>
            <span style={es.icon}>{item.icon}</span>
            <span style={es.label}>{item.label}</span>
            {item.detail && <span style={es.detail}>{item.detail}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

const DEFAULT_TASKS = [
  'Review solicitation attachments',
  'Identify potential teaming partners',
  'Contact agency program office',
  'Begin capture planning'
];

function CaptureTasks({ row, onUpdate }) {
  const [newTask, setNewTask] = useState('');
  const tasks = Array.isArray(row.captureTasks) && row.captureTasks.length > 0
    ? row.captureTasks
    : null;

  const initTasks = () => {
    const initial = DEFAULT_TASKS.map(text => ({ text, done: false, due: null, id: Date.now() + Math.random() }));
    onUpdate(row.noticeId, { captureTasks: initial });
  };

  const toggleTask = (idx) => {
    const updated = tasks.map((t, i) => i === idx ? { ...t, done: !t.done } : t);
    onUpdate(row.noticeId, { captureTasks: updated });
  };

  const addTask = () => {
    if (!newTask.trim()) return;
    const entry = { text: newTask.trim(), done: false, due: null, id: Date.now() };
    const updated = [...(tasks || []), entry];
    onUpdate(row.noticeId, { captureTasks: updated });
    setNewTask('');
  };

  const removeTask = (idx) => {
    const updated = tasks.filter((_, i) => i !== idx);
    onUpdate(row.noticeId, { captureTasks: updated });
  };

  const setDue = (idx, date) => {
    const updated = tasks.map((t, i) => i === idx ? { ...t, due: date || null } : t);
    onUpdate(row.noticeId, { captureTasks: updated });
  };

  const ct = {
    section: { marginTop: 0 },
    list: { display: 'flex', flexDirection: 'column', gap: 0 },
    item: {
      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
      borderBottom: '1px solid var(--border)', fontSize: 12
    },
    checkbox: {
      width: 16, height: 16, borderRadius: 3, border: '1px solid var(--text-2)',
      background: 'transparent', cursor: 'pointer', display: 'flex',
      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      color: 'var(--accent)', fontSize: 11, fontWeight: 700
    },
    checkboxDone: {
      background: 'var(--accent-dim)', borderColor: 'var(--accent)'
    },
    text: { flex: 1, color: 'var(--text-0)', lineHeight: 1.4 },
    textDone: { textDecoration: 'line-through', color: 'var(--text-2)' },
    due: {
      fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)',
      cursor: 'pointer', background: 'none', border: 'none', padding: 0
    },
    dueInput: {
      fontSize: 10, fontFamily: 'var(--font-mono)', background: 'var(--bg-3)',
      border: '1px solid var(--border)', borderRadius: 3, color: 'var(--text-1)',
      padding: '2px 4px'
    },
    remove: {
      background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer',
      fontSize: 14, padding: '0 4px', opacity: 0.5
    },
    addRow: {
      display: 'flex', gap: 8, marginTop: 10, alignItems: 'center'
    },
    addInput: {
      flex: 1, background: 'var(--bg-2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', color: 'var(--text-0)', padding: '6px 10px',
      fontSize: 12, fontFamily: 'var(--font-body)'
    },
    addBtn: {
      padding: '6px 12px', fontSize: 11, fontWeight: 600, color: 'var(--accent)',
      background: 'var(--accent-dim)', border: '1px solid var(--accent-glow)',
      borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap'
    },
    initBtn: {
      padding: '8px 16px', fontSize: 12, fontWeight: 600, color: 'var(--accent)',
      background: 'var(--accent-dim)', border: '1px solid var(--accent-glow)',
      borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)'
    },
    empty: { fontSize: 12, color: 'var(--text-2)', marginBottom: 10 },
    progress: {
      fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)',
      marginBottom: 8
    }
  };

  const doneCount = tasks ? tasks.filter(t => t.done).length : 0;

  return (
    <div className="detail-section" style={ct.section}>
      <div className="detail-section-label">Capture Tasks</div>

      {!tasks ? (
        <div>
          <div style={ct.empty}>No capture tasks yet. Start with the default checklist or add your own.</div>
          <button style={ct.initBtn} onClick={initTasks}>Start Default Checklist</button>
        </div>
      ) : (
        <>
          <div style={ct.progress}>{doneCount}/{tasks.length} complete</div>
          <div style={ct.list}>
            {tasks.map((t, i) => (
              <div key={t.id || i} style={ct.item}>
                <button
                  style={{ ...ct.checkbox, ...(t.done ? ct.checkboxDone : {}) }}
                  onClick={() => toggleTask(i)}
                >
                  {t.done ? '✓' : ''}
                </button>
                <span style={{ ...ct.text, ...(t.done ? ct.textDone : {}) }}>{t.text}</span>
                <input
                  type="date"
                  style={ct.dueInput}
                  value={t.due || ''}
                  onChange={e => setDue(i, e.target.value)}
                  title="Set due date"
                />
                {t.due && (() => {
                  const days = Math.ceil((new Date(t.due) - new Date()) / 86400000);
                  if (days < 0) return <span style={{ fontSize: 9, color: 'var(--red)', fontFamily: 'var(--font-mono)' }}>overdue</span>;
                  if (days <= 3) return <span style={{ fontSize: 9, color: '#f97316', fontFamily: 'var(--font-mono)' }}>{days}d</span>;
                  return null;
                })()}
                <button style={ct.remove} onClick={() => removeTask(i)} title="Remove task">×</button>
              </div>
            ))}
          </div>
          <div style={ct.addRow}>
            <input
              style={ct.addInput}
              value={newTask}
              onChange={e => setNewTask(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addTask()}
              placeholder="Add a task..."
            />
            <button style={ct.addBtn} onClick={addTask}>Add</button>
          </div>
        </>
      )}
    </div>
  );
}

function DateCell({ value, kind = "deadline" }) {
  // BD pros need to know whether each record is actionable. A blank cell or
  // "Not extracted yet" leaves them guessing; a clearly-labeled "No deadline"
  // signals "this is a forecast or notice without a fixed due date" so they
  // can deprioritize it instead of getting confused.
  if (!value || value === "—") {
    return <span className="date-missing">{kind === "posted" ? "Not posted" : "No deadline"}</span>;
  }
  const date = parseCalendarDate(value);
  if (isNaN(date)) return <span className="date-missing">{value}</span>;
  const formatted = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  // Posted dates describe when a notice entered the market. They are expected
  // to be historical, so overdue/deadline styling is never meaningful here.
  if (kind === "posted") {
    return <span className="date-col">{formatted}</span>;
  }

  const days = calendarDaysUntil(value);
  let cls = "";
  if (days < 0) cls = "date-past";
  else if (days <= 7) cls = "date-critical";
  else if (days <= 14) cls = "date-urgent";
  else if (days <= 30) cls = "date-soon";
  const daysLabel = days < 0
    ? `PAST DUE (${-days}d ago)`
    : days === 0
      ? "DUE TODAY"
      : days === 1
        ? "1 day left"
        : `${days} days left`;
  return (
    <span className={`date-col ${cls}`}>
      {formatted}
      <span style={{ fontSize: 10, display: "block", marginTop: 2, fontWeight: 500 }}>{daysLabel}</span>
    </span>
  );
}

function formatTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ─────────────────────────────────────────────────────────────────────────────
// PURSUITS BOARD (Kanban)
// ─────────────────────────────────────────────────────────────────────────────

const CAPTURE_STAGES = ['Discover', 'Qualify', 'Position', 'Pursue', 'Bid', 'Submitted'];
const BOARD_COLUMNS = [
  { key: 'Discover',  label: 'Discover',  color: 'var(--text-2)' },
  { key: 'Qualify',   label: 'Qualify',   color: '#8b5cf6' },
  { key: 'Position',  label: 'Position',  color: '#3b82f6' },
  { key: 'Pursue',    label: 'Pursue',    color: 'var(--accent)' },
  { key: 'Bid',       label: 'Bid',       color: 'var(--gold)' },
  { key: 'Submitted', label: 'Submitted', color: '#4c9fff' },
];

function captureNextAction(stage, opp = {}) {
  const actions = {
    Discover: 'Confirm the notice, deadline, documents, and basic company relevance.',
    Qualify: 'Validate vehicle access, security, customer need, and bid/no-bid gates.',
    Position: 'Map the customer and incumbent, select posture, and begin partner outreach.',
    Pursue: 'Run the capture review, close readiness gaps, and approve proposal investment.',
    Bid: 'Build the compliance matrix, lock the team, and produce the proposal.',
    Submitted: 'Track amendments, evaluation communications, award, and lessons learned.',
  };
  if (opp.responseDeadline) {
    const days = calendarDaysUntil(opp.responseDeadline);
    if (days >= 0 && days <= 7 && stage !== 'Submitted') return `Deadline in ${days} day${days === 1 ? '' : 's'} — complete the ${stage} gate immediately.`;
  }
  return actions[stage] || actions.Discover;
}

function PursuitsBoard({ opportunities, onUpdateStatus, onSelectRow }) {
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [recentChanges, setRecentChanges] = useState({});
  const BASE = process.env.REACT_APP_BACKEND_URL ?? "";

  // Fetch recent changes to show badges on cards
  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('bd_auth_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${BASE}/api/recent-changes`, { headers });
        if (res.ok) {
          const data = await res.json();
          // Group by noticeId — store the most important change type per opp
          const map = {};
          data.forEach(c => {
            if (!map[c.noticeId]) {
              // Prioritize: Amendment > Deadline > Attachment > other
              map[c.noticeId] = c.changeType;
            }
          });
          setRecentChanges(map);
        }
      } catch (_) { /* silent */ }
    };
    load();
  }, [BASE]);

  const boardOpps = useMemo(() => {
    return opportunities
      .filter(o => o.source !== 'usaspending-gov')
      .filter(o => o.bdStatus !== 'archived' && o.bdStatus !== 'lost')
      .filter(o => o.recommendation === 'PURSUE' || CAPTURE_STAGES.indexOf(o.captureStage || 'Discover') > 0 || ['reviewing', 'pursuing', 'partnering', 'submitted'].includes(o.bdStatus));
  }, [opportunities]);

  const columns = useMemo(() => {
    const cols = {};
    BOARD_COLUMNS.forEach(c => { cols[c.key] = []; });
    boardOpps.forEach(o => {
      const legacyStage = { reviewing: 'Qualify', pursuing: 'Pursue', partnering: 'Position', submitted: 'Submitted' }[o.bdStatus];
      const stage = o.captureStage && o.captureStage !== 'Discover' ? o.captureStage : (legacyStage || 'Discover');
      if (cols[stage]) cols[stage].push(o);
    });
    // Sort each column by score descending
    Object.values(cols).forEach(arr => arr.sort((a, b) => (b.score || 0) - (a.score || 0)));
    return cols;
  }, [boardOpps]);

  const handleDragStart = (e, opp) => {
    setDragId(opp.noticeId || opp.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', opp.noticeId || opp.id);
  };

  const handleDragOver = (e, colKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOver !== colKey) setDragOver(colKey);
  };

  const handleDragLeave = () => {
    setDragOver(null);
  };

  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData('text/plain');
    if (!id) return;
    const opp = boardOpps.find(o => (o.noticeId || o.id) === id);
    if (!opp || opp.captureStage === newStatus) { setDragId(null); return; }
    onUpdateStatus(opp.noticeId, { captureStage: newStatus });
    setDragId(null);
  };

  const handleDragEnd = () => {
    setDragId(null);
    setDragOver(null);
  };

  const formatDeadline = (iso) => {
    if (!iso) return null;
    const d = parseCalendarDate(iso);
    if (isNaN(d)) return null;
    const days = calendarDaysUntil(iso);
    const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (days < 0) return { text: `${dateStr} (past)`, cls: 'past' };
    if (days <= 7) return { text: `${dateStr} (${days}d)`, cls: 'urgent' };
    if (days <= 14) return { text: `${dateStr} (${days}d)`, cls: 'soon' };
    return { text: dateStr, cls: 'normal' };
  };

  const priorityColors = {
    high: { bg: 'var(--red-dim)', color: 'var(--red)' },
    medium: { bg: 'var(--amber-dim)', color: 'var(--amber)' },
    low: { bg: 'var(--bg-3)', color: 'var(--text-2)' },
  };

  const totalCount = boardOpps.length;

  return (
    <div style={kb.wrap}>
      {/* Board Header */}
      <div style={kb.boardHeader}>
        <div>
          <h2 style={kb.boardTitle}>PURSUITS BOARD</h2>
          <span style={kb.boardSub}>{totalCount} active capture{totalCount !== 1 ? 's' : ''} across {BOARD_COLUMNS.length} stages</span>
        </div>
      </div>

      {/* Columns */}
      <div style={kb.board}>
        {BOARD_COLUMNS.map(col => {
          const items = columns[col.key] || [];
          const isOver = dragOver === col.key;
          return (
            <div
              key={col.key}
              style={{
                ...kb.column,
                ...(isOver ? { background: 'var(--bg-2)', borderColor: col.color } : {}),
              }}
              onDragOver={(e) => handleDragOver(e, col.key)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, col.key)}
            >
              {/* Column header */}
              <div style={kb.colHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ ...kb.colDot, background: col.color }} />
                  <span style={kb.colLabel}>{col.label}</span>
                </div>
                <span style={kb.colCount}>{items.length}</span>
              </div>

              {/* Cards */}
              <div style={kb.colBody}>
                {items.length === 0 ? (
                  <div style={kb.emptyCol}>
                    No opportunities in {col.label.toLowerCase()}
                  </div>
                ) : (
                  items.map(opp => {
                    const deadline = formatDeadline(opp.responseDeadline);
                    const isDragging = dragId === (opp.noticeId || opp.id);
                    return (
                      <div
                        key={opp.noticeId || opp.id}
                        style={{
                          ...kb.card,
                          ...(isDragging ? { opacity: 0.4 } : {}),
                          borderLeftColor: col.color,
                          position: 'relative',
                        }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, opp)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onSelectRow && onSelectRow(opp)}
                      >
                        {/* Remove-from-board button (top right).
                            Sets bdStatus: 'archived' — the opportunity stays in the
                            shared DB and the dashboard table; it just drops off the
                            kanban. Stops propagation so clicking × doesn't also open
                            the detail panel. */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUpdateStatus(opp.noticeId, { bdStatus: 'archived' });
                          }}
                          title="Remove from board (archive)"
                          aria-label="Remove from board"
                          style={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            width: 30,
                            height: 30,
                            border: '1px solid var(--red)',
                            background: 'var(--red-dim)',
                            color: 'var(--red)',
                            cursor: 'pointer',
                            fontSize: 18,
                            lineHeight: 1,
                            padding: 0,
                            borderRadius: 4,
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = 'var(--red)';
                            e.currentTarget.style.color = '#fff';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = 'var(--red-dim)';
                            e.currentTarget.style.color = 'var(--red)';
                          }}
                        >
                          ×
                        </button>

                        {/* Title + Change Badge */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, paddingRight: 28 }}>
                          <div style={{ ...kb.cardTitle, flex: 1 }}>
                            {opp.title && opp.title.length > 70
                              ? opp.title.substring(0, 67) + '...'
                              : opp.title || 'Untitled'}
                          </div>
                          {recentChanges[opp.noticeId] && (
                            <span style={kb.changeBadge}>
                              {recentChanges[opp.noticeId].includes('Amendment') ? 'Amendment'
                                : recentChanges[opp.noticeId].includes('deadline') || recentChanges[opp.noticeId].includes('Response') ? 'Deadline Changed'
                                : recentChanges[opp.noticeId].includes('attachment') ? 'New Attachment'
                                : 'Updated'}
                            </span>
                          )}
                        </div>

                        {/* Agency */}
                        {opp.agency && (
                          <div style={kb.cardAgency}>{opp.agency}</div>
                        )}

                        {/* Meta row: score + deadline + priority */}
                        <div style={kb.cardMeta}>
                          {opp.score != null && (
                            <span style={{
                              ...kb.scorePill,
                              background: opp.score >= 70 ? 'var(--accent-dim)' : opp.score >= 40 ? 'var(--amber-dim)' : 'var(--red-dim)',
                              color: opp.score >= 70 ? 'var(--accent)' : opp.score >= 40 ? 'var(--amber)' : 'var(--red)',
                            }}>
                              {opp.score}
                            </span>
                          )}
                          {deadline && (
                            <span style={{
                              ...kb.deadlinePill,
                              color: deadline.cls === 'urgent' ? 'var(--red)' : deadline.cls === 'soon' ? 'var(--amber)' : deadline.cls === 'past' ? 'var(--text-2)' : 'var(--text-1)',
                            }}>
                              {deadline.text}
                            </span>
                          )}
                          {opp.priority && (
                            <span style={{
                              ...kb.priorityPill,
                              background: (priorityColors[opp.priority] || priorityColors.low).bg,
                              color: (priorityColors[opp.priority] || priorityColors.low).color,
                            }}>
                              {opp.priority}
                            </span>
                          )}
                          <RiskBadge opp={opp} compact />
                        </div>

                        {/* Capture Progress */}
                        {Array.isArray(opp.captureTasks) && opp.captureTasks.length > 0 && (() => {
                          const done = opp.captureTasks.filter(t => t.done).length;
                          const total = opp.captureTasks.length;
                          const pct = Math.round((done / total) * 100);
                          return (
                            <div style={kb.captureProgress}>
                              <div style={{ ...kb.progressBar, width: `${pct}%` }} />
                              <span style={kb.progressText}>Tasks {done} / {total}</span>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const kb = {
  wrap: {
    flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '20px 24px',
  },
  boardHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 20, flexShrink: 0,
  },
  boardTitle: {
    fontFamily: 'var(--font-display)', fontSize: 22, letterSpacing: 3,
    color: 'var(--text-0)', margin: 0,
  },
  boardSub: {
    fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 4,
    display: 'block',
  },
  board: {
    display: 'flex', gap: 12, flex: 1, overflow: 'hidden', minHeight: 0,
  },
  column: {
    flex: 1, display: 'flex', flexDirection: 'column',
    background: 'var(--bg-1)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', overflow: 'hidden',
    transition: 'background 0.15s, border-color 0.15s',
    minWidth: 0,
  },
  colHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0,
  },
  colDot: {
    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
  },
  colLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase',
    fontFamily: 'var(--font-mono)', color: 'var(--text-0)',
  },
  colCount: {
    fontSize: 11, fontWeight: 600, color: 'var(--text-2)',
    fontFamily: 'var(--font-mono)', background: 'var(--bg-3)',
    padding: '2px 8px', borderRadius: 10,
  },
  colBody: {
    flex: 1, overflowY: 'auto', padding: 8, display: 'flex',
    flexDirection: 'column', gap: 8,
  },
  emptyCol: {
    fontSize: 12, color: 'var(--text-2)', fontStyle: 'italic',
    textAlign: 'center', padding: '32px 12px', lineHeight: 1.5,
  },
  card: {
    background: 'var(--bg-0)', border: '1px solid var(--border)',
    borderLeft: '3px solid var(--text-2)',
    borderRadius: 'var(--radius)', padding: '10px 12px',
    cursor: 'grab', transition: 'opacity 0.15s, box-shadow 0.15s',
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  cardTitle: {
    fontSize: 12, fontWeight: 600, color: 'var(--text-0)',
    lineHeight: 1.4, wordBreak: 'break-word',
  },
  cardAgency: {
    fontSize: 11, color: 'var(--text-2)', lineHeight: 1.3,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  cardMeta: {
    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
  },
  scorePill: {
    display: 'inline-block', padding: '2px 6px', borderRadius: 3,
    fontSize: 10, fontWeight: 700, fontFamily: 'var(--font-mono)',
  },
  deadlinePill: {
    fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 500,
  },
  priorityPill: {
    display: 'inline-block', padding: '2px 6px', borderRadius: 3,
    fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
    fontFamily: 'var(--font-mono)', letterSpacing: '0.5px',
  },
  captureProgress: {
    position: 'relative', height: 16, background: 'var(--bg-2)',
    borderRadius: 3, overflow: 'hidden', marginTop: 2,
  },
  progressBar: {
    position: 'absolute', top: 0, left: 0, height: '100%',
    background: 'var(--accent-dim)', borderRadius: 3,
    transition: 'width 0.3s',
  },
  progressText: {
    position: 'relative', zIndex: 1, fontSize: 9, fontWeight: 600,
    fontFamily: 'var(--font-mono)', color: 'var(--text-1)',
    lineHeight: '16px', paddingLeft: 6,
  },
  changeBadge: {
    display: 'inline-block', padding: '1px 5px', fontSize: 8, fontWeight: 700,
    borderRadius: 3, background: 'var(--amber-dim)', color: 'var(--amber)',
    fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.3px',
    whiteSpace: 'nowrap', flexShrink: 0, lineHeight: '14px',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// RESEARCH VIEW
// ─────────────────────────────────────────────────────────────────────────────
// WHAT'S NEW VIEW
// Shows all recently discovered opportunities sorted by newest, with AI scores.
// This is the Hatch Report equivalent — everything SAM.gov posted recently,
// scored against the company profile so you know what matters.
// ─────────────────────────────────────────────────────────────────────────────

function WhatsNewView({ opportunities, onSelectRow, onNavigate, authToken }) {
  const [timeRange, setTimeRange] = useState(3); // days
  const [sortBy, setSortBy] = useState('newest'); // newest | score | deadline

  const now = Date.now();
  const cutoff = now - (timeRange * 86400000);

  // Get recent opportunities
  const recent = (opportunities || []).filter(o => {
    const found = new Date(o.crawledAt || o.foundAt || o.createdAt || 0).getTime();
    return found > cutoff && o.pipelineStage !== 'expired';
  });

  // Sort
  const sorted = [...recent].sort((a, b) => {
    if (sortBy === 'score') return (b.score || 0) - (a.score || 0);
    if (sortBy === 'deadline') {
      const aD = a.responseDeadline ? new Date(a.responseDeadline).getTime() : Infinity;
      const bD = b.responseDeadline ? new Date(b.responseDeadline).getTime() : Infinity;
      return aD - bD;
    }
    // newest
    return new Date(b.crawledAt || b.foundAt || b.createdAt || 0) - new Date(a.crawledAt || a.foundAt || a.createdAt || 0);
  });

  const scored = sorted.filter(o => o.score != null);
  const unscored = sorted.filter(o => o.score == null);
  const pursue = scored.filter(o => o.recommendation === 'PURSUE');
  const monitor = scored.filter(o => o.recommendation === 'MONITOR');

  // Group by service branch
  const groupByBranch = (opps) => {
    const groups = {};
    for (const o of opps) {
      const agency = (o.agency || '').toLowerCase();
      let branch = 'Other';
      if (agency.includes('army')) branch = 'Army';
      else if (agency.includes('navy') || agency.includes('marine') || agency.includes('usmc')) branch = 'Navy / USMC';
      else if (agency.includes('air force') || agency.includes('space force')) branch = 'Air Force / Space Force';
      else if (agency.includes('darpa') || agency.includes('defense advanced')) branch = 'DARPA';
      else if (agency.includes('defense') || agency.includes('dod') || agency.includes('joint') || agency.includes('socom') || agency.includes('disa') || agency.includes('dla') || agency.includes('nga') || agency.includes('nsa')) branch = 'OSD / Joint';
      else if (agency.includes('homeland') || agency.includes('dhs')) branch = 'DHS / Civilian';
      if (!groups[branch]) groups[branch] = [];
      groups[branch].push(o);
    }
    return groups;
  };

  const grouped = groupByBranch(sorted);
  const branchOrder = ['Army', 'Navy / USMC', 'Air Force / Space Force', 'DARPA', 'OSD / Joint', 'DHS / Civilian', 'Other'];

  const s = {
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
    title: { fontSize: 20, fontWeight: 700, color: 'var(--text-0)' },
    subtitle: { fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' },
    controls: { display: 'flex', gap: 8, alignItems: 'center' },
    btn: (active) => ({ padding: '5px 12px', borderRadius: 4, border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border)'), background: active ? 'var(--accent-dim)' : 'var(--bg-2)', color: active ? 'var(--accent)' : 'var(--text-2)', fontSize: 11, fontFamily: 'var(--font-mono)', cursor: 'pointer', fontWeight: 600 }),
    statsRow: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
    stat: { padding: '8px 14px', background: 'var(--bg-1)', borderRadius: 6, border: '1px solid var(--border)', textAlign: 'center', minWidth: 80 },
    statVal: { fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-mono)' },
    statLabel: { fontSize: 9, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-mono)' },
    branchHeader: { fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'var(--font-mono)', marginTop: 20, marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--border)' },
    card: { padding: '12px 16px', background: 'var(--bg-1)', borderRadius: 6, border: '1px solid var(--border)', marginBottom: 8, cursor: 'pointer', transition: 'border-color 0.15s' },
    cardHover: { borderColor: 'var(--accent)' },
    cardTitle: { fontSize: 13, fontWeight: 600, color: 'var(--text-0)', marginBottom: 4 },
    cardMeta: { fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 6 },
    cardDesc: { fontSize: 12, color: 'var(--text-1)', lineHeight: 1.6, maxHeight: 60, overflow: 'hidden' },
    scoreBadge: (score) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-mono)', background: score >= 70 ? 'var(--accent-dim)' : score >= 40 ? 'var(--gold-dim)' : 'var(--bg-3)', color: score >= 70 ? 'var(--accent)' : score >= 40 ? 'var(--gold)' : 'var(--text-2)' }),
    recBadge: (rec) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.5px', marginLeft: 6, background: rec === 'PURSUE' ? 'var(--accent-dim)' : rec === 'MONITOR' ? 'var(--gold-dim)' : 'var(--bg-3)', color: rec === 'PURSUE' ? 'var(--accent)' : rec === 'MONITOR' ? 'var(--gold)' : 'var(--text-2)' }),
    newDot: { display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', marginRight: 6, verticalAlign: 'middle' },
    empty: { textAlign: 'center', padding: 40, color: 'var(--text-2)', fontSize: 13 },
  };

  const daysAgo = (dateStr) => {
    if (!dateStr) return '';
    const d = Math.floor((now - new Date(dateStr).getTime()) / 86400000);
    if (d === 0) return 'Today';
    if (d === 1) return 'Yesterday';
    return `${d}d ago`;
  };

  const deadlineDisplay = (dl) => {
    if (!dl) return '';
    const d = calendarDaysUntil(dl, new Date(now));
    if (d < 0) return 'Expired';
    if (d === 0) return 'Due today';
    if (d <= 7) return `${d}d left`;
    return parseCalendarDate(dl).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div>
      <div style={s.header}>
        <div>
          <div style={s.title}>What's New</div>
          <div style={s.subtitle}>Every new SAM.gov posting, scored by AI</div>
        </div>
        <div style={s.controls}>
          <span style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>Show:</span>
          {[1, 3, 7, 14].map(d => (
            <button key={d} style={s.btn(timeRange === d)} onClick={() => setTimeRange(d)}>{d}d</button>
          ))}
          <span style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginLeft: 8 }}>Sort:</span>
          {[{k:'newest',l:'Newest'},{k:'score',l:'Score'},{k:'deadline',l:'Deadline'}].map(({k,l}) => (
            <button key={k} style={s.btn(sortBy === k)} onClick={() => setSortBy(k)}>{l}</button>
          ))}
        </div>
      </div>

      <div style={s.statsRow}>
        <div style={s.stat}>
          <div style={{ ...s.statVal, color: 'var(--accent)' }}>{sorted.length}</div>
          <div style={s.statLabel}>Discovered</div>
        </div>
        <div style={s.stat}>
          <div style={{ ...s.statVal, color: scored.length > 0 ? 'var(--text-0)' : 'var(--text-2)' }}>{scored.length}</div>
          <div style={s.statLabel}>Scored</div>
        </div>
        <div style={s.stat}>
          <div style={{ ...s.statVal, color: pursue.length > 0 ? 'var(--accent)' : 'var(--text-2)' }}>{pursue.length}</div>
          <div style={s.statLabel}>Pursue</div>
        </div>
        <div style={s.stat}>
          <div style={{ ...s.statVal, color: monitor.length > 0 ? 'var(--gold)' : 'var(--text-2)' }}>{monitor.length}</div>
          <div style={s.statLabel}>Monitor</div>
        </div>
        <div style={s.stat}>
          <div style={{ ...s.statVal, color: unscored.length > 0 ? 'var(--red, #ef4444)' : 'var(--text-2)' }}>{unscored.length}</div>
          <div style={s.statLabel}>Unscored</div>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div style={s.empty}>
          No opportunities discovered in the last {timeRange} day{timeRange > 1 ? 's' : ''}.<br />
          <span style={{ fontSize: 11 }}>Try expanding the time range, or trigger a pipeline run.</span>
        </div>
      ) : (
        branchOrder.map(branch => {
          const branchOpps = grouped[branch];
          if (!branchOpps || branchOpps.length === 0) return null;
          return (
            <div key={branch}>
              <div style={s.branchHeader}>{branch} ({branchOpps.length})</div>
              {branchOpps.map(opp => (
                <div key={opp.noticeId || opp.id}
                  style={s.card}
                  onClick={() => { if (onSelectRow) onSelectRow(opp); else if (onNavigate) onNavigate({ filter: 'all', selectId: opp.noticeId || opp.id }); }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={s.cardTitle}>
                      {daysAgo(opp.crawledAt || opp.foundAt) === 'Today' && <span style={s.newDot} />}
                      {opp.title}
                    </div>
                    <div>
                      {opp.score != null && <span style={s.scoreBadge(opp.score)}>{opp.score}</span>}
                      {opp.recommendation && opp.recommendation !== 'PASS' && <span style={s.recBadge(opp.recommendation)}>{opp.recommendation}</span>}
                    </div>
                  </div>
                  <div style={s.cardMeta}>
                    <span>{opp.agency || 'Unknown Agency'}</span>
                    {opp.noticeType && <span>{opp.noticeType}</span>}
                    {opp.setAside && <span>{opp.setAside}</span>}
                    {opp.estimatedValue && <span>{opp.estimatedValue}</span>}
                    <span>{daysAgo(opp.crawledAt || opp.foundAt)}</span>
                    {opp.responseDeadline && <span style={{ color: deadlineDisplay(opp.responseDeadline).includes('left') ? 'var(--gold)' : 'inherit' }}>Due: {deadlineDisplay(opp.responseDeadline)}</span>}
                  </div>
                  {opp.summary ? (
                    <div style={s.cardDesc}>{opp.summary}</div>
                  ) : opp.description ? (
                    <div style={s.cardDesc}>{opp.description.substring(0, 200)}...</div>
                  ) : null}
                </div>
              ))}
            </div>
          );
        })
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RESEARCH VIEW
// Market intelligence, AI insights, partner intel, and strategic recommendations
// ─────────────────────────────────────────────────────────────────────────────

function DiagnosticsView({ authToken }) {
  const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const headers = {};
        if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
        const res = await fetch(`${BASE_URL}/api/diagnostics`, { headers });
        if (res.ok) setData(await res.json());
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, [authToken, BASE_URL]);

  if (loading) return <div style={{ padding: 24, color: 'var(--text-2)' }}>Loading diagnostics...</div>;
  if (!data) return <div style={{ padding: 24, color: 'var(--text-2)' }}>Failed to load diagnostics.</div>;

  const s = {
    wrap: { padding: 24, maxWidth: 960, overflowY: 'auto', height: '100%' },
    title: { fontSize: 16, fontWeight: 700, color: 'var(--text-0)', marginBottom: 4 },
    sub: { fontSize: 12, color: 'var(--text-2)', marginBottom: 20, lineHeight: 1.5 },
    section: { marginBottom: 28 },
    sectionTitle: { fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 },
    card: { background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 6, padding: '10px 14px' },
    cardLabel: { fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', marginBottom: 2 },
    cardValue: { fontSize: 22, fontFamily: 'var(--font-display)', color: 'var(--text-0)', fontWeight: 700 },
    cardSub: { fontSize: 10, color: 'var(--text-2)', marginTop: 2 },
    bar: (pct, color) => ({ height: 6, borderRadius: 3, background: `linear-gradient(90deg, ${color} ${pct}%, var(--bg-3) ${pct}%)`, marginTop: 4 }),
    flag: { fontSize: 12, color: 'var(--amber)', padding: '6px 10px', background: 'var(--amber-dim)', borderRadius: 4, marginBottom: 4, fontFamily: 'var(--font-mono)' },
    sourceRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid var(--border)', fontSize: 12 },
  };

  const { extraction, scoring, sources, pipeline, qualityFlags } = data;

  const ExtractionCard = ({ label, field }) => {
    const pct = field.pct;
    const color = pct >= 75 ? 'var(--accent)' : pct >= 50 ? 'var(--amber)' : 'var(--red)';
    return (
      <div style={s.card}>
        <div style={s.cardLabel}>{label}</div>
        <div style={{ ...s.cardValue, color }}>{pct}%</div>
        <div style={s.bar(pct, color)} />
        <div style={s.cardSub}>{field.present}/{field.present + field.missing} present</div>
      </div>
    );
  };

  return (
    <div style={s.wrap}>
      <div style={{ marginBottom: 24 }}>
        <div style={s.title}>Platform Diagnostics</div>
        <div style={s.sub}>
          Extraction completeness, scoring distribution, pipeline health, and data quality flags.
          {pipeline.lastRun && ` Last run: ${new Date(pipeline.lastRun).toLocaleString()}`}
          {pipeline.running && ` · Pipeline running (${pipeline.currentPhase})`}
        </div>
      </div>

      {/* Quality Flags */}
      {qualityFlags.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Quality Flags</div>
          {qualityFlags.map((f, i) => <div key={i} style={s.flag}>{f}</div>)}
        </div>
      )}

      {/* Extraction Completeness */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Extraction Completeness ({extraction.total} active records)</div>
        <div style={s.grid}>
          <ExtractionCard label="Response Date" field={extraction.responseDeadline} />
          <ExtractionCard label="Agency" field={extraction.agency} />
          <ExtractionCard label="Est. Value" field={extraction.estimatedValue} />
          <ExtractionCard label="NAICS" field={extraction.naics} />
          <ExtractionCard label="PSC" field={extraction.psc} />
          <ExtractionCard label="Set-Aside" field={extraction.setAside} />
          <ExtractionCard label="Place of Perf." field={extraction.placeOfPerformance} />
          <ExtractionCard label="Description" field={extraction.description} />
          <ExtractionCard label="Attachments" field={extraction.attachments} />
          {extraction.contactName && <ExtractionCard label="Contact" field={extraction.contactName} />}
          {extraction.noticeType && <ExtractionCard label="Notice Type" field={extraction.noticeType} />}
        </div>
      </div>

      {/* Field Provenance Breakdown */}
      {data.provenance && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Field Provenance (where values came from)</div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', background: 'var(--bg-1)' }}>
            <div style={{ ...s.sourceRow, fontWeight: 700, background: 'var(--bg-2)', fontSize: 10 }}>
              <span style={{ flex: 1 }}>Field</span>
              <span style={{ width: 55, textAlign: 'right' }}>SAM.gov</span>
              <span style={{ width: 55, textAlign: 'right', color: 'var(--gold)' }}>Inferred</span>
              <span style={{ width: 55, textAlign: 'right' }}>Untagged</span>
              <span style={{ width: 55, textAlign: 'right', color: 'var(--red)' }}>Missing</span>
              <span style={{ width: 50, textAlign: 'right' }}>% Full</span>
            </div>
            {Object.entries(data.provenance).map(([field, counts]) => (
              <div key={field} style={s.sourceRow}>
                <span style={{ flex: 1, fontFamily: 'var(--font-mono)', color: 'var(--text-0)', fontSize: 11 }}>{field}</span>
                <span style={{ width: 55, textAlign: 'right', color: 'var(--accent)', fontSize: 11 }}>{(counts['sam.gov'] || 0) + (counts['sam-api'] || 0)}</span>
                <span style={{ width: 55, textAlign: 'right', color: 'var(--gold)', fontSize: 11 }}>{counts['field-recovery'] || 0}</span>
                <span style={{ width: 55, textAlign: 'right', color: 'var(--text-2)', fontSize: 11 }}>{counts.untagged || 0}</span>
                <span style={{ width: 55, textAlign: 'right', color: counts.missing > 0 ? 'var(--red)' : 'var(--text-2)', fontSize: 11 }}>{counts.missing || 0}</span>
                <span style={{ width: 50, textAlign: 'right', fontWeight: 700, fontSize: 11, color: counts.pctPresent >= 75 ? 'var(--accent)' : counts.pctPresent >= 50 ? 'var(--gold)' : 'var(--red)' }}>{counts.pctPresent}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data Confidence Distribution */}
      {data.confidenceDistribution && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Data Confidence Distribution</div>
          <div style={s.grid}>
            <div style={s.card}><div style={s.cardLabel}>High (70%+)</div><div style={{ ...s.cardValue, color: 'var(--accent)' }}>{data.confidenceDistribution.high}</div></div>
            <div style={s.card}><div style={s.cardLabel}>Medium (40-69%)</div><div style={{ ...s.cardValue, color: 'var(--gold)' }}>{data.confidenceDistribution.medium}</div></div>
            <div style={s.card}><div style={s.cardLabel}>Low (&lt;40%)</div><div style={{ ...s.cardValue, color: 'var(--red)' }}>{data.confidenceDistribution.low}</div></div>
            <div style={s.card}><div style={s.cardLabel}>No Provenance</div><div style={{ ...s.cardValue, color: 'var(--text-2)' }}>{data.confidenceDistribution.none}</div></div>
          </div>
        </div>
      )}

      {/* Scoring Summary */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Scoring Summary</div>
        <div style={s.grid}>
          <div style={s.card}>
            <div style={s.cardLabel}>Scored</div>
            <div style={s.cardValue}>{scoring.total}</div>
            <div style={s.cardSub}>Avg: {scoring.avgScore ?? '—'}</div>
          </div>
          <div style={s.card}>
            <div style={s.cardLabel}>PURSUE</div>
            <div style={{ ...s.cardValue, color: 'var(--accent)' }}>{scoring.pursue}</div>
          </div>
          <div style={s.card}>
            <div style={s.cardLabel}>MONITOR</div>
            <div style={{ ...s.cardValue, color: 'var(--amber)' }}>{scoring.monitor}</div>
          </div>
          <div style={s.card}>
            <div style={s.cardLabel}>PASS</div>
            <div style={{ ...s.cardValue, color: 'var(--text-2)' }}>{scoring.pass}</div>
          </div>
          <div style={s.card}>
            <div style={s.cardLabel}>Needs Re-extract</div>
            <div style={{ ...s.cardValue, color: scoring.needsReextract > 0 ? 'var(--amber)' : 'var(--text-2)' }}>{scoring.needsReextract}</div>
          </div>
          <div style={s.card}>
            <div style={s.cardLabel}>Scoring Skipped</div>
            <div style={{ ...s.cardValue, color: scoring.scoringSkipped > 0 ? 'var(--amber)' : 'var(--text-2)' }}>{scoring.scoringSkipped}</div>
          </div>
        </div>
        {/* Score distribution bar */}
        {scoring.total > 0 && (
          <div style={{ marginTop: 12, display: 'flex', gap: 2, height: 24, borderRadius: 4, overflow: 'hidden' }}>
            {Object.entries(scoring.distribution).map(([range, count]) => {
              const pct = (count / scoring.total * 100);
              const colors = { '0-19': 'var(--red)', '20-39': '#d97706', '40-59': 'var(--amber)', '60-79': '#65a30d', '80-100': 'var(--accent)' };
              return pct > 0 ? (
                <div key={range} style={{ width: `${pct}%`, background: colors[range], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', fontFamily: 'var(--font-mono)', minWidth: count > 0 ? 20 : 0 }} title={`${range}: ${count}`}>
                  {pct > 8 ? count : ''}
                </div>
              ) : null;
            })}
          </div>
        )}
      </div>

      {/* Source Breakdown */}
      <div style={s.section}>
        <div style={s.sectionTitle}>By Source</div>
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', background: 'var(--bg-1)' }}>
          <div style={{ ...s.sourceRow, fontWeight: 700, background: 'var(--bg-2)' }}>
            <span style={{ flex: 1 }}>Source</span>
            <span style={{ width: 60, textAlign: 'right' }}>Total</span>
            <span style={{ width: 60, textAlign: 'right' }}>Scored</span>
            <span style={{ width: 70, textAlign: 'right' }}>Avg Score</span>
          </div>
          {Object.entries(sources).sort((a, b) => b[1].total - a[1].total).map(([name, d]) => (
            <div key={name} style={s.sourceRow}>
              <span style={{ flex: 1, fontFamily: 'var(--font-mono)', color: 'var(--text-0)' }}>{name}</span>
              <span style={{ width: 60, textAlign: 'right', color: 'var(--text-2)' }}>{d.total}</span>
              <span style={{ width: 60, textAlign: 'right', color: 'var(--text-2)' }}>{d.scored}</span>
              <span style={{ width: 70, textAlign: 'right', color: d.avgScore != null ? 'var(--text-0)' : 'var(--text-2)' }}>{d.avgScore ?? '—'}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Feed Architecture */}
      {data.feeds && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Feed Architecture</div>
          <div style={s.grid}>
            {Object.entries(data.feeds).map(([key, feed]) => (
              <div key={key} style={s.card}>
                <div style={s.cardLabel}>{feed.label}</div>
                <div style={{ ...s.cardValue, color: feed.count > 0 ? 'var(--text-0)' : 'var(--text-2)' }}>{feed.count}</div>
                <div style={{ ...s.cardSub, color: feed.status === 'active' ? 'var(--accent)' : 'var(--text-2)' }}>{feed.status}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pipeline Timing */}
      {pipeline.lastPhaseTimings && pipeline.lastPhaseTimings.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Last Pipeline Timing{pipeline.lastRunDurationSec ? ` (${pipeline.lastRunDurationSec}s total)` : ''}</div>
          <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {pipeline.lastPhaseTimings.map((p, i) => {
              const isSlow = p.durationSec > 30;
              return (
                <div key={i} style={{ ...s.card, padding: '6px 10px', minWidth: 90 }}>
                  <div style={{ ...s.cardLabel, color: isSlow ? 'var(--amber)' : 'var(--text-2)' }}>{p.name}</div>
                  <div style={{ fontSize: 14, fontFamily: 'var(--font-display)', fontWeight: 700, color: isSlow ? 'var(--amber)' : 'var(--text-0)' }}>{p.durationSec}s</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function CalibrationView({ opportunities, onNavigate }) {
  const scored = opportunities.filter(o => o.score != null);

  // High model score but user archived/passed
  const highIgnored = scored
    .filter(o => o.score >= 65 && ['archived', 'lost'].includes(o.bdStatus || 'new'))
    .sort((a, b) => b.score - a.score).slice(0, 15);

  // Low model score but user is actively pursuing
  const lowPursued = scored
    .filter(o => o.score < 45 && ['pursuing', 'partnering', 'submitted', 'won'].includes(o.bdStatus || 'new'))
    .sort((a, b) => a.score - b.score).slice(0, 15);

  // User posture label contradicts model posture
  const postureMismatches = scored.filter(o => {
    const v2 = computeScoringV2(o);
    if (!v2 || !o.userPostureLabel) return false;
    const m = v2.winPosture;
    const u = o.userPostureLabel;
    return (m === 'PRIME' && u === 'ignore') ||
           (m === 'PASS'  && u === 'primeable') ||
           (m === 'PARTNER' && u === 'primeable');
  }).slice(0, 15);

  // Low model confidence but being actively pursued
  const lowConfPursue = scored.filter(o => {
    const v2 = computeScoringV2(o);
    return v2?.confidence?.label === 'LOW' &&
           ['pursuing', 'partnering', 'submitted'].includes(o.bdStatus || 'new');
  }).sort((a, b) => b.score - a.score).slice(0, 15);

  const OppRow = ({ opp, tag, tagColor }) => {
    const v2 = computeScoringV2(opp);
    const ps = v2 ? POSTURE_STYLE[v2.winPosture] : null;
    return (
      <div
        onClick={() => onNavigate({ filter: 'all', selectId: opp.noticeId })}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: 'transparent', transition: 'background 0.1s' }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-2)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--text-0)', minWidth: 32, textAlign: 'right' }}>{opp.score}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: 'var(--text-0)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opp.title}</div>
          <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{opp.agency} · bd: {opp.bdStatus || 'new'}{opp.userFitLabel ? ` · fit: ${opp.userFitLabel}` : ''}</div>
        </div>
        {ps && <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, color: ps.color, background: ps.bg, border: `1px solid ${ps.border}`, borderRadius: 3, padding: '2px 6px', flexShrink: 0 }}>{v2.winPosture}</span>}
        <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: tagColor, background: tagColor + '22', borderRadius: 3, padding: '2px 6px', flexShrink: 0 }}>{tag}</span>
      </div>
    );
  };

  const Section = ({ title, items, tag, tagColor, emptyMsg }) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</span>
        <span style={{ fontSize: 10, color: 'var(--text-2)', background: 'var(--bg-3)', borderRadius: 10, padding: '1px 7px' }}>{items.length}</span>
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', background: 'var(--bg-1)' }}>
        {items.length === 0
          ? <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-2)', fontStyle: 'italic' }}>{emptyMsg}</div>
          : items.map(o => <OppRow key={o.noticeId} opp={o} tag={tag} tagColor={tagColor} />)
        }
      </div>
    </div>
  );

  return (
    <div style={{ padding: 24, maxWidth: 860, overflowY: 'auto', height: '100%' }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-0)', marginBottom: 4 }}>Score Calibration</div>
        <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6 }}>
          Surfaces mismatches between model predictions and BD decisions. Click any row to open the opportunity.
          Use findings to tune <code style={{ fontFamily: 'var(--font-mono)', background: 'var(--bg-3)', padding: '1px 4px', borderRadius: 3 }}>WEIGHTS</code> in <code style={{ fontFamily: 'var(--font-mono)', background: 'var(--bg-3)', padding: '1px 4px', borderRadius: 3 }}>scoringV2.js</code>.
        </div>
      </div>
      <Section title="High-Scoring — Ignored" items={highIgnored} tag="ignored" tagColor="var(--red)"
        emptyMsg="No mismatches — high-scoring opportunities are being engaged." />
      <Section title="Low-Scoring — Pursued" items={lowPursued} tag="pursuing" tagColor="var(--amber)"
        emptyMsg="No mismatches — pursued opportunities all score ≥45." />
      <Section title="Posture Feedback Contradicts Model" items={postureMismatches} tag="mismatch" tagColor="var(--blue)"
        emptyMsg="No posture mismatches. Add feedback labels in opportunity detail to populate this." />
      <Section title="Low Model Confidence — Active Pursuit" items={lowConfPursue} tag="low confidence" tagColor="var(--amber)"
        emptyMsg="No low-confidence active pursuits." />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ACCOUNTS VIEW — Account-based BD planning organized around agencies
// ─────────────────────────────────────────────────────────────────────────────
function AgencySpendPanel({ agencyName, authToken }) {
  const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    if (data || loading) { setExpanded(!expanded); return; }
    setLoading(true);
    setExpanded(true);
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const res = await fetch(`${BASE_URL}/api/spend/agency/${encodeURIComponent(agencyName)}`, { headers });
      if (res.ok) setData(await res.json());
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const fmtVal = (v) => {
    if (!v || v === 0) return '—';
    if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
    if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
    return `$${Math.round(v).toLocaleString()}`;
  };

  return (
    <div style={{ marginTop: 20, marginBottom: 0 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        Agency Spend Intelligence
        <button onClick={load} style={{ fontSize: 10, fontFamily: 'var(--font-mono)', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text-1)', borderRadius: 3, padding: '2px 8px' }}>
          {loading ? 'Loading...' : expanded ? 'Collapse' : data ? 'Expand' : 'Load from USAspending'}
        </button>
      </div>
      {expanded && data && (
        <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 6, padding: 14 }}>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-2)' }}>
            <span><span style={{ color: 'var(--text-0)', fontWeight: 700 }}>{fmtVal(data.totalSpend)}</span> total spend (2yr)</span>
            <span><span style={{ color: 'var(--text-0)', fontWeight: 700 }}>{data.awardCount}</span> awards</span>
          </div>
          {data.topContractors?.length > 0 && (
            <>
              <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Top Contractors</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6, marginBottom: 10 }}>
                {data.topContractors.slice(0, 8).map((c, i) => (
                  <div key={i} style={{ background: 'var(--bg-2)', borderRadius: 4, padding: '6px 10px', fontSize: 11 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-0)', fontSize: 11, marginBottom: 2 }}>{c.name}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-2)' }}>
                      {c.contracts} awards · {fmtVal(c.totalValue)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {data.naicsBreakdown?.length > 0 && (
            <>
              <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--amber)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>NAICS Spend Breakdown</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {data.naicsBreakdown.slice(0, 6).map((n, i) => (
                  <span key={i} style={{ fontSize: 10, fontFamily: 'var(--font-mono)', background: 'var(--bg-2)', borderRadius: 3, padding: '3px 8px', color: 'var(--text-1)' }}>
                    {n.code} · {fmtVal(n.totalValue)} ({n.contracts})
                  </span>
                ))}
              </div>
            </>
          )}
          <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 8 }}>
            Source: USAspending.gov · {new Date(data.queriedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── ACCOUNT INTELLIGENCE PANEL ──────────────────────────────────────────────
// Shows pipeline breakdown, upcoming deadlines, NAICS breakdown, and partner ecosystem
function AccountIntelPanel({ agencyName, authToken }) {
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(false);
  const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? '';

  useEffect(() => {
    if (!agencyName) return;
    setLoading(true);
    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
    fetch(`${BASE_URL}/api/accounts/${encodeURIComponent(agencyName)}/intelligence`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && !d.error) setIntel(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [agencyName, authToken, BASE_URL]);

  if (loading) return <div style={{ fontSize: 11, color: 'var(--text-2)', padding: 10 }}>Loading account intelligence...</div>;
  if (!intel) return null;

  const stageColors = { Discover: '#6b7280', Qualify: '#8b5cf6', Position: '#3b82f6', Pursue: '#10b981', Bid: 'var(--gold)', Submitted: 'var(--red)' };
  const ds = { sectionLbl: { fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, color: "var(--text-2)", letterSpacing: 1, marginBottom: 8, marginTop: 16 }, block: { background: "var(--bg-1)", border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px", marginBottom: 10 } };

  return (
    <>
      {/* Pipeline by Capture Stage */}
      <div style={ds.sectionLbl}>PIPELINE BY STAGE</div>
      <div style={ds.block}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {Object.entries(intel.pipeline || {}).filter(([, v]) => v.count > 0).map(([stage, data]) => (
            <div key={stage} style={{ background: 'var(--bg-0)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', minWidth: 70, textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: stageColors[stage] || 'var(--text-0)' }}>{data.count}</div>
              <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', marginTop: 2 }}>{stage}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming Deadlines */}
      {intel.upcomingDeadlines && intel.upcomingDeadlines.length > 0 && (
        <>
          <div style={ds.sectionLbl}>UPCOMING DEADLINES</div>
          <div style={ds.block}>
            {intel.upcomingDeadlines.slice(0, 5).map((d, i) => {
              const daysLeft = Math.ceil((new Date(d.deadline) - new Date()) / 86400000);
              return (
                <div key={d.noticeId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < Math.min(intel.upcomingDeadlines.length, 5) - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-0)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</div>
                    <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                      Stage: {d.captureStage} · Score: {d.score ?? '—'} · {d.estimatedValue || '—'}
                    </div>
                  </div>
                  <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700, color: daysLeft <= 7 ? 'var(--red)' : daysLeft <= 30 ? 'var(--amber)' : 'var(--text-1)', flexShrink: 0, marginLeft: 8 }}>{daysLeft}d</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* NAICS Breakdown */}
      {intel.naicsBreakdown && intel.naicsBreakdown.length > 0 && (
        <>
          <div style={ds.sectionLbl}>NAICS BREAKDOWN</div>
          <div style={ds.block}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {intel.naicsBreakdown.slice(0, 8).map(n => (
                <span key={n.code} style={{ fontSize: 10, fontFamily: 'var(--font-mono)', background: 'var(--bg-0)', border: '1px solid var(--border)', borderRadius: 3, padding: '2px 8px', color: 'var(--text-1)' }}>{n.code} ({n.count})</span>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Mission Breakdown */}
      {intel.missionBreakdown && intel.missionBreakdown.length > 0 && (
        <>
          <div style={ds.sectionLbl}>MISSION AREAS</div>
          <div style={ds.block}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {intel.missionBreakdown.map(m => (
                <span key={m.name} style={{ fontSize: 10, fontFamily: 'var(--font-mono)', background: 'var(--accent-dim)', border: '1px solid var(--accent)', borderRadius: 3, padding: '2px 8px', color: 'var(--accent)' }}>{m.name} ({m.count})</span>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function AccountForecastSignals({ agencyName, authToken }) {
  const [forecasts, setForecasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadForecasts = async () => {
    if (!agencyName) return;
    setLoading(true);
    try {
      const token = authToken || localStorage.getItem('bd_auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${BASE_URL}/api/forecasts/agency/${encodeURIComponent(agencyName)}`, { headers });
      if (res.ok) setForecasts(await res.json());
      setLoaded(true);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadForecasts(); }, [agencyName]); // eslint-disable-line

  if (!loaded) return null;
  if (forecasts.length === 0) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-1)', marginBottom: 8, fontFamily: 'var(--font-display)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
        Early Signals ({forecasts.length})
      </div>
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
        {loading ? (
          <div style={{ fontSize: 11, color: 'var(--text-2)' }}>Loading forecast signals...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {forecasts.slice(0, 6).map((f, i) => (
              <div key={f.forecastId || i} style={{ borderBottom: i < Math.min(forecasts.length, 6) - 1 ? '1px solid var(--border)' : 'none', paddingBottom: i < Math.min(forecasts.length, 6) - 1 ? 8 : 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-0)', lineHeight: 1.3 }}>
                    {(f.title || '').length > 70 ? f.title.substring(0, 67) + '...' : f.title}
                  </div>
                  <span style={{
                    fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, padding: '2px 6px', borderRadius: 3, flexShrink: 0,
                    background: f.signalStrength >= 70 ? 'rgba(52,211,153,0.15)' : f.signalStrength >= 50 ? 'rgba(251,191,36,0.15)' : 'rgba(148,163,184,0.1)',
                    color: f.signalStrength >= 70 ? '#34d399' : f.signalStrength >= 50 ? '#fbbf24' : 'var(--text-2)',
                  }}>{f.signalStrength}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4, alignItems: 'center' }}>
                  {f.noticeType && <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', padding: '1px 5px', borderRadius: 3, background: 'rgba(59,130,246,0.12)', color: '#3b82f6', fontWeight: 600 }}>{f.noticeType}</span>}
                  {f.anticipatedDate && <span style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{f.anticipatedDate}</span>}
                  {f.estimatedValue && <span style={{ fontSize: 10, color: 'var(--text-1)', fontWeight: 600 }}>{f.estimatedValue}</span>}
                  {f.matchedMissionAreas?.map(m => (
                    <span key={m} style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'rgba(99,102,241,0.12)', color: '#818cf8', fontFamily: 'var(--font-mono)' }}>{m}</span>
                  ))}
                </div>
                {f.sourceLink && (
                  <a href={f.sourceLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: 'var(--accent)', textDecoration: 'none', marginTop: 2, display: 'inline-block' }}>View source</a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AccountsView({ authToken, opportunities, onNavigate, isWatched, onToggleWatch }) {
  const [programs, setPrograms] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [strategyNotes, setStrategyNotes] = useState('');
  const [strategyUpdatedAt, setStrategyUpdatedAt] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [sortBy, setSortBy] = useState('pursuits');

  useEffect(() => {
    if (!authToken) return;
    fetch(`${BASE_URL}/api/programs`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setPrograms(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [authToken]);

  // Load strategy notes when account selected
  useEffect(() => {
    if (!selectedAccount || !authToken) return;
    const token = localStorage.getItem('bd_auth_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${BASE_URL}/api/accounts/${encodeURIComponent(selectedAccount.name)}/strategy`, { headers })
      .then(r => r.ok ? r.json() : { strategyNotes: '', updatedAt: null })
      .then(d => { setStrategyNotes(d.strategyNotes || ''); setStrategyUpdatedAt(d.updatedAt); })
      .catch(() => {});
  }, [selectedAccount, authToken]);

  const saveStrategyNotes = async (notes) => {
    if (!selectedAccount) return;
    setSavingNotes(true);
    try {
      const token = localStorage.getItem('bd_auth_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${BASE_URL}/api/accounts/${encodeURIComponent(selectedAccount.name)}/strategy`, {
        method: 'PUT', headers, body: JSON.stringify({ notes }),
      });
      setStrategyUpdatedAt(new Date().toISOString());
    } catch (_e) { /* non-fatal */ } finally {
      setSavingNotes(false);
    }
  };

  const formatCurrency = (v) => {
    if (!v || Number.isNaN(v)) return null;
    if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
    if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
    return `$${Math.round(v).toLocaleString()}`;
  };

  const parseIntel = (opp) => {
    if (!opp?.competitiveIntel) return null;
    try { return typeof opp.competitiveIntel === 'string' ? JSON.parse(opp.competitiveIntel) : opp.competitiveIntel; }
    catch { return null; }
  };

  const missionDefinitions = [
    { key: 'Cyber',      terms: ['cyber', 'zero trust', 'identity', 'siem', 'rmf'] },
    { key: 'Cloud',      terms: ['cloud', 'saas', 'iaas', 'paas', 'kubernetes'] },
    { key: 'AI/ML',      terms: ['artificial intelligence', 'machine learning', 'ai/ml', 'llm', 'genai'] },
    { key: 'C2',         terms: ['command and control', 'c2', 'c4isr', 'jadc2', 'mission command'] },
    { key: 'DevSecOps',  terms: ['devsecops', 'ci/cd', 'pipeline automation', 'containeriz'] },
    { key: 'Data',       terms: ['data engineering', 'data analytics', 'etl', 'data pipeline', 'data fabric'] },
    { key: 'Software',   terms: ['software development', 'application development', 'agile'] },
    { key: 'Training',   terms: ['training', 'simulation', 'wargaming'] },
  ];

  const accounts = useMemo(() => {
    const active = opportunities.filter(o => o.source !== 'usaspending-gov' && o.pipelineStage !== 'expired');
    const cutoff30d = new Date(); cutoff30d.setDate(cutoff30d.getDate() - 30);
    const map = {};

    active.forEach((opp) => {
      const agency = (opp.agency || '').trim();
      if (!agency || agency === 'Unknown') return;
      if (!map[agency]) {
        map[agency] = {
          name: agency, opportunityCount: 0, pursueCount: 0, highScoreCount: 0,
          pursuingCount: 0, submittedCount: 0, scoredCount: 0, scoreSum: 0,
          totalCompetitiveValue: 0, recentCount: 0, missionCounts: {},
          contractorMap: {}, opportunities: [],
        };
      }
      const a = map[agency];
      a.opportunityCount++;
      a.opportunities.push(opp);
      if (opp.recommendation === 'PURSUE') a.pursueCount++;
      if ((opp.score || 0) >= 80) a.highScoreCount++;
      if (['pursuing', 'partnering', 'submitted'].includes(opp.bdStatus)) a.pursuingCount++;
      if (opp.bdStatus === 'submitted') a.submittedCount++;
      if (opp.score != null) { a.scoredCount++; a.scoreSum += Number(opp.score) || 0; }
      if (opp.postedDate && new Date(opp.postedDate) >= cutoff30d) a.recentCount++;

      const text = `${opp.title || ''} ${opp.description || ''}`.toLowerCase();
      missionDefinitions.forEach(({ key, terms }) => {
        if (terms.some(t => text.includes(t))) a.missionCounts[key] = (a.missionCounts[key] || 0) + 1;
      });

      const intel = parseIntel(opp);
      (intel?.competitors || []).forEach((c) => {
        if (!c?.name) return;
        const tv = Number(c.totalValue || c.total_value || 0) || 0;
        const aw = Number(c.contracts || c.awardsCount || c.awards_count || 0) || 0;
        a.totalCompetitiveValue += tv;
        if (!a.contractorMap[c.name]) a.contractorMap[c.name] = { name: c.name, totalValue: 0, awards: 0 };
        a.contractorMap[c.name].totalValue += tv;
        a.contractorMap[c.name].awards += aw > 0 ? aw : 1;
      });
    });

    return Object.values(map).map((a) => {
      const topMissionAreas = Object.entries(a.missionCounts).map(([name, count]) => ({ name, count })).sort((x, y) => y.count - x.count);
      const topContractors = Object.values(a.contractorMap).sort((x, y) => y.totalValue - x.totalValue || y.awards - x.awards).slice(0, 8);
      const avgScore = a.scoredCount > 0 ? Math.round(a.scoreSum / a.scoredCount) : null;
      const programCount = programs.filter(p => (p.agenciesInvolved || []).some(ag => ag === a.name || (ag.name || ag) === a.name)).length;
      const trending = a.recentCount > 0 && a.opportunityCount > 0 && (a.recentCount / a.opportunityCount) > 0.4;

      const summary = `${a.name} has ${a.opportunityCount} active opportunities with ${a.pursuingCount} in active pursuit${avgScore != null ? ` and an average AI score of ${avgScore}` : ''}. ${topMissionAreas.length > 0 ? `Strongest demand in ${topMissionAreas.slice(0, 2).map(m => m.name).join(' and ')}.` : ''}`;

      return { ...a, topMissionAreas, topContractors, avgScore, programCount, trending, summary };
    }).sort((x, y) => {
      if (sortBy === 'score') return (y.avgScore || 0) - (x.avgScore || 0);
      if (sortBy === 'volume') return y.opportunityCount - x.opportunityCount;
      return y.pursuingCount - x.pursuingCount || y.opportunityCount - x.opportunityCount;
    });
  }, [opportunities, programs, sortBy]); // eslint-disable-line

  const s = {
    page: { maxWidth: 1200, width: '100%', margin: '0 auto', padding: 24, overflowY: 'auto' },
    head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 16, flexWrap: 'wrap' },
    title: { fontFamily: 'var(--font-display)', fontSize: 28, letterSpacing: 2, color: 'var(--text-0)' },
    sub: { color: 'var(--text-2)', fontSize: 12, marginTop: 4 },
    sortWrap: { display: 'flex', gap: 8, marginTop: 4 },
    sortBtn: (active) => ({ border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`, background: active ? 'var(--accent-dim)' : 'var(--bg-2)', color: active ? 'var(--accent)' : 'var(--text-1)', borderRadius: 4, padding: '5px 9px', fontSize: 11, fontFamily: 'var(--font-mono)', cursor: 'pointer' }),
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 },
    card: (active) => ({ background: 'var(--bg-1)', border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 'var(--radius)', padding: 16, cursor: 'pointer', transition: 'border-color 0.15s' }),
    cardName: { fontSize: 14, fontWeight: 700, color: 'var(--text-0)', marginBottom: 10, lineHeight: 1.3 },
    kpiMini: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 10 },
    kpiVal: { color: 'var(--text-0)', fontWeight: 700, marginLeft: 4 },
    tag: { fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 0.5, borderRadius: 3, padding: '2px 6px', border: '1px solid', display: 'inline-block' },
    cardFooter: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 4 },
  };

  if (selectedAccount) {
    // ── ACCOUNT DETAIL VIEW ──
    const acct = selectedAccount;
    const acctPrograms = programs.filter(p => (p.agenciesInvolved || []).some(ag => ag === acct.name || (ag.name || ag) === acct.name));
    const activePursuits = acct.opportunities.filter(o => ['pursuing', 'partnering', 'submitted', 'won'].includes(o.bdStatus || 'new'));
    const ds = {
      page: { maxWidth: 900, margin: '0 auto', padding: 24, overflowY: 'auto' },
      back: { fontSize: 11, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', cursor: 'pointer', marginBottom: 16, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0 },
      header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 },
      title: { fontFamily: 'var(--font-display)', fontSize: 26, letterSpacing: 1, color: 'var(--text-0)', lineHeight: 1.2 },
      subLabel: { fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 },
      kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, marginBottom: 20 },
      kpiCard: { background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 6, padding: 12 },
      kpiVal: { fontFamily: 'var(--font-display)', fontSize: 22, letterSpacing: 1, color: 'var(--text-0)' },
      kpiLbl: { fontSize: 10, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: 'var(--font-mono)' },
      sectionLbl: { fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 8, marginTop: 20 },
      block: { background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 6, padding: 14, marginBottom: 0 },
      row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' },
      rowLast: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 0' },
      chipWrap: { display: 'flex', flexWrap: 'wrap', gap: 6 },
      chip: { border: '1px solid var(--border)', borderRadius: 999, padding: '3px 10px', fontSize: 11, color: 'var(--text-1)', background: 'var(--bg-2)' },
      actionRow: { display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' },
      actionBtn: { border: '1px solid var(--accent)', background: 'var(--accent-dim)', color: 'var(--accent)', borderRadius: 4, padding: '7px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' },
    };

    return (
      <div style={{ ...ds.page, height: '100%', overflowY: 'auto' }}>
        <button style={ds.back} onClick={() => setSelectedAccount(null)}>← All Accounts</button>

        <div style={ds.header}>
          <div>
            <div style={ds.title}>{acct.name}</div>
            <div style={ds.subLabel}>Account Intelligence · {acct.opportunityCount} opportunities tracked</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {onToggleWatch && (
              <button
                style={{ fontSize: 11, fontFamily: 'var(--font-mono)', cursor: 'pointer', border: isWatched?.('agency', acct.name) ? '1px solid var(--accent)' : '1px solid var(--border)', background: isWatched?.('agency', acct.name) ? 'var(--accent-dim)' : 'var(--bg-2)', color: isWatched?.('agency', acct.name) ? 'var(--accent)' : 'var(--text-1)', borderRadius: 4, padding: '6px 12px' }}
                onClick={() => onToggleWatch('agency', acct.name, acct.name)}
              >{isWatched?.('agency', acct.name) ? 'Watching' : 'Watch'}</button>
            )}
            <button style={ds.actionBtn} onClick={() => onNavigate({ filter: 'all', agency: acct.name })}>View All Opportunities</button>
          </div>
        </div>

        {/* KPIs */}
        <div style={ds.kpiRow}>
          <div style={ds.kpiCard}>
            <div style={{ ...ds.kpiVal, color: 'var(--accent)' }}>{acct.pursuingCount}</div>
            <div style={ds.kpiLbl}>Active Pursuits</div>
          </div>
          <div style={ds.kpiCard}>
            <div style={ds.kpiVal}>{acct.programCount || acctPrograms.length}</div>
            <div style={ds.kpiLbl}>Programs</div>
          </div>
          <div style={ds.kpiCard}>
            <div style={ds.kpiVal}>{acct.avgScore ?? '—'}</div>
            <div style={ds.kpiLbl}>Avg AI Score</div>
          </div>
          <div style={ds.kpiCard}>
            <div style={{ ...ds.kpiVal, color: 'var(--blue)' }}>{formatCurrency(acct.totalCompetitiveValue) || '—'}</div>
            <div style={ds.kpiLbl}>Competitive Spend</div>
          </div>
        </div>

        {/* Account Intelligence */}
        <AccountIntelPanel agencyName={acct.name} authToken={authToken} />

        {/* Account Overview */}
        <div style={ds.sectionLbl}>Account Overview</div>
        <div style={ds.block}>
          <p style={{ fontSize: 13, color: 'var(--text-1)', lineHeight: 1.7, margin: 0 }}>{acct.summary}</p>
          {acct.topMissionAreas.length > 0 && (
            <div style={{ ...ds.chipWrap, marginTop: 12 }}>
              {acct.topMissionAreas.slice(0, 6).map(m => (
                <span key={m.name} style={ds.chip}>{m.name} ({m.count})</span>
              ))}
            </div>
          )}
        </div>

        {/* Programs */}
        {acctPrograms.length > 0 && (
          <>
            <div style={ds.sectionLbl}>Programs ({acctPrograms.length})</div>
            <div style={ds.block}>
              {acctPrograms.map((p, i) => (
                <div key={p.programName} style={i < acctPrograms.length - 1 ? ds.row : ds.rowLast}
                  onClick={() => onNavigate({ tab: 'programs', program: p.programName })}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-0)' }}>{p.programName}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                      {p.opportunityCount} opp{p.opportunityCount !== 1 ? 's' : ''} · {p.maturityStage || 'Emerging'} · {(p.missionAreas || []).slice(0, 2).map(m => m.name || m).join(', ')}
                    </div>
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>→</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Active Pursuits */}
        <div style={ds.sectionLbl}>Active Pursuits ({activePursuits.length})</div>
        <div style={ds.block}>
          {activePursuits.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--text-2)', margin: 0 }}>No active pursuits for this agency yet.</p>
          ) : (
            activePursuits.map((opp, i) => {
              const v2 = computeScoringV2(opp);
              const ps = v2 ? POSTURE_STYLE[v2.winPosture] : null;
              return (
                <div key={opp.noticeId} style={i < activePursuits.length - 1 ? ds.row : ds.rowLast}
                  onClick={() => onNavigate({ filter: 'all', selectId: opp.noticeId })}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-0)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opp.title}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>bd: {opp.bdStatus} · score: {opp.score ?? '—'}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                    {ps && <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, color: ps.color, background: ps.bg, border: `1px solid ${ps.border}`, borderRadius: 3, padding: '2px 6px' }}>{v2.winPosture}</span>}
                    <span style={{ fontSize: 10, color: 'var(--text-2)' }}>→</span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Partner Landscape */}
        {acct.topContractors.length > 0 && (
          <>
            <div style={ds.sectionLbl}>Partner Landscape</div>
            <div style={ds.block}>
              {acct.topContractors.map((c, i) => (
                <div key={c.name} style={i < acct.topContractors.length - 1 ? ds.row : ds.rowLast}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-0)' }}>{c.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{c.awards} award{c.awards !== 1 ? 's' : ''} detected</div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>{formatCurrency(c.totalValue) || '—'}</div>
                </div>
              ))}
            </div>
            <PartnerContactSignals agencyContext={acct} />
          </>
        )}

        {/* Ecosystem Summary */}
        <EcosystemSummary account={acct} />

        {/* Connected Programs & Contractors */}
        <GraphSignals entityType="agency" entityId={acct.name} title="Connected Programs & Contractors" />

        {/* Key Contact Signals */}
        <div style={ds.sectionLbl}>Key Contact Signals</div>
        <AgencyContactSignals agency={acct} />

        {/* Agency Spend Intelligence */}
        <AgencySpendPanel agencyName={acct.name} authToken={authToken} />

        {/* Early Signals / Forecasts for this agency */}
        <AccountForecastSignals agencyName={acct.name} authToken={authToken} />

        {/* BD Strategy Notes */}
        <div style={ds.sectionLbl}>BD Strategy Notes</div>
        <div style={{ ...ds.block, padding: 0, overflow: 'hidden' }}>
          <textarea
            value={strategyNotes}
            onChange={e => setStrategyNotes(e.target.value)}
            onBlur={e => saveStrategyNotes(e.target.value)}
            placeholder={`Capture your team's BD strategy for ${acct.name}...\n\nExample: Key relationships to develop, teaming strategy, upcoming events, positioning notes, priority programs.`}
            style={{ width: '100%', minHeight: 140, background: 'var(--bg-1)', border: 'none', color: 'var(--text-0)', padding: '14px 16px', fontSize: 12, fontFamily: 'var(--font-body)', resize: 'vertical', lineHeight: 1.6, outline: 'none', boxSizing: 'border-box' }}
          />
          <div style={{ padding: '6px 14px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
              {savingNotes ? 'Saving...' : strategyUpdatedAt ? `Saved ${new Date(strategyUpdatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : 'Auto-saves on blur'}
            </span>
            <button style={{ fontSize: 10, fontFamily: 'var(--font-mono)', cursor: 'pointer', border: '1px solid var(--accent)', background: 'var(--accent-dim)', color: 'var(--accent)', borderRadius: 3, padding: '3px 10px' }}
              onClick={() => saveStrategyNotes(strategyNotes)}
            >Save</button>
          </div>
        </div>
      </div>
    );
  }

  // ── CARD GRID VIEW ──
  return (
    <div style={s.page}>
      <div style={s.head}>
        <div>
          <div style={s.title}>ACCOUNTS</div>
          <div style={s.sub}>Account-based BD planning across agencies, programs, and competitive landscape.</div>
        </div>
        <div style={s.sortWrap}>
          <button style={s.sortBtn(sortBy === 'pursuits')} onClick={() => setSortBy('pursuits')}>By Pursuits</button>
          <button style={s.sortBtn(sortBy === 'volume')}  onClick={() => setSortBy('volume')}>By Volume</button>
          <button style={s.sortBtn(sortBy === 'score')}   onClick={() => setSortBy('score')}>By Score</button>
        </div>
      </div>

      {accounts.length === 0 ? (
        <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 32, textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>
          No agency data yet. Run the pipeline to populate opportunities.
        </div>
      ) : (
        <div style={s.grid}>
          {accounts.map((a) => (
            <div key={a.name}
              style={s.card(false)}
              onClick={() => setSelectedAccount(a)}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={s.cardName}>{a.name}</div>
                {a.trending && (
                  <span style={{ ...s.tag, color: 'var(--accent)', borderColor: 'var(--accent)', background: 'var(--accent-dim)', flexShrink: 0, marginLeft: 6 }}>↑ Active</span>
                )}
              </div>
              <div style={s.kpiMini}>
                <div>Programs<span style={s.kpiVal}>{a.programCount}</span></div>
                <div>Pursuits<span style={{ ...s.kpiVal, color: a.pursuingCount > 0 ? 'var(--accent)' : 'var(--text-0)' }}>{a.pursuingCount}</span></div>
                <div>Avg Score<span style={s.kpiVal}>{a.avgScore ?? '—'}</span></div>
                <div>Opps<span style={s.kpiVal}>{a.opportunityCount}</span></div>
              </div>
              {a.totalCompetitiveValue > 0 && (
                <div style={{ fontSize: 11, color: 'var(--blue)', fontFamily: 'var(--font-mono)', marginBottom: 6 }}>
                  {formatCurrency(a.totalCompetitiveValue)} competitive spend detected
                </div>
              )}
              <div style={s.cardFooter}>
                {a.topMissionAreas.slice(0, 3).map(m => (
                  <span key={m.name} style={{ fontSize: 9, color: 'var(--text-2)', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 3, padding: '2px 6px', fontFamily: 'var(--font-mono)' }}>{m.name}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WatchlistView({ authToken, watchlist, onUnwatch, opportunities, onNavigate }) {
  const [programs, setPrograms] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!authToken) return;
    fetch(`${BASE_URL}/api/programs`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setPrograms(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [authToken]);

  const displayed = filter === 'all' ? watchlist : watchlist.filter(w => w.entityType === filter);

  const handleClick = (item) => {
    switch (item.entityType) {
      case 'agency':       onNavigate({ tab: 'agencies', agency: item.entityId }); break;
      case 'program':      onNavigate({ tab: 'programs', program: item.entityId }); break;
      case 'opportunity':  onNavigate({ filter: 'all', selectId: item.entityId }); break;
      default:             onNavigate({ filter: 'all', search: item.entityName });
    }
  };

  const s = {
    page: { maxWidth: 1100, width: '100%', margin: '0 auto', padding: 24 },
    head: { marginBottom: 20 },
    title: { fontFamily: 'var(--font-display)', fontSize: 28, letterSpacing: 2, color: 'var(--text-0)' },
    sub: { color: 'var(--text-2)', fontSize: 12, marginTop: 4 },
    filters: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' },
    filterBtn: (active) => ({
      border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
      background: active ? 'var(--accent-dim)' : 'var(--bg-2)',
      color: active ? 'var(--accent)' : 'var(--text-1)',
      borderRadius: 4, padding: '5px 10px', fontSize: 11, fontFamily: 'var(--font-mono)', cursor: 'pointer',
    }),
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12, marginTop: 16 },
    card: { background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14, display: 'flex', flexDirection: 'column', gap: 8, cursor: 'pointer', transition: 'border-color 0.15s' },
    typeTag: (type) => ({
      fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 0.5,
      color: TYPE_COLOR[type] || 'var(--text-2)', textTransform: 'uppercase',
    }),
    name: { fontSize: 13, fontWeight: 600, color: 'var(--text-0)', lineHeight: 1.3 },
    signal: { fontSize: 11, color: 'var(--text-2)', lineHeight: 1.5 },
    footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 },
    unwatch: { fontSize: 10, color: 'var(--text-2)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-mono)', padding: '2px 0' },
  };

  if (watchlist.length === 0) {
    return (
      <div style={s.page}>
        <div style={s.head}>
          <div style={s.title}>WATCHLIST</div>
          <div style={s.sub}>Track agencies, programs, contractors, and opportunities that matter to you.</div>
        </div>
        <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 32, textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>
          Nothing on your watchlist yet. Click Watch on any agency, program, contractor, or opportunity to start tracking.
        </div>
      </div>
    );
  }

  const typeCounts = Object.fromEntries(['agency','program','mission_area','contractor','opportunity'].map(t => [t, watchlist.filter(w => w.entityType === t).length]));

  return (
    <div style={s.page}>
      <div style={s.head}>
        <div style={s.title}>WATCHLIST</div>
        <div style={s.sub}>{watchlist.length} watched item{watchlist.length !== 1 ? 's' : ''} · signals refresh with pipeline data</div>
        <div style={s.filters}>
          <button style={s.filterBtn(filter === 'all')} onClick={() => setFilter('all')}>All ({watchlist.length})</button>
          {Object.entries(typeCounts).filter(([, n]) => n > 0).map(([type, n]) => (
            <button key={type} style={s.filterBtn(filter === type)} onClick={() => setFilter(type)}>
              {TYPE_LABEL[type]} ({n})
            </button>
          ))}
        </div>
      </div>

      <div style={s.grid}>
        {displayed.map(item => {
          const { signal, badge } = computeWatchSignals(item, opportunities, programs);
          return (
            <div key={item.id} style={s.card} onClick={() => handleClick(item)}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <span style={s.typeTag(item.entityType)}>{TYPE_LABEL[item.entityType] || item.entityType}</span>
                {badge && (
                  <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, color: badge.color, background: `${badge.color}20`, border: `1px solid ${badge.color}`, borderRadius: 3, padding: '2px 6px' }}>{badge.label}</span>
                )}
              </div>
              <div style={s.name}>{item.entityName}</div>
              <div style={s.signal}>{signal}</div>
              <div style={s.footer}>
                <span style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
                  Added {new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                <button style={s.unwatch} onClick={e => { e.stopPropagation(); onUnwatch(item); }}>
                  Unwatch
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AGENCIES VIEW
// Agency account intelligence built from opportunity + competitive intel data
// ─────────────────────────────────────────────────────────────────────────────

function MarketPulseView({ authToken, onSelectOpportunity }) {
  const BASE = process.env.REACT_APP_BACKEND_URL ?? "";
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const res = await fetch(`${BASE}/api/market-pulse`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPulse(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authToken, BASE]);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) headers.Authorization = `Bearer ${authToken}`;
      await fetch(`${BASE}/api/market-pulse`, { method: 'POST', headers });
      // Generation runs async server-side; poll for the result.
      setTimeout(() => { load(); setRefreshing(false); }, 30000);
    } catch (err) {
      setRefreshing(false);
      setError(err.message);
    }
  };

  const categoryStyle = (cat) => {
    if (cat === 'opportunity-update') return { background: 'var(--accent-dim)', color: 'var(--accent)' };
    if (cat === 'agency-news') return { background: 'var(--gold-dim)', color: 'var(--gold)' };
    if (cat === 'industry-trend') return { background: 'var(--bg-3)', color: 'var(--text-1)' };
    return { background: 'var(--bg-3)', color: 'var(--text-2)' };
  };
  const categoryLabel = (cat) => ({
    'opportunity-update': 'Opportunity update',
    'agency-news': 'Agency news',
    'industry-trend': 'Industry trend',
  }[cat] || 'Item');

  return (
    <div className="center-panel" style={{ overflow: 'auto', padding: '28px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 8 }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--text-0)', margin: 0 }}>
            Market Pulse
          </h1>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary"
            style={{ fontSize: 12 }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh now'}
          </button>
        </div>
        <div style={{ color: 'var(--text-1)', fontSize: 13, marginBottom: 24 }}>
          What defense industry publications are saying about your space — updated every pipeline cycle.
          {pulse?.generatedAt && (
            <span style={{ marginLeft: 8, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
              · last updated {new Date(pulse.generatedAt).toLocaleString()}
            </span>
          )}
        </div>

        {loading && <div style={{ padding: 24, color: 'var(--text-1)' }}>Loading…</div>}

        {!loading && error && (
          <div style={{ padding: 16, background: 'var(--red-dim)', color: 'var(--red)', borderRadius: 6, fontSize: 13 }}>
            Could not load Market Pulse: {error}
          </div>
        )}

        {!loading && !error && pulse && pulse.available === false && (
          <div style={{ padding: 24, background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 8 }}>
            <div style={{ color: 'var(--text-0)', fontWeight: 600, marginBottom: 6 }}>No pulse generated yet</div>
            <div style={{ color: 'var(--text-1)', fontSize: 13 }}>
              Hit "Refresh now" to generate the first one, or wait for the next pipeline tick (every 6 hours).
            </div>
          </div>
        )}

        {!loading && !error && pulse && pulse.summary && (
          <div style={{
            padding: 18,
            background: 'var(--bg-2)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            marginBottom: 20,
            color: 'var(--text-0)',
            fontSize: 14,
            lineHeight: 1.55,
          }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 6 }}>This week</div>
            {pulse.summary}
          </div>
        )}

        {!loading && !error && pulse?.items?.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pulse.items.map((item, idx) => (
              <div key={idx} style={{
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 18,
                background: 'var(--bg-1)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-2)' }}>{item.source || '—'}</span>
                  <span style={{ ...categoryStyle(item.category), padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {categoryLabel(item.category)}
                  </span>
                </div>
                <div style={{ fontWeight: 600, color: 'var(--text-0)', fontSize: 15, marginBottom: 6, lineHeight: 1.35 }}>
                  {item.url ? (
                    <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                      {item.title}
                    </a>
                  ) : item.title}
                </div>
                {item.summary && (
                  <div style={{ color: 'var(--text-1)', fontSize: 13, marginBottom: 8, lineHeight: 1.5 }}>
                    {item.summary}
                  </div>
                )}
                {item.whyItMatters && (
                  <div style={{
                    fontSize: 12,
                    color: 'var(--accent)',
                    background: 'var(--accent-dim)',
                    padding: '8px 10px',
                    borderRadius: 4,
                    borderLeft: '3px solid var(--accent)',
                    marginBottom: 8,
                  }}>
                    <strong style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Why it matters: </strong>
                    {item.whyItMatters}
                  </div>
                )}
                {Array.isArray(item.linkedOpportunityIds) && item.linkedOpportunityIds.length > 0 && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 4 }}>
                      You have these in your DB:
                    </div>
                    {item.linkedOpportunityIds.map((opp, oIdx) => (
                      <div
                        key={oIdx}
                        onClick={() => onSelectOpportunity && onSelectOpportunity(opp.noticeId)}
                        style={{
                          cursor: 'pointer',
                          fontSize: 12,
                          color: 'var(--accent)',
                          padding: '4px 0',
                          textDecoration: 'underline',
                        }}
                      >
                        → {opp.title} {opp.score != null && <span style={{ color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>({opp.score} · {opp.recommendation})</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


export default function App() {
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('bd_auth_token'));
  const [userEmail, setUserEmail] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(Boolean(authToken));
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const activePersonality = AI_PERSONALITIES[0]; // Single personality — no tabs needed
  const [selectedRow, setSelectedRow] = useState(null);
  useEffect(() => {
    if (!selectedRow?._summary || !selectedRow.noticeId || !authToken) return undefined;
    let active = true;
    const noticeId = selectedRow.noticeId;
    fetch(`${BASE_URL}/api/opportunities/${encodeURIComponent(noticeId)}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(res => res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))
      .then(detail => {
        if (!active) return;
        setSelectedRow(current => current?.noticeId === noticeId ? detail : current);
      })
      .catch(err => console.error('[App] Failed to load opportunity detail:', err));
    return () => { active = false; };
  }, [selectedRow?._summary, selectedRow?.noticeId, authToken]);
  // Default sort: by Response Date ascending (soonest deadlines first). The
  // sort comparator treats no-deadline records as max-date, so they sink to
  // the bottom — what a BD pro wants by default. User can still click any
  // column to re-sort.
  const [sortKey, setSortKey] = useState("due_date");
  const [sortDir, setSortDir] = useState("asc");
  const [filter, setFilter] = useState("all"); // all | pursue | monitor | high
  const [scoreFilter, setScoreFilter] = useState(0); // min AI score slider — default to showing all
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [agencyFilter, setAgencyFilter] = useState("all");
  const [naicsFilter, setNaicsFilter] = useState("all");
  // viewMode default was "analytics" until that view was removed in the bulk
  // delete — left every fresh load with a blank main-content area because no
  // {viewMode === ...} block matched. Default to opportunities (the dashboard).
  const [viewMode, setViewMode] = useState("opportunities"); // opportunities | pursuits | intelligence | admin
  // intelligenceTab removed — Intelligence is now a single Market Pulse view.
  const [adminTab, setAdminTab] = useState("diagnostics"); // diagnostics | calibration | accounts
  const [pursuitsTab, setPursuitsTab] = useState("board"); // board | watchlist
  const [showOutbox, setShowOutbox] = useState(false);
  // BD team panel: holds the opp record being drafted (null = panel closed)
  const [bdTeamOpenFor, setBdTeamOpenFor] = useState(null);
  const [savedSearches, setSavedSearches] = useState([]);
  const [showSavedMenu, setShowSavedMenu] = useState(false);
  const [watchlist, setWatchlist] = useState([]); // { id, entityType, entityId, entityName, createdAt }
  const dashboardReady = Boolean(authToken && organization && onboarding?.completed);

  // Cross-view navigation called from cards / panels. Intelligence sub-tabs
  // (agencies, programs, research, news) collapsed into a single Market Pulse
  // view and are no longer routed individually.
  const handleNavigate = ({ filter: f, agency, search, selectId, tab } = {}) => {
    if (tab === 'watchlist') {
      setViewMode('pursuits');
      setPursuitsTab('watchlist');
      return;
    }
    if (tab === 'diagnostics' || tab === 'calibration' || tab === 'accounts') {
      setViewMode('admin');
      setAdminTab(tab);
      return;
    }
    if (tab) { setViewMode(tab); return; }
    setViewMode("opportunities");
    if (f != null) setFilter(f);
    if (agency != null) setAgencyFilter(agency);
    if (search != null) setSearchQuery(search);
    if (selectId != null) {
      const opp = opportunities.find(o => o.id === selectId || o.noticeId === selectId);
      if (opp) setSelectedRow(opp);
    }
  };
  const [pendingActionsCount, setPendingActionsCount] = useState(0);
  const [rescoring, setRescoring] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(false);

  // ── Watchlist ────────────────────────────────────────────────────────────────
  const loadWatchlist = async () => {
    if (!dashboardReady) return;
    try {
      const res = await fetch(`${BASE_URL}/api/watchlist`, { headers: { Authorization: `Bearer ${authToken}` } });
      if (res.ok) setWatchlist(await res.json());
    } catch { /* silent */ }
  };

  useEffect(() => { loadWatchlist(); }, [authToken, dashboardReady]); // eslint-disable-line

  // ── Saved searches ──────────────────────────────────────────────────────────
  const loadSavedSearches = async () => {
    if (!dashboardReady) return;
    try {
      const res = await fetch(`${BASE_URL}/api/saved-searches`, { headers: { Authorization: `Bearer ${authToken}` } });
      if (res.ok) { const data = await res.json(); setSavedSearches(data.items || []); }
    } catch { /* silent */ }
  };

  useEffect(() => { loadSavedSearches(); }, [authToken, dashboardReady]); // eslint-disable-line

  const applySavedSearch = (item) => {
    const f = item.filter || {};
    if (f.filter !== undefined) setFilter(f.filter);
    if (f.scoreFilter !== undefined) setScoreFilter(f.scoreFilter);
    if (f.searchQuery !== undefined) setSearchQuery(f.searchQuery);
    if (f.sourceFilter !== undefined) setSourceFilter(f.sourceFilter);
    if (f.agencyFilter !== undefined) setAgencyFilter(f.agencyFilter);
    if (f.naicsFilter !== undefined) setNaicsFilter(f.naicsFilter);
    if (f.sortKey !== undefined) setSortKey(f.sortKey);
    if (f.sortDir !== undefined) setSortDir(f.sortDir);
    setShowSavedMenu(false);
  };

  const saveCurrentFilter = async () => {
    const name = window.prompt('Name this saved search:', '');
    if (!name || !name.trim()) return;
    try {
      const res = await fetch(`${BASE_URL}/api/saved-searches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          name: name.trim(),
          filter: { filter, scoreFilter, searchQuery, sourceFilter, agencyFilter, naicsFilter, sortKey, sortDir },
        }),
      });
      if (res.ok) await loadSavedSearches();
    } catch { /* silent */ }
  };

  const deleteSavedSearch = async (id) => {
    try {
      await fetch(`${BASE_URL}/api/saved-searches/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      await loadSavedSearches();
    } catch { /* silent */ }
  };

  const isWatched = (entityType, entityId) => watchlist.some(w => w.entityType === entityType && w.entityId === entityId);

  const toggleWatch = async (entityType, entityId, entityName) => {
    const existing = watchlist.find(w => w.entityType === entityType && w.entityId === entityId);
    if (existing) {
      try {
        await fetch(`${BASE_URL}/api/watchlist/${existing.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } });
        setWatchlist(prev => prev.filter(w => w.id !== existing.id));
      } catch { /* silent */ }
    } else {
      try {
        await fetch(`${BASE_URL}/api/watchlist`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ entityType, entityId, entityName }),
        });
        await loadWatchlist();
      } catch { /* silent */ }
    }
  };

  // Logout — also called automatically when API returns 401 so the user
  // isn't stuck on a broken dashboard with a stale token.
  const handleLogout = useCallback(() => {
    localStorage.removeItem('bd_auth_token');
    setAuthToken(null);
    setUserEmail(null);
    setUserRole(null);
    setOrganization(null);
    setOnboarding(null);
    setSessionLoading(false);
  }, []);

  const loadSession = useCallback(async () => {
    if (!authToken) return;
    setSessionLoading(true);
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      const [meResponse, orgResponse] = await Promise.all([
        fetch(`${BASE_URL}/api/auth/me`, { headers }),
        fetch(`${BASE_URL}/api/organization`, { headers }),
      ]);
      if (meResponse.status === 401) return handleLogout();
      const me = await meResponse.json();
      const org = orgResponse.ok ? await orgResponse.json() : {};
      setUserEmail(me.user?.email || null);
      setUserRole(me.user?.role || null);
      setOrganization(org.organization || me.user?.organization || null);
      setOnboarding(org.onboarding || me.user?.organization?.onboarding || null);
    } catch {
      // Keep the authenticated shell available during a transient network
      // failure; API calls still enforce the session server-side.
    } finally {
      setSessionLoading(false);
    }
  }, [authToken, handleLogout]);

  useEffect(() => { loadSession(); }, [loadSession]);

  // ✅ ALWAYS call hooks - move BEFORE early return
  // Pass authToken so hook only fetches when authenticated. When user clicks
  // the Dismissed filter, we ask the API to include archived/lost records
  // (otherwise they're filtered out server-side by default).
  const extraOppParams = filter === 'dismissed' ? 'includeDismissed=true' : '';
  const dashboardToken = dashboardReady ? authToken : null;
  const { opportunities, activity, agentStatus, stats, loading, error, triggerRun, refresh, triggering } = useAgentData(activePersonality, dashboardToken, handleLogout, extraOppParams);

  // Fetch pending actions count periodically
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const token = localStorage.getItem('bd_auth_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${BASE_URL}/api/agent/outbox`, { headers });
        const data = await res.json();
        setPendingActionsCount(data.count || 0);
      } catch (err) {
        console.error('Failed to fetch pending count:', err);
      }
    };

    if (dashboardReady) {
      fetchPendingCount();
      const interval = setInterval(fetchPendingCount, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
  }, [dashboardReady]);

  // ── RESCORE HANDLER ──
  // "Rescore All" re-runs Claude on every scored record. With ~2,400 records
  // and two-tier scoring averaging ~$0.025/record, one click ≈ $60 in
  // Anthropic spend. The button is admin-only in the UI and the endpoint is
  // admin-only on the backend; this confirm exists to stop the admin from
  // accidentally clicking it too.
  const handleRescore = async () => {
    if (rescoring) return;
    const scoredCount = opportunities.filter(o => o.score !== null && o.score !== undefined).length;
    const estCost = (scoredCount * 0.025).toFixed(2);
    const ok = window.confirm(
      `Rescore ALL ${scoredCount} scored opportunities?\n\n` +
      `This re-runs Claude on every record and will cost roughly $${estCost} in Anthropic credits.\n\n` +
      `Use the Admin → Diagnostics tab for cheaper targeted rescores.\n\n` +
      `Continue?`
    );
    if (!ok) return;
    setRescoring(true);
    try {
      const token = localStorage.getItem('bd_auth_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${BASE_URL}/api/rescore`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ mode: 'all' })
      });
      const data = await res.json();
      alert(data.message || data.error || 'Re-score started. Check activity feed for progress.');
    } catch (err) {
      alert('Failed to start re-score: ' + err.message);
    } finally {
      setRescoring(false);
    }
  };

  // ── UPDATE OPPORTUNITY STATUS/NOTES ──
  const handleUpdateOpportunity = async (noticeId, updates) => {
    try {
      const token = localStorage.getItem('bd_auth_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${BASE_URL}/api/opportunities/${encodeURIComponent(noticeId)}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(updates)
      });
      if (!res.ok) throw new Error('Failed to update');
      // Update selected row immediately and refresh full list
      if (selectedRow && selectedRow.noticeId === noticeId) {
        setSelectedRow(prev => ({ ...prev, ...updates }));
      }
      refresh();
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  // ── NEXT RUN DISPLAY ──
  const nextRunDisplay = (() => {
    const nextCrawl = agentStatus?.nextCrawl;
    if (!nextCrawl) return null;
    const next = new Date(nextCrawl);
    if (isNaN(next)) return nextCrawl; // fallback to raw string
    const now = new Date();
    const diffMs = next - now;
    if (diffMs <= 0) return 'Soon';
    const diffH = Math.floor(diffMs / 3600000);
    const diffM = Math.floor((diffMs % 3600000) / 60000);
    const timeStr = next.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (diffH > 0) return `${timeStr} (~${diffH}h ${diffM}m)`;
    return `${timeStr} (~${diffM}m)`;
  })();

  // ── SORT & FILTER ── (Must be BEFORE early return to avoid React Error #310)
  const filteredAndSorted = useMemo(() => {
    // Hide two kinds of records from the default opportunities view:
    //   1. usaspending-gov — historical award data, not actionable opportunities
    //   2. govcon-intel:* — industry news articles (Breaking Defense, GovCon
    //      Wire, Hatch Report, C4ISRNET, etc). These are useful intel but they
    //      are NOT contract opportunities — "View on SAM.gov" on a news-sourced
    //      record opens a YouTube video or article, which makes the dashboard
    //      feel like a news feed instead of a BD tool.
    // The news sources are still reachable: when the user explicitly switches
    // the source dropdown to a news category, the exclusion is bypassed below.
    const showNewsSources = ['intel', 'hatch', 'govcon', 'defense'].includes(sourceFilter);
    let data = opportunities.filter(o => {
      if (o.source === 'usaspending-gov') return false;
      if (!showNewsSources) {
        const src = (o.source || '').toLowerCase();
        if (src.includes('govcon-intel')) return false;
      }
      return true;
    });

    // Apply category filter
    if (filter === "pursue") data = data.filter(o => o.recommendation === "PURSUE");
    if (filter === "monitor") data = data.filter(o => o.recommendation === "MONITOR");
    if (filter === "high") data = data.filter(o => (o.score || 0) >= 70);
    if (filter === "dismissed") {
      // Show records the user has explicitly dismissed (or marked lost). The
      // default API call filters these out — toggle with ?includeDismissed=true.
      data = data.filter(o => o.bdStatus === 'archived' || o.bdStatus === 'lost');
    }
    if (filter === "needs_review") data = data.filter(o => {
      const eq = getExtractionQuality(o);
      return eq.score < 50 || !o.responseDeadline || (!o.agency || o.agency === 'Unknown') || (!o.estimatedValue || o.estimatedValue === 'Unknown');
    });
    if (filter === "whats_new") {
      const threeDaysAgo = new Date(); threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      data = data.filter(o => {
        const found = o.foundAt || o.createdAt;
        return found && new Date(found) >= threeDaysAgo;
      });
      data.sort((a, b) => new Date(b.foundAt || b.createdAt || 0) - new Date(a.foundAt || a.createdAt || 0));
    }

    // Apply score slider filter — when active, only show scored items meeting the threshold
    if (scoreFilter > 0) {
      data = data.filter(o => o.score != null && o.score >= scoreFilter);
    }

    // Apply search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(o =>
        (o.title || '').toLowerCase().includes(q) ||
        (o.agency || '').toLowerCase().includes(q) ||
        (o.description || '').toLowerCase().includes(q) ||
        (o.noticeId || '').toLowerCase().includes(q)
      );
    }

    // Apply source filter
    if (sourceFilter !== "all") {
      data = data.filter(o => {
        const src = (o.source || '').toLowerCase();
        if (sourceFilter === "sam") return !src.includes('govcon-intel');
        if (sourceFilter === "hatch") return src.includes('hatch');
        if (sourceFilter === "govcon") return src.includes('govcon-wire') || src.includes('govconwire');
        if (sourceFilter === "defense") return src.includes('c4isr') || src.includes('breaking-defense') || src.includes('defense-one');
        if (sourceFilter === "intel") return src.includes('govcon-intel');
        return true;
      });
    }

    // Apply agency filter
    if (agencyFilter !== "all") {
      data = data.filter(o => (o.agency || '').toLowerCase().includes(agencyFilter.toLowerCase()));
    }

    // Apply NAICS filter
    if (naicsFilter !== "all") {
      data = data.filter(o => o.naics === naicsFilter);
    }

    // Sort - map column keys to actual API fields for sorting
    const sortFieldMap = { 'ai_score': 'score', 'contract_value': 'estimatedValue', 'due_date': 'responseDeadline', 'posted_date': 'postedDate', 'freshness': 'postedDate', 'name': 'title', 'source_badge': 'source', 'capture_risk': '_captureRisk', 'action_type': '_actionType' };
    const actualSortKey = sortFieldMap[sortKey] || sortKey;

    data.sort((a, b) => {
      let valA = a[actualSortKey];
      let valB = b[actualSortKey];
      // Numeric sort for score
      if (actualSortKey === "score") { valA = Number(valA) || 0; valB = Number(valB) || 0; }
      if (actualSortKey === "_actionType") { valA = a.captureIntelligence?.actionType || ''; valB = b.captureIntelligence?.actionType || ''; }
      // Risk sort - compute risk score on the fly
      if (actualSortKey === "_captureRisk") { valA = getCaptureRisk(a).score; valB = getCaptureRisk(b).score; }
      // Date sort
      if (actualSortKey === "responseDeadline" || actualSortKey === "postedDate") { valA = new Date(valA || "9999").getTime(); valB = new Date(valB || "9999").getTime(); }
      // String sort
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return data;
  }, [opportunities, filter, scoreFilter, searchQuery, sourceFilter, agencyFilter, naicsFilter, sortKey, sortDir]);

  // Derive unique agencies for filter dropdown
  const uniqueAgencies = useMemo(() => {
    const agencies = opportunities
      .map(o => o.agency)
      .filter(Boolean)
      .reduce((acc, a) => {
        const key = a.toLowerCase();
        if (!acc.seen.has(key)) { acc.seen.add(key); acc.list.push(a); }
        return acc;
      }, { seen: new Set(), list: [] })
      .list
      .sort();
    return agencies;
  }, [opportunities]);

  // Derive unique NAICS codes for filter dropdown
  const uniqueNaics = useMemo(() => {
    return [...new Set(opportunities.map(o => o.naics).filter(Boolean))].sort();
  }, [opportunities]);

  // ── Pursuit opportunities (BD status: reviewing, pursuing, partnering, submitted) ──
  const pursuitOpps = useMemo(() => {
    return opportunities
      .filter(o => o.source !== 'usaspending-gov')
      .filter(o => ['reviewing', 'pursuing', 'partnering', 'submitted'].includes(o.bdStatus))
      .sort((a, b) => (b.score || 0) - (a.score || 0));
  }, [opportunities]);

  // Handle login
  const handleLogin = (token) => {
    localStorage.setItem('bd_auth_token', token);
    setAuthToken(token);
  };

  // Show login screen if not authenticated (AFTER all hooks)
  if (!authToken) {
    return <Login onLogin={handleLogin} />;
  }

  if (sessionLoading) {
    return (
      <>
        <style>{css}</style>
        <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg-0)', color: 'var(--text-2)' }}>Preparing your workspace…</div>
      </>
    );
  }

  if (!organization || !onboarding?.completed) {
    return (
      <>
        <style>{css}</style>
        <Onboarding
          authToken={authToken}
          initialOrganization={organization}
          initialOnboarding={onboarding}
          onComplete={loadSession}
          onLogout={handleLogout}
        />
      </>
    );
  }

  // ── SORT HANDLER ──
  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "ai_score" ? "desc" : "asc");
    }
  }

  // ── RENDER CELL ──
  function renderCell(col, row) {
    // Map frontend column keys to actual API field names
    const fieldMapping = {
      'name': 'title',
      'ai_score': 'score',
      'tier': 'recommendation',
      'recommendation': 'recommendation',
      'agency': 'agency',
      'contract_value': 'estimatedValue',
      'posted_date': 'postedDate',
      'due_date': 'responseDeadline',
      'attachments': 'attachments',
      'freshness': 'postedDate',
      'source_badge': 'source',
      'capture_stage': 'captureStage',
      'action_type': 'captureIntelligence'
    };

    const fieldName = fieldMapping[col.key] || col.key;
    let val = row[fieldName];
    if (col.key === 'action_type') val = row.captureIntelligence?.actionType;

    // Special handling for tier - derive from score
    if (col.key === 'tier' && row.score !== undefined) {
      if (row.score >= 70) val = 'High';
      else if (row.score >= 40) val = 'Medium';
      else val = 'Low';
    }

    // Special handling for attachments count
    if (col.key === 'attachments' && Array.isArray(row.attachments)) {
      val = row.attachments.length;
    }

    // Special handling for capture stage
    if (col.key === 'capture_stage') {
      const stage = val || 'Discover';
      const stageColors = { Discover: '#6b7280', Qualify: '#8b5cf6', Position: '#3b82f6', Pursue: '#10b981', Bid: 'var(--gold)', Submitted: 'var(--red)' };
      return <span className="badge" style={{ background: stageColors[stage] || '#6b7280', color: '#fff', fontSize: 10 }}>{stage}</span>;
    }

    // Special handling for capture risk
    if (col.key === 'capture_risk') {
      return <RiskBadge opp={row} compact />;
    }

    if (col.key === 'action_type') {
      const action = val || 'TRACK';
      const colors = { BID:'#22c55e', RESPOND:'#3b82f6', ATTEND:'#a78bfa', TEAM:'var(--gold)', TRACK:'#6b7280', PASS:'var(--red)' };
      return <span className="badge" style={{background:colors[action] || '#6b7280',color:'#fff',fontSize:9}}>{action}</span>;
    }

    // Special handling for freshness - show "Xd" badge
    if (col.key === 'freshness') {
      if (row.postedDate) {
        const daysSince = Math.floor((new Date() - new Date(row.postedDate)) / 86400000);
        const cls = daysSince <= 3 ? 'fresh-new' : daysSince <= 7 ? 'fresh-week' : daysSince <= 14 ? 'fresh-old' : 'fresh-stale';
        const label = daysSince <= 0 ? 'New' : `${daysSince}d`;
        return <span className={`badge ${cls}`}>{label}</span>;
      }
      return <span style={{color: 'var(--text-2)', fontSize: '11px'}}>—</span>;
    }

    // Special handling for name - show with hover actions + extraction quality dot
    if (col.key === 'name') {
      const eq = getExtractionQuality(row);
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {eq.score < 50 && <span title={`Low extraction quality (${eq.score}%) — missing: ${eq.gaps.join(', ')}`} style={{ width: 6, height: 6, borderRadius: '50%', background: eq.color, flexShrink: 0, marginRight: 6, opacity: 0.8 }} />}
          <span style={{ flex: 1, textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.15)', textUnderlineOffset: '3px', cursor: 'pointer' }}>{val || "—"}</span>
          <span className="row-actions">
            {row.link && <a href={row.link} target="_blank" rel="noopener noreferrer" className="row-action-btn" onClick={(e) => e.stopPropagation()} title="Open source">↗</a>}
            <button className="row-action-btn" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(row.title || val); }} title="Copy title">⎘</button>
          </span>
        </div>
      );
    }

    if (col.type === "source") {
      // Support multi-source: comma-separated values get multiple badges
      const srcParts = (val || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
      const badges = srcParts.map((src, i) => {
        let label = 'SAM.gov'; let cls = 'sam';
        if (src.includes('puppeteer') || src.includes('naics') || src.includes('rss') || src === 'sam.gov' || (!src.includes('govcon-intel') && !src.includes('usaspending'))) {
          if (src.includes('puppeteer')) { label = 'SAM.gov'; cls = 'sam'; }
          else if (src.includes('naics')) { label = 'SAM.gov'; cls = 'sam'; }
          else if (src.includes('rss')) { label = 'SAM.gov'; cls = 'sam'; }
        }
        if (src.includes('govcon-intel:hatch') || src.includes('hatch')) { label = 'Hatch'; cls = 'hatch'; }
        else if (src.includes('govcon-intel:govcon-wire') || src.includes('govconwire')) { label = 'GovCon Wire'; cls = 'govcon'; }
        else if (src.includes('executivegov')) { label = 'ExecGov'; cls = 'execgov'; }
        else if (src.includes('defense-one') || src.includes('defenseone')) { label = 'Def One'; cls = 'defone'; }
        else if (src.includes('fedscoop')) { label = 'FedScoop'; cls = 'fedscoop'; }
        else if (src.includes('c4isrnet') || src.includes('c4isr')) { label = 'C4ISRNET'; cls = 'defone'; }
        else if (src.includes('breaking-defense') || src.includes('breakingdefense')) { label = 'Brk Def'; cls = 'defone'; }
        else if (src.includes('federal-news') || src.includes('federalnews')) { label = 'Fed News'; cls = 'fedscoop'; }
        else if (src.includes('nextgov') || src.includes('route-fifty')) { label = 'NextGov'; cls = 'fedscoop'; }
        else if (src.includes('afcea') || src.includes('signal')) { label = 'AFCEA'; cls = 'defone'; }
        else if (src.includes('govcon-intel')) { label = src.split(':')[1] ? src.split(':')[1].replace(/-/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) : 'Intel'; cls = 'govcon'; }
        return <span key={i} className={`badge ${cls}`} style={i > 0 ? {marginLeft: 3} : {}}>{label}</span>;
      });
      return <span style={{display:'inline-flex',alignItems:'center',flexWrap:'wrap',gap:2}}>{badges}</span>;
    }
    if (col.type === "score") return <ScoreCell value={val} />;
    if (col.type === "tier") return <PillCell value={val} type="tier" />;
    if (col.type === "recommendation") return <PillCell value={val} type="rec" />;
    if (col.type === "date") return <DateCell value={val} kind={col.key === "posted_date" ? "posted" : "deadline"} />;
    if (col.type === "count") return <span style={{ color: val > 0 ? "var(--accent)" : "var(--text-2)", fontFamily: "var(--font-mono)", fontSize: 11 }}>{val > 0 ? val : "0"}</span>;
    // Contextual empty states
    if (!val) {
      if (col.key === "contract_value") return <span style={{color:"var(--text-2)",fontSize:11}}>Not identified yet</span>;
      if (col.key === "agency") return <span style={{color:"var(--text-2)",fontSize:11}}>Not identified yet</span>;
      return <span style={{color:"var(--text-2)"}}>—</span>;
    }
    return val;
  }

  // ── IS AGENT RUNNING ──
  const isRunning = agentStatus && agentStatus.running;

  const tableData = filteredAndSorted;

  return (
    <>
      <style>{css}</style>
      <div className="app-shell">

        {/* ── TOP BAR ── */}
        <div className="topbar">
          <div className="topbar-left">
            <div className="logo"><Lockup iconSize={28} wordSize={22} /></div>
            <div className="status-indicator">
              <div className={`status-dot ${isRunning ? "active" : ""}`} />
              <span>{isRunning ? `Running — ${agentStatus.currentPhase}` : "Idle"}</span>
            </div>
          </div>
          <div className="topbar-right">
            <nav className="main-nav">
              {[
                { key: "opportunities", label: "Opportunities" },
                { key: "pursuits", label: `Pursuits${watchlist.length > 0 ? ` (${watchlist.length})` : ''}` },
                { key: "intelligence", label: "Market Pulse" },
              ].map(tab => (
                <button key={tab.key} className={`btn-nav ${viewMode === tab.key ? "active" : ""}`} onClick={() => { setViewMode(tab.key); setSelectedRow(null); }}>{tab.label}</button>
              ))}
              {userRole === 'admin' && <button className={`btn-nav ${viewMode === 'admin' ? "active" : ""}`} onClick={() => { setViewMode('admin'); setSelectedRow(null); }}>Admin</button>}
            </nav>
            <div className="topbar-divider" />
            <button className={`btn-secondary btn-outbox ${pendingActionsCount > 0 ? "has-pending" : ""}`} onClick={() => setShowOutbox(true)} title="Agent Outbox">
              Outbox {pendingActionsCount > 0 && <span className="btn-badge">{pendingActionsCount}</span>}
            </button>
            <button className="btn-secondary" onClick={() => setShowProfileEditor(true)}>Profile</button>
            <button
              className="btn-secondary"
              onClick={handleLogout}
              title={userEmail ? `Signed in as ${userEmail}` : 'Sign out'}
            >
              Sign Out
            </button>
            {userRole === 'admin' && (
              <button className="btn-secondary" onClick={handleRescore} disabled={rescoring}>{rescoring ? 'Rescoring...' : 'Rescore\nAll'}</button>
            )}
            {userRole === 'admin' && (
              <button className={`btn-toggle-activity ${showActivityPanel ? "open" : ""}`} onClick={() => setShowActivityPanel(p => !p)} title={showActivityPanel ? "Hide activity log" : "Show activity log"}>
                {">_"}
              </button>
            )}
            {userRole === 'admin' && (
              <button className="btn-run" onClick={triggerRun} disabled={triggering || agentStatus?.running}><span className="dot" />{triggering ? "Starting..." : agentStatus?.running ? `Running: ${agentStatus.currentPhase || '...'}` : "Run Now"}</button>
            )}
            {nextRunDisplay && <span className="next-run-label">Next: {nextRunDisplay}</span>}
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div className="main-content">
        <ErrorBoundary label="main content">

          {/* ── ANALYTICS DASHBOARD PAGE ── */}
          {/* Analytics / ExecutiveDashboard was cut — the Opportunities view is the dashboard. */}

          {/* ── OPPORTUNITIES PAGE ── */}
          {viewMode === "opportunities" && <><div className="center-panel">

            {/* ── STATS ── */}
            <div className="stats-bar">
              <div className={`stat-card clickable ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>
                <span className="stat-label">Total Found</span>
                <span className="stat-value">{stats?.total || 0}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Scored</span>
                <span className="stat-value">{stats?.scored || 0}</span>
              </div>
              <div className={`stat-card clickable ${filter === "pursue" ? "active" : ""}`} onClick={() => setFilter("pursue")}>
                <span className="stat-label">Pursue</span>
                <span className="stat-value green">{stats?.pursue || 0}</span>
              </div>
              <div className={`stat-card clickable ${filter === "monitor" ? "active" : ""}`} onClick={() => setFilter("monitor")}>
                <span className="stat-label">Monitor</span>
                <span className="stat-value amber">{stats?.monitor || 0}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Pass</span>
                <span className="stat-value red">{stats?.pass || 0}</span>
              </div>
            </div>

            {/* ── TOOLBAR ── */}
            <div className="toolbar">
              <div className="filters">
                {[
                  { key: "all", label: "All" },
                  { key: "pursue", label: "Pursue" },
                  { key: "monitor", label: "Monitor" },
                  { key: "high", label: "High Score" },
                  { key: "needs_review", label: "Needs Review" },
                  { key: "whats_new", label: "What's New" },
                  { key: "dismissed", label: "Dismissed" }
                ].map(f => (
                  <button key={f.key} className={`filter-btn ${filter === f.key ? "active" : ""}`} onClick={() => setFilter(f.key)}>
                    {f.label}
                  </button>
                ))}
                {/* Saved searches dropdown — stores user's commonly used filter
                    combinations. Click name → restores entire filter state. */}
                <div style={{ position: 'relative', marginLeft: 8 }}>
                  <button
                    className="filter-btn"
                    onClick={() => setShowSavedMenu(!showSavedMenu)}
                    style={{ background: showSavedMenu ? 'var(--bg-2)' : undefined }}
                  >
                    Saved {savedSearches.length > 0 ? `(${savedSearches.length})` : ''} ▾
                  </button>
                  {showSavedMenu && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      minWidth: 240,
                      maxWidth: 360,
                      background: 'var(--bg-1)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      boxShadow: '0 8px 24px rgba(20,25,35,0.10)',
                      zIndex: 50,
                      padding: '4px 0',
                    }}>
                      <button
                        onClick={() => { saveCurrentFilter(); setShowSavedMenu(false); }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', background: 'transparent', border: 'none', color: 'var(--accent)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                      >
                        + Save current filter
                      </button>
                      {savedSearches.length === 0 ? (
                        <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--text-2)', fontStyle: 'italic' }}>
                          No saved searches yet
                        </div>
                      ) : (
                        <>
                          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
                          {savedSearches.map(s => (
                            <div key={s.id} style={{ display: 'flex', alignItems: 'center' }}>
                              <button
                                onClick={() => applySavedSearch(s)}
                                style={{ flex: 1, textAlign: 'left', padding: '6px 14px', background: 'transparent', border: 'none', color: 'var(--text-0)', fontSize: 12, cursor: 'pointer' }}
                              >
                                {s.name}
                              </button>
                              <button
                                onClick={() => deleteSavedSearch(s.id)}
                                title="Delete saved search"
                                style={{ padding: '4px 10px', background: 'transparent', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 14 }}
                              >×</button>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="toolbar-divider" />
              <div className="toolbar-right">
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    className="search-input"
                    type="text"
                    placeholder="Search opportunities..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{ paddingRight: searchQuery ? 28 : undefined }}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => { setSearchQuery(""); setFilter("all"); setAgencyFilter("all"); }}
                      title="Clear search and filters"
                      style={{
                        position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                        background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 4,
                        color: 'var(--text-1)', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                        width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 0, lineHeight: 1,
                      }}
                    >×</button>
                  )}
                </div>
                <select className="filter-select" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} title="Filter by source">
                  <option value="all">Opportunities (SAM.gov)</option>
                  <option value="sam">SAM.gov only</option>
                  <option value="intel">News &amp; Articles</option>
                  <option value="hatch">— Hatch Report</option>
                  <option value="govcon">— GovCon Wire</option>
                  <option value="defense">— Defense Press</option>
                </select>
                <select
                  className="filter-select"
                  value={agencyFilter}
                  onChange={e => setAgencyFilter(e.target.value)}
                  title="Filter by agency"
                  style={{maxWidth: 130}}
                >
                  <option value="all">All Agencies</option>
                  {uniqueAgencies.slice(0, 30).map(a => (
                    <option key={a} value={a}>{a.length > 20 ? a.substring(0, 20) + '…' : a}</option>
                  ))}
                </select>
                <select
                  className="filter-select"
                  value={naicsFilter}
                  onChange={e => setNaicsFilter(e.target.value)}
                  title="Filter by NAICS code"
                  style={{maxWidth: 110}}
                >
                  <option value="all">All NAICS</option>
                  {uniqueNaics.slice(0, 40).map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <div className="score-slider-wrap">
                  <span>Score</span>
                  <input
                    type="range" min="0" max="100" step="5"
                    value={scoreFilter}
                    onChange={e => setScoreFilter(Number(e.target.value))}
                  />
                  <span className="score-slider-val">{scoreFilter > 0 ? `${scoreFilter}+` : "All"}</span>
                </div>
                <span className="toolbar-count">{tableData.length} results</span>
                {error && (
                  <span
                    style={{ color: "var(--red)", fontSize: 11, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={error}
                  >
                    ⚠ {error}
                  </span>
                )}
              </div>
            </div>

            {/* ── ACTIVE FILTER BANNER ── */}
            {(searchQuery || agencyFilter !== 'all' || naicsFilter !== 'all' || sourceFilter !== 'all' || scoreFilter > 0) && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 28px',
                background: 'var(--accent-dim, var(--accent-dim))', borderBottom: '1px solid var(--border)',
                fontSize: 12, color: 'var(--text-1)',
              }}>
                <span style={{ fontWeight: 600 }}>Filtered:</span>
                {searchQuery && <span style={{ background: 'var(--bg-3)', padding: '2px 8px', borderRadius: 4 }}>Search: "{searchQuery}"</span>}
                {agencyFilter !== 'all' && <span style={{ background: 'var(--bg-3)', padding: '2px 8px', borderRadius: 4 }}>Agency: {agencyFilter}</span>}
                {naicsFilter !== 'all' && <span style={{ background: 'var(--bg-3)', padding: '2px 8px', borderRadius: 4 }}>NAICS: {naicsFilter}</span>}
                {sourceFilter !== 'all' && <span style={{ background: 'var(--bg-3)', padding: '2px 8px', borderRadius: 4 }}>Source: {sourceFilter}</span>}
                {scoreFilter > 0 && <span style={{ background: 'var(--bg-3)', padding: '2px 8px', borderRadius: 4 }}>Score: {scoreFilter}+</span>}
                <button
                  onClick={() => { setSearchQuery(''); setFilter('all'); setAgencyFilter('all'); setNaicsFilter('all'); setSourceFilter('all'); setScoreFilter(0); }}
                  style={{
                    marginLeft: 'auto', background: 'var(--bg-3)', border: '1px solid var(--border)',
                    borderRadius: 4, color: 'var(--accent)', cursor: 'pointer', fontSize: 11,
                    fontWeight: 700, padding: '3px 10px',
                  }}
                >Clear All Filters</button>
              </div>
            )}

            {/* ── TABLE ── */}
            <div className="table-wrap">
              {loading && opportunities.length === 0 ? (
                <div className="empty-state">
                  <div className="icon">◎</div>
                  <p>Connecting to backend...</p>
                  <span className="sub">Make sure your Railway backend is running</span>
                </div>
              ) : tableData.length === 0 ? (
                <div className="empty-state">
                  <div className="icon">◎</div>
                  {(() => {
                    const totalInDb = stats?.totalInDb ?? 0;
                    const expired = stats?.expiredOrStale ?? 0;
                    const lastRun = stats?.lastRunAt;
                    const lastRunRel = lastRun
                      ? (() => {
                          const mins = Math.round((Date.now() - new Date(lastRun)) / 60000);
                          if (mins < 1) return 'just now';
                          if (mins < 60) return `${mins} min ago`;
                          const hrs = Math.round(mins / 60);
                          if (hrs < 24) return `${hrs} hr ago`;
                          return `${Math.round(hrs / 24)} day(s) ago`;
                        })()
                      : null;

                    if (totalInDb === 0) {
                      return (
                        <>
                          <p>No opportunities in the database yet. Hit <strong>Run Now</strong> to crawl SAM.gov.</p>
                          <span className="sub">{lastRun ? `Last pipeline run: ${lastRunRel}` : 'Pipeline has not run yet.'}</span>
                        </>
                      );
                    }
                    if (filter === 'pursue') {
                      return (
                        <>
                          <p><strong>No opportunities currently clear the evidence-gated PURSUE standard.</strong></p>
                          <span className="sub">The strongest live candidates remain MONITOR until SAMKLR verifies enough scope, eligibility, and response-path evidence.</span>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 14 }}>
                            <button className="filter-btn" onClick={() => setFilter('monitor')}>Show Monitor candidates</button>
                            <button className="filter-btn" onClick={() => setFilter('all')}>Show all opportunities</button>
                          </div>
                        </>
                      );
                    }
                    if (expired > 0 && totalInDb > 0 && (stats?.total ?? 0) === 0) {
                      return (
                        <>
                          <p><strong>{totalInDb}</strong> records in DB, but all are past deadline or older than 90 days. Hit <strong>Run Now</strong> to pull fresh listings.</p>
                          <span className="sub">{lastRun ? `Last run: ${lastRunRel}` : 'Pipeline has not run yet.'}</span>
                        </>
                      );
                    }
                    return (
                      <>
                        <p>No opportunities match your current filters. Try clearing the filters.</p>
                        <span className="sub">{lastRun ? `Last run: ${lastRunRel}` : 'Pipeline has not run yet.'}</span>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      {activePersonality.columns.map(col => (
                        <th
                          key={col.key}
                          style={{ width: col.width }}
                          className={`${col.sortable ? "sortable" : ""} ${sortKey === col.key ? "sorted" : ""}`}
                          onClick={() => col.sortable && handleSort(col.key)}
                        >
                          {col.label}
                          {col.sortable && <span className="sort-arrow">{sortKey === col.key ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</span>}
                        </th>
                      ))}
                      <th style={{ width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row, i) => {
                      const isDismissed = row.bdStatus === 'archived' || row.bdStatus === 'lost';
                      return (
                        <tr
                          key={row.id || i}
                          className={selectedRow?.id === row.id ? "selected" : ""}
                          onClick={() => setSelectedRow(row)}
                          style={{ animationDelay: `${i * 0.02}s` }}
                        >
                          {activePersonality.columns.map(col => (
                            <td key={col.key} className={col.key === "name" ? "name-col" : ""}>
                              {renderCell(col, row)}
                            </td>
                          ))}
                          <td
                            style={{ textAlign: 'center', padding: '4px 6px' }}
                            onClick={e => {
                              e.stopPropagation();
                              const next = isDismissed ? 'new' : 'archived';
                              const verb = isDismissed ? 'restore' : 'dismiss';
                              if (window.confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} "${(row.title || row.noticeId || '').substring(0, 80)}"?`)) {
                                handleUpdateOpportunity(row.noticeId, { bdStatus: next });
                              }
                            }}
                          >
                            <button
                              title={isDismissed ? 'Restore to active feed' : 'Dismiss from feed'}
                              style={{
                                background: 'transparent',
                                border: '1px solid var(--border)',
                                color: 'var(--text-2)',
                                width: 22,
                                height: 22,
                                lineHeight: '20px',
                                padding: 0,
                                borderRadius: 3,
                                cursor: 'pointer',
                                fontSize: 12,
                              }}
                            >
                              {isDismissed ? '↶' : '✕'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          </>}

          {/* ── PURSUITS BOARD ── */}
          {/* ── PURSUITS PAGE (Board + Watchlist) ── */}
          {viewMode === "pursuits" && (
            <div className="center-panel" style={{ overflow: 'auto' }}>
              <div style={{ display: 'flex', gap: 8, padding: '16px 28px 0' }}>
                {[
                  { key: 'board', label: 'Pursuit Board' },
                  { key: 'watchlist', label: `Watchlist${watchlist.length > 0 ? ` (${watchlist.length})` : ''}` },
                ].map(t => (
                  <button key={t.key} className={`filter-btn ${pursuitsTab === t.key ? 'active' : ''}`} onClick={() => setPursuitsTab(t.key)}>{t.label}</button>
                ))}
              </div>
              {pursuitsTab === 'board' && (
                <PursuitsBoard
                  opportunities={opportunities}
                  onUpdateStatus={handleUpdateOpportunity}
                  onSelectRow={setSelectedRow}
                />
              )}
              {pursuitsTab === 'watchlist' && (
                <div style={{ padding: '0 28px 28px' }}>
                  <WatchlistView
                    authToken={authToken}
                    watchlist={watchlist}
                    opportunities={opportunities}
                    onUnwatch={item => toggleWatch(item.entityType, item.entityId, item.entityName)}
                    onNavigate={(opts) => {
                      if (opts.tab) { setViewMode(opts.tab); }
                      else { handleNavigate(opts); }
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* ── INTELLIGENCE PAGE (Agencies + Programs + Research) ── */}
          {viewMode === "intelligence" && (
            <MarketPulseView authToken={authToken} onSelectOpportunity={(id) => {
              const opp = opportunities.find(o => o.noticeId === id);
              if (opp) { setViewMode("opportunities"); setSelectedRow(opp); }
            }} />
          )}

          {/* ── ADMIN PAGE (Diagnostics + Calibration + Accounts + Settings) ── */}
          {viewMode === "admin" && (
            <div className="center-panel" style={{ overflow: 'auto' }}>
              <div style={{ display: 'flex', gap: 8, padding: '16px 28px 0' }}>
                {[
                  { key: 'diagnostics', label: 'Diagnostics' },
                  { key: 'calibration', label: 'Calibration' },
                  { key: 'accounts', label: 'Accounts' },
                  { key: 'settings', label: 'Settings' },
                ].map(t => (
                  <button key={t.key} className={`filter-btn ${adminTab === t.key ? 'active' : ''}`} onClick={() => setAdminTab(t.key)}>{t.label}</button>
                ))}
              </div>
              <div style={{ padding: adminTab === 'accounts' ? 0 : undefined }}>
                {adminTab === 'diagnostics' && <DiagnosticsView authToken={authToken} />}
                {adminTab === 'calibration' && <CalibrationView opportunities={opportunities} onNavigate={handleNavigate} />}
                {adminTab === 'accounts' && (
                  <AccountsView
                    authToken={authToken}
                    opportunities={opportunities}
                    onNavigate={(opts) => {
                      if (opts.tab) { setViewMode(opts.tab); }
                      else { handleNavigate(opts); }
                    }}
                    isWatched={isWatched}
                    onToggleWatch={toggleWatch}
                  />
                )}
                {adminTab === 'settings' && (
                  <div style={{ padding: '28px', color: 'var(--text-1)', fontSize: 14 }}>
                    <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: 'var(--text-0)' }}>Settings</h2>
                    <button className="btn-secondary" onClick={() => setShowAdminPanel(true)} style={{ fontSize: 13 }}>Open Admin Panel</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── DETAIL PANEL (shared across Opportunities + Pursuits + Intelligence) ── */}
          {(viewMode === "opportunities" || viewMode === "pursuits" || viewMode === "intelligence") && selectedRow && (
            <div className="detail-overlay" onClick={() => setSelectedRow(null)} style={{
              position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
              background: 'rgba(0,0,0,0.4)', zIndex: 1,
              display: window.innerWidth <= 768 ? 'block' : 'none',
            }} />
          )}
          {(viewMode === "opportunities" || viewMode === "pursuits" || viewMode === "intelligence") && (
          <div className={`detail-panel ${selectedRow ? "" : "hidden"}`}>
            {selectedRow && (
              <ErrorBoundary label="opportunity detail" onClose={() => setSelectedRow(null)}>
              <>
                <div className="detail-header">
                  <button className="detail-close" onClick={() => setSelectedRow(null)}>×</button>
                  <div className="detail-title">{selectedRow.title}</div>
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setBdTeamOpenFor(selectedRow)}
                      title="Run an evidence-based capture review before deciding whether to draft a proposal"
                      style={{
                        fontSize: 11, fontFamily: 'var(--font-mono)', cursor: 'pointer',
                        padding: '5px 10px', borderRadius: 4, fontWeight: 700,
                        border: '1px solid var(--accent)',
                        background: selectedRow.bdTeamDraft?.status === 'complete' ? 'var(--accent)' : 'var(--accent-dim)',
                        color: selectedRow.bdTeamDraft?.status === 'complete' ? 'white' : 'var(--accent)',
                      }}
                    >
                      {selectedRow.bdTeamDraft?.status === 'complete' ? '✓ Open capture review' :
                       selectedRow.bdTeamDraft?.status === 'running' ? '⟳ Capture review running…' :
                       selectedRow.bdTeamDraft?.status === 'partial' ? '⚠ Capture review incomplete' :
                       selectedRow.bdTeamDraft?.status === 'failed' ? '✗ Capture review failed — retry' :
                       '◎ Run capture review'}
                    </button>
                  </div>
                  <div className="detail-meta">
                    <ScoreCell value={selectedRow.score} />
                    <PillCell value={selectedRow.score >= 70 ? 'High' : selectedRow.score >= 40 ? 'Medium' : 'Low'} type="tier" />
                    <PillCell value={selectedRow.recommendation} type="rec" />
                    <RiskBadge opp={selectedRow} />
                    {selectedRow.postedDate && (() => {
                      const posted = new Date(selectedRow.postedDate);
                      const daysSince = Math.floor((new Date() - posted) / (1000 * 60 * 60 * 24));
                      if (daysSince <= 3) return <span style={{padding: '2px 8px', fontSize: '10px', fontWeight: '700', backgroundColor: '#2ecc71', color: '#fff', borderRadius: '4px', marginLeft: '8px'}}>NEW</span>;
                      if (daysSince <= 7) return <span style={{padding: '2px 8px', fontSize: '10px', fontWeight: '700', backgroundColor: '#f39c12', color: '#fff', borderRadius: '4px', marginLeft: '8px'}}>{daysSince}d ago</span>;
                      return null;
                    })()}
                    {(selectedRow.source || 'sam').split(',').map((rawSrc, i) => {
                      const src = rawSrc.trim().toLowerCase();
                      let label = 'SAM.gov'; let cls = 'sam';
                      if (src.includes('govcon-intel:hatch') || src.includes('hatch')) { label = 'Hatch'; cls = 'hatch'; }
                      else if (src.includes('govcon-intel:govcon-wire') || src.includes('govconwire')) { label = 'GovCon Wire'; cls = 'govcon'; }
                      else if (src.includes('executivegov')) { label = 'ExecGov'; cls = 'execgov'; }
                      else if (src.includes('defense-one') || src.includes('defenseone')) { label = 'Defense One'; cls = 'defone'; }
                      else if (src.includes('c4isrnet') || src.includes('c4isr')) { label = 'C4ISRNET'; cls = 'defone'; }
                      else if (src.includes('breaking-defense') || src.includes('breakingdefense')) { label = 'Brk Def'; cls = 'defone'; }
                      else if (src.includes('federal-news') || src.includes('federalnews')) { label = 'Fed News'; cls = 'fedscoop'; }
                      else if (src.includes('nextgov')) { label = 'NextGov'; cls = 'fedscoop'; }
                      else if (src.includes('afcea')) { label = 'AFCEA'; cls = 'defone'; }
                      else if (src.includes('fedscoop')) { label = 'FedScoop'; cls = 'fedscoop'; }
                      else if (src.includes('govcon-intel')) { label = 'GovCon Intel'; cls = 'govcon'; }
                      return <span key={i} className={`badge ${cls}`} style={{marginLeft: '6px'}}>{label}</span>;
                    })}
                  </div>
                </div>
                <div className="detail-body">
                  {/* ── CAPTURE LIFECYCLE — single stage model shared with Pursuits board ── */}
                  {(() => {
                    const stage = selectedRow.captureStage || 'Discover';
                    const currentIndex = CAPTURE_STAGES.indexOf(stage);
                    return (
                      <div className="detail-section" style={{background:'linear-gradient(135deg, var(--accent-dim), var(--bg-2))',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'14px 16px'}}>
                        <div className="detail-section-label">Capture Lifecycle</div>
                        <div style={{display:'grid',gridTemplateColumns:`repeat(${CAPTURE_STAGES.length}, minmax(0, 1fr))`,gap:4,marginBottom:10}}>
                          {CAPTURE_STAGES.map((name, index) => {
                            const active = name === stage;
                            const complete = index < currentIndex;
                            return (
                              <button key={name} onClick={() => handleUpdateOpportunity(selectedRow.noticeId, { captureStage: name })}
                                title={`Move to ${name}`}
                                style={{fontSize:9,fontFamily:'var(--font-mono)',fontWeight:700,padding:'7px 3px',cursor:'pointer',borderRadius:4,border:active?'1px solid var(--accent)':'1px solid var(--border)',background:active?'var(--accent)':complete?'var(--accent-dim)':'var(--bg-3)',color:active?'white':complete?'var(--accent)':'var(--text-2)'}}>
                                {complete ? '✓ ' : ''}{name}
                              </button>
                            );
                          })}
                        </div>
                        <div style={{fontSize:11,lineHeight:1.5,color:'var(--text-1)'}}><strong style={{color:'var(--accent)'}}>Next action:</strong> {captureNextAction(stage, selectedRow)}</div>
                      </div>
                    );
                  })()}
                  {/* ── BD STATUS & NOTES ── */}
                  <div className="detail-section" style={{background: 'var(--bg-2)', borderRadius: 'var(--radius)', padding: '12px 16px'}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8}}>
                      <div className="detail-section-label" style={{marginBottom: 0}}>BD Tracking</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => toggleWatch('opportunity', selectedRow.noticeId, selectedRow.title || selectedRow.noticeId)}
                          style={{ fontSize: 10, fontFamily: 'var(--font-mono)', cursor: 'pointer', border: isWatched('opportunity', selectedRow.noticeId) ? '1px solid var(--accent)' : '1px solid var(--border)', background: isWatched('opportunity', selectedRow.noticeId) ? 'var(--accent-dim)' : 'var(--bg-3)', color: isWatched('opportunity', selectedRow.noticeId) ? 'var(--accent)' : 'var(--text-2)', borderRadius: 3, padding: '3px 8px' }}
                        >{isWatched('opportunity', selectedRow.noticeId) ? 'Watching' : 'Watch'}</button>
                        {/* Archive / Unarchive — removes the record from active
                            views (kanban, default opportunities list). Stays in
                            the DB; visible again via the Dismissed filter. */}
                        {selectedRow.bdStatus === 'archived' ? (
                          <button
                            onClick={() => handleUpdateOpportunity(selectedRow.noticeId, { bdStatus: 'new' })}
                            title="Bring this record back to active views"
                            style={{ fontSize: 10, fontFamily: 'var(--font-mono)', cursor: 'pointer', border: '1px solid var(--accent)', background: 'var(--accent-dim)', color: 'var(--accent)', borderRadius: 3, padding: '3px 8px' }}
                          >↩ Unarchive</button>
                        ) : (
                          <button
                            onClick={() => {
                              handleUpdateOpportunity(selectedRow.noticeId, { bdStatus: 'archived' });
                              setSelectedRow(null);
                            }}
                            title="Remove from active views (stays in DB, recoverable via Dismissed filter)"
                            style={{ fontSize: 10, fontFamily: 'var(--font-mono)', cursor: 'pointer', border: '1px solid var(--red)', background: 'var(--red-dim)', color: 'var(--red)', borderRadius: 3, padding: '3px 8px', fontWeight: 600 }}
                          >🗑 Archive</button>
                        )}
                      </div>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap'}}>
                      <span style={{fontSize: 11, color: 'var(--text-2)', minWidth: 40}}>Priority</span>
                      <select className="filter-select" value={selectedRow.priority || ''} onChange={e => handleUpdateOpportunity(selectedRow.noticeId, { priority: e.target.value || null })} style={{flex: 1, maxWidth: 120}}>
                        <option value="">Not set</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                      <span style={{fontSize:10,color:'var(--text-2)'}}>Stage is managed in the capture lifecycle above.</span>
                    </div>
                    {/* Fit label feedback */}
                    <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap'}}>
                      <span style={{fontSize: 10, color: 'var(--text-2)', minWidth: 40, fontFamily: 'var(--font-mono)'}}>Fit</span>
                      {[
                        { key: 'strong-fit',   label: 'Strong Fit',   color: 'var(--accent)' },
                        { key: 'good-fit',     label: 'Good Fit',     color: 'var(--blue)'   },
                        { key: 'weak-fit',     label: 'Weak Fit',     color: 'var(--amber)'  },
                        { key: 'not-relevant', label: 'Not Relevant', color: 'var(--red)'    },
                      ].map(({ key, label, color }) => {
                        const sel = selectedRow.userFitLabel === key;
                        return (
                          <button key={key} onClick={() => handleUpdateOpportunity(selectedRow.noticeId, { userFitLabel: sel ? null : key })}
                            style={{ fontSize: 10, fontFamily: 'var(--font-mono)', cursor: 'pointer', border: sel ? `1px solid ${color}` : '1px solid var(--border)', background: sel ? color + '22' : 'var(--bg-3)', color: sel ? color : 'var(--text-2)', borderRadius: 3, padding: '3px 8px', transition: 'all 0.15s' }}
                          >{label}</button>
                        );
                      })}
                    </div>
                    {/* Posture feedback */}
                    <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap'}}>
                      <span style={{fontSize: 10, color: 'var(--text-2)', minWidth: 40, fontFamily: 'var(--font-mono)'}}>Posture</span>
                      {[
                        { key: 'primeable',    label: 'Primeable',     color: 'var(--accent)' },
                        { key: 'partner-only', label: 'Partner-Only',  color: 'var(--blue)'   },
                        { key: 'ignore',       label: 'Ignore',        color: 'var(--red)'    },
                      ].map(({ key, label, color }) => {
                        const sel = selectedRow.userPostureLabel === key;
                        return (
                          <button key={key} onClick={() => handleUpdateOpportunity(selectedRow.noticeId, { userPostureLabel: sel ? null : key })}
                            style={{ fontSize: 10, fontFamily: 'var(--font-mono)', cursor: 'pointer', border: sel ? `1px solid ${color}` : '1px solid var(--border)', background: sel ? color + '22' : 'var(--bg-3)', color: sel ? color : 'var(--text-2)', borderRadius: 3, padding: '3px 8px', transition: 'all 0.15s' }}
                          >{label}</button>
                        );
                      })}
                    </div>
                    <div style={{display: 'flex', gap: 10}}>
                      <span style={{fontSize: 11, color: 'var(--text-2)', minWidth: 40, paddingTop: 6}}>Notes</span>
                      <textarea style={{flex: 1, minHeight: 60, background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text-0)', padding: '8px 10px', fontSize: 12, fontFamily: 'var(--font-body)', resize: 'vertical', lineHeight: 1.5}} value={selectedRow.notes || ''} placeholder="Add notes about this opportunity..." onChange={e => setSelectedRow(prev => ({ ...prev, notes: e.target.value }))} onBlur={e => handleUpdateOpportunity(selectedRow.noticeId, { notes: e.target.value })} />
                    </div>
                  </div>
                  {/* ── OPPORTUNITY SUMMARY ── */}
                  <div className="detail-section">
                    <div className="detail-section-label">Opportunity Summary</div>
                    {selectedRow.summary && <p style={{fontSize: '13px', lineHeight: '1.7', color: 'var(--text-0)', marginBottom: 8}}>{selectedRow.summary}</p>}
                    {selectedRow.recommendationReason && (<div style={{padding: '8px 12px', background: 'var(--accent-dim)', borderLeft: '3px solid var(--accent)', borderRadius: 4, fontSize: '12px', color: 'var(--text-1)', marginBottom: 8}}>{selectedRow.recommendationReason}</div>)}
                    {selectedRow.description && <p style={{fontSize: '12px', lineHeight: '1.6', color: 'var(--text-1)'}}>{selectedRow.description}</p>}
                    {selectedRow.link && (<a href={selectedRow.link} target="_blank" rel="noopener noreferrer" className="detail-link-btn" style={{marginTop: 10, display: 'inline-block'}}>View Original SAM.gov Listing ↗</a>)}
                  </div>
                  {/* ── CONTRACT DETAILS ── */}
                  {(() => {
                    const fs = selectedRow.fieldSources || {};
                    // Provenance badge: shows source and confidence for inferred fields
                    const ProvBadge = ({ field }) => {
                      const src = fs[field];
                      const sys = src && (src.system || src.source);
                      if (!src || sys === 'sam.gov' || sys === 'sam-api') return null;
                      const isInferred = sys === 'field-recovery';
                      const conf = src.confidence || 0;
                      const color = conf >= 70 ? '#22c55e' : conf >= 40 ? 'var(--gold)' : 'var(--red)';
                      return <span style={{fontSize:9,fontFamily:'var(--font-mono)',padding:'1px 5px',borderRadius:3,marginLeft:6,background:isInferred ? '#f59e0b18' : '#818cf818',border:`1px solid ${color}30`,color}}>{isInferred ? 'inferred' : sys} {conf}%</span>;
                    };
                    const MissingField = ({ label }) => <span style={{color:'var(--text-2)',fontStyle:'italic'}}>{label || 'Not captured'}<span style={{fontSize:9,fontFamily:'var(--font-mono)',padding:'1px 5px',borderRadius:3,marginLeft:6,background:'#ef444418',border:'1px solid #ef444430',color:'var(--red)'}}>missing</span></span>;
                    return (
                      <div className="detail-section">
                        <div className="detail-section-label">Contract Details</div>
                        <div className="detail-row"><span className="detail-row-label">Type</span><span className="detail-row-value">{selectedRow.noticeType || selectedRow.opportunityType || <MissingField />}</span></div>
                        <div className="detail-row"><span className="detail-row-label">Set-Aside</span><span className="detail-row-value">{selectedRow.setAside && selectedRow.setAside !== 'None' ? <>{selectedRow.setAside}<ProvBadge field="setAside" /></> : <span style={{color:'var(--text-2)',fontStyle:'italic'}}>Open competition</span>}</span></div>
                        <div className="detail-row"><span className="detail-row-label">NAICS</span><span className="detail-row-value">{selectedRow.naics || <MissingField />}</span></div>
                        <div className="detail-row"><span className="detail-row-label">PSC</span><span className="detail-row-value">{selectedRow.psc ? <>{selectedRow.psc}<ProvBadge field="psc" /></> : <MissingField />}</span></div>
                        <div className="detail-row"><span className="detail-row-label">Est. Value</span><span className="detail-row-value">{selectedRow.estimatedValue ? <>{selectedRow.estimatedValue}<ProvBadge field="estimatedValue" /></> : <MissingField label="Not disclosed" />}</span></div>
                        <div className="detail-row"><span className="detail-row-label">Place of Performance</span><span className="detail-row-value">{selectedRow.placeOfPerformance ? <>{selectedRow.placeOfPerformance}<ProvBadge field="placeOfPerformance" /></> : <MissingField label="Not specified" />}</span></div>
                        <div className="detail-row"><span className="detail-row-label">Response Date</span><span className="detail-row-value"><DateCell value={selectedRow.responseDeadline} /><ProvBadge field="responseDeadline" /></span></div>
                        <div className="detail-row"><span className="detail-row-label">Posted</span><span className="detail-row-value">{selectedRow.postedDate || <span style={{color:'var(--text-2)',fontStyle:'italic'}}>Unknown</span>}</span></div>
                        <div className="detail-row"><span className="detail-row-label">Notice ID</span><span className="detail-row-value" style={{fontFamily:'var(--font-mono)',fontSize:11}}>{selectedRow.noticeId || "—"}</span></div>
                        {selectedRow.solicitationNumber && <div className="detail-row"><span className="detail-row-label">Solicitation #</span><span className="detail-row-value" style={{fontFamily:'var(--font-mono)',fontSize:11}}>{selectedRow.solicitationNumber}</span></div>}
                        {selectedRow.contactName && <div className="detail-row"><span className="detail-row-label">Point of Contact</span><span className="detail-row-value">{selectedRow.contactName}{selectedRow.contactEmail ? ` (${selectedRow.contactEmail})` : ''}<ProvBadge field="contactName" /></span></div>}
                      </div>
                    );
                  })()}
                  {/* ── AGENCY STRUCTURE ── */}
                  {(() => {
                    const fs = selectedRow.fieldSources || {};
                    const agencySrc = fs.agency;
                    const isInferred = agencySrc && (agencySrc.system || agencySrc.source) === 'field-recovery';
                    return (
                      <div className="detail-section">
                        <div className="detail-section-label">Agency Structure</div>
                        <div className="detail-row"><span className="detail-row-label">Agency</span><span className="detail-row-value">{selectedRow.agency || <span style={{color:'var(--text-2)',fontStyle:'italic'}}>Unknown</span>}{isInferred && <span style={{fontSize:9,fontFamily:'var(--font-mono)',padding:'1px 5px',borderRadius:3,marginLeft:6,background:'#f59e0b18',border:'1px solid #f59e0b30',color:'var(--gold)'}}>inferred via {agencySrc.method} {agencySrc.confidence}%</span>}</span></div>
                        <div className="detail-row"><span className="detail-row-label">Department</span><span className="detail-row-value">{selectedRow.department || <span style={{color:'var(--text-2)',fontStyle:'italic'}}>—</span>}</span></div>
                        {selectedRow.subTier && <div className="detail-row"><span className="detail-row-label">Sub-Tier</span><span className="detail-row-value">{selectedRow.subTier}</span></div>}
                        {selectedRow.majorCommand && <div className="detail-row"><span className="detail-row-label">Major Command</span><span className="detail-row-value">{selectedRow.majorCommand}</span></div>}
                        {selectedRow.subCommand && <div className="detail-row"><span className="detail-row-label">Sub-Command</span><span className="detail-row-value">{selectedRow.subCommand}</span></div>}
                        <div className="detail-row"><span className="detail-row-label">Office</span><span className="detail-row-value">{selectedRow.office || <span style={{color:'var(--text-2)',fontStyle:'italic'}}>Not specified</span>}</span></div>
                      </div>
                    );
                  })()}
                  {/* ── EXTRACTION QUALITY ── */}
                  {(() => {
                    const eq = getExtractionQuality(selectedRow);
                    return (
                      <div className="detail-section" style={{ background: eq.bg, borderRadius: 'var(--radius)', padding: '12px 16px' }}>
                        <div className="detail-section-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          Extraction Quality
                          <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 700, color: eq.color, background: 'rgba(0,0,0,0.2)', padding: '1px 8px', borderRadius: 4 }}>{eq.level} — {eq.score}%</span>
                        </div>
                        {eq.gaps.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            {eq.gaps.map(g => (
                              <span key={g} style={{ fontSize: 10, fontFamily: 'var(--font-mono)', padding: '2px 8px', borderRadius: 4, background: 'rgba(0,0,0,0.15)', color: eq.color, border: `1px solid ${eq.color}30` }}>
                                Missing: {g}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize: 11, color: 'var(--text-1)', marginTop: 6 }}>All key fields extracted successfully.</div>
                        )}
                      </div>
                    );
                  })()}
                  {/* ── DOCUMENTS ── */}
                  <div className="detail-section">
                    <div className="detail-section-label">Documents {Array.isArray(selectedRow.attachments) && selectedRow.attachments.length > 0 && <span style={{marginLeft:8,color:'var(--text-2)',fontWeight:400,fontSize:11}}>({selectedRow.attachments.length} files)</span>}</div>
                    <div className="detail-row"><span className="detail-row-label">Attachment Status</span><span className="detail-row-value" style={{color: selectedRow.attachmentStatus === 'downloaded' ? 'var(--accent)' : selectedRow.attachmentStatus?.includes('failed') ? 'var(--red)' : 'var(--text-2)'}}>{(selectedRow.attachmentStatus || 'pending').replace(/-/g, ' ')}</span></div>
                    {selectedRow.attachmentAttempts > 0 && <div className="detail-row"><span className="detail-row-label">Retrieval Attempts</span><span className="detail-row-value">{selectedRow.attachmentAttempts}</span></div>}
                    {selectedRow.attachmentError && <div style={{fontSize:11,color:'var(--red)',marginTop:6,lineHeight:1.45}}>Last retrieval error: {selectedRow.attachmentError}</div>}
                    <div className="detail-row"><span className="detail-row-label">AI Parse Status</span><span className="detail-row-value" style={{color: selectedRow.aiScrubStatus === 'complete' ? 'var(--accent)' : 'var(--text-2)'}}>{selectedRow.aiScrubStatus || 'pending'}</span></div>
                    {Array.isArray(selectedRow.attachments) && selectedRow.attachments.length > 0 ? (
                      <div style={{marginTop: 8}}>{selectedRow.attachments.map((att, i) => (<div key={i} style={{marginBottom: 4, display:'flex', alignItems:'center', gap:8}}><a href={att.url || att.link} target="_blank" rel="noopener noreferrer" style={{color:'var(--accent)', fontSize:12, textDecoration:'none'}}>📄 {att.name || att.filename || `Document ${i+1}`}</a>{att.fileType && <span style={{fontSize:10,color:'var(--text-2)',fontFamily:'var(--font-mono)'}}>{att.fileType.toUpperCase()}</span>}</div>))}</div>
                    ) : (<div style={{fontSize:12,color:'var(--text-2)',marginTop:6}}>{selectedRow.score >= 40 ? 'Document retrieval is queued or no downloadable attachment was published.' : 'Document retrieval begins when an opportunity reaches MONITOR (score 40+) or PURSUE.'}</div>)}
                  </div>
                  {/* ── AI ANALYSIS ── */}
                  <div className="detail-section">
                    <div className="detail-section-label">AI Analysis</div>
                    {(() => {
                      const ci = selectedRow.captureIntelligence || {};
                      const evidence = getExtractionQuality(selectedRow);
                      const actionability = Number.isFinite(Number(ci.actionabilityScore)) ? Number(ci.actionabilityScore) : null;
                      const confidence = Number.isFinite(Number(selectedRow.pursuitConfidence)) ? Number(selectedRow.pursuitConfidence) : evidence.score;
                      const signalColor = (v) => v >= 70 ? '#22c55e' : v >= 40 ? 'var(--gold)' : 'var(--red)';
                      const cards = [
                        { label: 'Competitive Position', value: selectedRow.score, note: 'Actionable, evidence-gated position' },
                        { label: 'Actionability', value: actionability, note: 'Can capture action happen now?' },
                        { label: 'Evidence Confidence', value: confidence, note: selectedRow.pursuitConfidence ? 'Model confidence in this assessment' : 'Based on source completeness' },
                      ];
                      return (
                        <div style={{display:'grid',gridTemplateColumns:'repeat(3, minmax(0, 1fr))',gap:8,margin:'8px 0 12px'}}>
                          {cards.map(card => (
                            <div key={card.label} style={{padding:'10px 11px',background:'var(--bg-2)',border:'1px solid var(--border)',borderRadius:6,minWidth:0}}>
                              <div style={{fontSize:9,color:'var(--text-2)',textTransform:'uppercase',letterSpacing:'0.5px',fontWeight:700}}>{card.label}</div>
                              <div style={{fontSize:20,fontFamily:'var(--font-mono)',fontWeight:700,color:card.value == null ? 'var(--text-2)' : signalColor(card.value),margin:'3px 0'}}>{card.value == null ? '—' : `${card.value}`}</div>
                              <div style={{fontSize:9,color:'var(--text-2)',lineHeight:1.35}}>{card.note}</div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    <div className="detail-row"><span className="detail-row-label">Score</span><span className="detail-row-value"><ScoreCell value={selectedRow.score} /></span></div>
                    {selectedRow.pursuitConfidence && (<div className="detail-row"><span className="detail-row-label">Confidence</span><span className="detail-row-value">{selectedRow.pursuitConfidence}/100</span></div>)}
                    <div className="detail-row"><span className="detail-row-label">Recommendation</span><span className="detail-row-value"><PillCell value={selectedRow.recommendation} type="rec" /></span></div>
                    {selectedRow.captureIntelligence?.actionType && <div className="detail-row"><span className="detail-row-label">Next Action</span><span className="detail-row-value" style={{fontWeight:700}}>{selectedRow.captureIntelligence.actionType}</span></div>}
                    {selectedRow.decision && <div className="detail-row"><span className="detail-row-label">GO / NO-GO Decision</span><span className="detail-row-value" style={{fontWeight:700,color:selectedRow.decision === 'GO' ? '#22c55e' : selectedRow.decision.includes('NO-GO') ? 'var(--red)' : 'var(--gold)'}}>{selectedRow.decision}</span></div>}
                    {selectedRow.evidenceGate && (() => {
                      const gate = selectedRow.evidenceGate;
                      const prime = gate.primeEligibility || {};
                      const matrix = Array.isArray(gate.complianceMatrix) ? gate.complianceMatrix : [];
                      const statusColor = status => status === 'FAIL' ? 'var(--red)' : status === 'UNKNOWN' ? 'var(--gold)' : '#22c55e';
                      return <div style={{marginTop:12,padding:'12px 14px',background:'var(--bg-2)',border:'1px solid var(--border)',borderRadius:6}}>
                        <div style={{fontSize:11,fontWeight:700,color:'var(--text-0)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:8}}>Prime Eligibility</div>
                        <div className="detail-row"><span className="detail-row-label">Source</span><span className="detail-row-value">{gate.sourceCompleteness?.status || 'Unknown'}</span></div>
                        <div className="detail-row"><span className="detail-row-label">Eligible</span><span className="detail-row-value">{prime.eligible || 'Unresolved'}</span></div>
                        <div className="detail-row"><span className="detail-row-label">Executable</span><span className="detail-row-value">{prime.executable || 'Unresolved'}</span></div>
                        <div className="detail-row"><span className="detail-row-label">Mandatory Gates</span><span className="detail-row-value">{prime.passed || 0} passed · {prime.unknown || 0} unknown · {prime.failed || 0} failed</span></div>
                        {prime.hardestBlockingRequirement && <div style={{fontSize:11,color:'var(--gold)',marginTop:7,lineHeight:1.45}}>Blocking requirement: {prime.hardestBlockingRequirement}</div>}
                        {matrix.length > 0 && <div style={{marginTop:10,overflowX:'auto'}}><table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}><thead><tr><th style={{textAlign:'left',padding:'5px',color:'var(--text-2)'}}>Requirement</th><th style={{textAlign:'left',padding:'5px',color:'var(--text-2)'}}>MAVRC Evidence</th><th style={{textAlign:'right',padding:'5px',color:'var(--text-2)'}}>Status</th></tr></thead><tbody>{matrix.map((item, i) => <tr key={i} style={{borderTop:'1px solid var(--border)'}}><td style={{padding:'6px 5px',verticalAlign:'top'}} title={item.governmentLanguage}>{item.requirement}</td><td style={{padding:'6px 5px',verticalAlign:'top',color:'var(--text-2)'}}>{item.companyEvidence}</td><td style={{padding:'6px 5px',verticalAlign:'top',textAlign:'right',fontWeight:700,color:statusColor(item.status),whiteSpace:'nowrap'}}>{item.status}</td></tr>)}</tbody></table></div>}
                      </div>;
                    })()}
                    {selectedRow.fitReasons && selectedRow.fitReasons.length > 0 && (<><div style={{fontSize:11,fontWeight:600,color:'var(--text-2)',textTransform:'uppercase',letterSpacing:'0.5px',marginTop:10,marginBottom:4}}>Matched Capabilities</div><ul className="detail-list fit">{selectedRow.fitReasons.map((r, i) => <li key={i}>{r}</li>)}</ul></>)}
                    {selectedRow.risks && selectedRow.risks.length > 0 && (<><div style={{fontSize:11,fontWeight:600,color:'var(--text-2)',textTransform:'uppercase',letterSpacing:'0.5px',marginTop:10,marginBottom:4}}>Risks</div><ul className="detail-list risks">{selectedRow.risks.map((r, i) => <li key={i}>{r}</li>)}</ul></>)}
                    {selectedRow.winStrategy && (<div style={{marginTop:10,padding:'10px 12px',background:'var(--accent-dim)',borderLeft:'3px solid var(--accent)',borderRadius:4,fontSize:12,lineHeight:1.6}}><strong style={{fontSize:11,color:'var(--accent)',textTransform:'uppercase',letterSpacing:'0.5px',display:'block',marginBottom:4}}>Win Strategy</strong>{selectedRow.winStrategy}</div>)}
                    {/* ── CAPTURE INTELLIGENCE ── */}
                    {selectedRow.captureIntelligence && (() => {
                      const ci = selectedRow.captureIntelligence;
                      const hasData = ci.pwinEstimate || ci.incumbentStrength || ci.competitorDensity || ci.proposalComplexity;
                      if (!hasData) return null;
                      const strengthColor = (v) => v === 'strong' ? 'var(--red)' : v === 'moderate' ? 'var(--gold)' : v === 'weak' ? '#22c55e' : 'var(--text-2)';
                      const densityColor = (v) => v === 'high' ? 'var(--red)' : v === 'moderate' ? 'var(--gold)' : v === 'low' ? '#22c55e' : 'var(--text-2)';
                      const complexityColor = (v) => v === 'high' ? 'var(--red)' : v === 'moderate' ? 'var(--gold)' : v === 'low' ? '#22c55e' : 'var(--text-2)';
                      const pwinColor = (v) => v >= 60 ? '#22c55e' : v >= 35 ? 'var(--gold)' : 'var(--red)';
                      return (
                        <div style={{marginTop:12,padding:'12px 14px',background:'rgba(99,102,241,0.06)',border:'1px solid rgba(99,102,241,0.15)',borderRadius:6}}>
                          <div style={{fontSize:11,fontWeight:700,color:'#818cf8',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:10,display:'flex',alignItems:'center',gap:6}}>
                            Capture Intelligence
                            {ci.capabilityFitScore != null && <span style={{fontSize:12}}>Technical fit: {ci.capabilityFitScore}/100 · Prime eligibility is assessed separately</span>}
                            {ci.pwinEstimate != null && <span style={{marginLeft:'auto',fontSize:13,fontWeight:700,color:pwinColor(ci.pwinEstimate)}}>P(Win): {ci.pwinEstimate}%</span>}
                          </div>
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px 16px'}}>
                            {ci.incumbentStrength && (
                              <div><span style={{fontSize:10,color:'var(--text-2)',textTransform:'uppercase'}}>Incumbent</span><div style={{fontSize:12,fontWeight:600,color:strengthColor(ci.incumbentStrength),textTransform:'capitalize'}}>{ci.incumbentStrength}{ci.incumbentName ? ` — ${ci.incumbentName}` : ''}</div></div>
                            )}
                            {ci.competitorDensity && (
                              <div><span style={{fontSize:10,color:'var(--text-2)',textTransform:'uppercase'}}>Competition</span><div style={{fontSize:12,fontWeight:600,color:densityColor(ci.competitorDensity),textTransform:'capitalize'}}>{ci.competitorDensity}{ci.competitorNotes ? ` — ${ci.competitorNotes}` : ''}</div></div>
                            )}
                            {ci.teamingRequirement && (
                              <div><span style={{fontSize:10,color:'var(--text-2)',textTransform:'uppercase'}}>Teaming</span><div style={{fontSize:12,fontWeight:600,color:'var(--text-1)',textTransform:'capitalize'}}>{ci.teamingRequirement}{ci.teamingNotes ? ` — ${ci.teamingNotes}` : ''}</div></div>
                            )}
                            {ci.proposalComplexity && (
                              <div><span style={{fontSize:10,color:'var(--text-2)',textTransform:'uppercase'}}>Proposal Complexity</span><div style={{fontSize:12,fontWeight:600,color:complexityColor(ci.proposalComplexity),textTransform:'capitalize'}}>{ci.proposalComplexity}{ci.proposalNotes ? ` — ${ci.proposalNotes}` : ''}</div></div>
                            )}
                            {ci.strategicValue && (
                              <div><span style={{fontSize:10,color:'var(--text-2)',textTransform:'uppercase'}}>Strategic Value</span><div style={{fontSize:12,fontWeight:600,color:ci.strategicValue === 'high' ? '#22c55e' : ci.strategicValue === 'moderate' ? 'var(--gold)' : 'var(--text-2)',textTransform:'capitalize'}}>{ci.strategicValue}{ci.strategicNotes ? ` — ${ci.strategicNotes}` : ''}</div></div>
                            )}
                            {ci.captureReadiness && (
                              <div><span style={{fontSize:10,color:'var(--text-2)',textTransform:'uppercase'}}>Capture Readiness</span><div style={{fontSize:12,fontWeight:600,color:'var(--text-1)',textTransform:'capitalize'}}>{ci.captureReadiness}</div></div>
                            )}
                          </div>
                          {ci.captureGaps && ci.captureGaps.length > 0 && (
                            <div style={{marginTop:8,paddingTop:8,borderTop:'1px solid rgba(99,102,241,0.12)'}}>
                              <span style={{fontSize:10,color:'var(--text-2)',textTransform:'uppercase'}}>Capture Gaps</span>
                              <ul style={{margin:'4px 0 0',paddingLeft:16,fontSize:12,color:'var(--gold)',lineHeight:1.6}}>{ci.captureGaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {selectedRow.marketResearch?.intelligence && (<div style={{marginTop:12}}><div style={{fontSize:11,fontWeight:600,color:'var(--text-2)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6}}>Market Research</div><p style={{fontSize:12,lineHeight:1.6,color:'var(--text-1)',marginBottom:6}}>{selectedRow.marketResearch.intelligence}</p>{selectedRow.marketResearch?.citations?.length > 0 && (<div style={{fontSize:11,color:'var(--text-2)'}}>Sources: {selectedRow.marketResearch.citations.map((c, i) => (<a key={i} href={c} target="_blank" rel="noopener noreferrer" style={{color:'var(--accent)',marginRight:8}}>[{i+1}]</a>))}</div>)}</div>)}
                  </div>
                  {selectedRow.graphSignals && <EcosystemSummary opp={selectedRow} />}
                  <ScoreBreakdown opp={selectedRow} />
                  <PartnerIntelSection row={selectedRow} />
                  <CaptureStrategy row={selectedRow} />
                  <RecommendedPartners row={selectedRow} />
                  <PartnerContactSignals row={selectedRow} />
                  <ExternalIntelSection row={selectedRow} authToken={authToken} onUpdate={(intel, fetchedAt) => {
                    setSelectedRow(prev => prev ? { ...prev, externalIntel: intel, externalIntelFetchedAt: fetchedAt } : prev);
                  }} />
                  <RelatedNotices noticeId={selectedRow.noticeId} onSelectNotice={(nid) => {
                    const found = opportunities.find(o => o.noticeId === nid);
                    if (found) setSelectedRow(found);
                  }} />
                  <ChangeHistory noticeId={selectedRow.noticeId} />
                  <CaptureTasks row={selectedRow} onUpdate={handleUpdateOpportunity} />
                  {selectedRow.agentPlan && (
                    <div className="agent-plan-panel">
                      <div className="agent-plan-header">
                        <div className="agent-plan-title">🤖 Agent Plan</div>
                        <label className="agent-plan-toggle"><input type="checkbox" defaultChecked={false} /><span>Allow Agent to execute</span></label>
                      </div>
                      <div className="agent-plan-section">
                        <div className="agent-plan-label">Decision: <strong>{selectedRow.recommendation}</strong></div>
                        {selectedRow.agentPlan.rationale && selectedRow.agentPlan.rationale.length > 0 && (<ul className="agent-plan-rationale">{selectedRow.agentPlan.rationale.map((r, i) => <li key={i}>{r}</li>)}</ul>)}
                      </div>
                      {selectedRow.agentPlan.actions && selectedRow.agentPlan.actions.length > 0 && (
                        <div className="agent-plan-section">
                          <div className="agent-plan-label">Planned Actions:</div>
                          <div className="agent-plan-actions">{selectedRow.agentPlan.actions.map((action, i) => (<div key={i} className={`agent-action-card priority-${action.priority}`}><div className="agent-action-header"><span className="agent-action-type">{action.type === 'email' ? '📨' : action.type === 'linkedin' ? '💼' : action.type === 'research' ? '🔍' : '📅'} {action.type}</span><span className="agent-action-priority">{action.priority}</span></div><div className="agent-action-target">{action.target}</div><div className="agent-action-purpose">{action.purpose}</div><div className="agent-action-timing">Execute in {action.daysOut} day{action.daysOut !== 1 ? 's' : ''}</div></div>))}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
              </ErrorBoundary>
            )}
          </div>
          )}

          {/* ── ACTIVITY FEED ── */}
          {viewMode === "opportunities" && (
          <div className={`activity-panel ${showActivityPanel ? "" : "collapsed"}`}>
            <div className="activity-header">
              <h3>Activity</h3>
              <div className="activity-live"><div className="dot" /> LIVE</div>
            </div>
            <div className="activity-feed">
              {activity.length === 0 ? (
                <div style={{ padding: "30px 18px", color: "var(--text-2)", fontSize: 12, textAlign: "center" }}>No activity yet.<br /><span style={{ fontSize: 11 }}>Hit Run Now or wait for the schedule.</span></div>
              ) : (
                activity.map((entry, i) => (
                  <div className="activity-entry" key={entry.id || i}>
                    <div className="activity-entry-header">
                      <span className={`activity-phase ${entry.phase}`}>{entry.phase}</span>
                      <span className="activity-time">{formatTime(entry.timestamp)}</span>
                    </div>
                    <div className="activity-msg">{entry.message}</div>
                  </div>
                ))
              )}
            </div>
          </div>
          )}

        </ErrorBoundary>
        </div>
      </div>

      {/* Side Panels */}
      <AdminPanel isOpen={showAdminPanel} onClose={() => setShowAdminPanel(false)} />
      <ProfileEditor isOpen={showProfileEditor} onClose={() => setShowProfileEditor(false)} />
      <AgentOutbox isOpen={showOutbox} onClose={() => setShowOutbox(false)} />
      {bdTeamOpenFor && <BdTeamPanel opp={bdTeamOpenFor} onClose={() => setBdTeamOpenFor(null)} />}
    </>
  );
}
